import { useState, useEffect, useCallback } from "react";
import { motion,  } from "framer-motion";
import { supabase } from "./supabase";

// ── Framer Motion variants ──
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] } } };
const fadeIn = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.25 } } };
const stagger = { show: { transition: { staggerChildren: 0.07 } } };
const slideUp = { hidden: { opacity: 0, y: 24, scale: 0.98 }, show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } } };
const modalVariant = { hidden: { opacity: 0, scale: 0.95, y: 12 }, show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] } }, exit: { opacity: 0, scale: 0.97, y: 8, transition: { duration: 0.2 } } };
const backdropVariant = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.2 } }, exit: { opacity: 0, transition: { duration: 0.15 } } };

// ============================================
// CLAUDE API — via Netlify proxy
// ============================================
async function callClaude(system, userMsg) {
  const res = await fetch("/.netlify/functions/claude-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ system, messages: [{ role: "user", content: userMsg }] }),
  });
  const d = await res.json();
  return d.content?.[0]?.text || "";
}

function parseJSON(raw) {
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return null; }
}

// ============================================
// NOTIFY REFERRER — send email on key events
async function notifyReferrer({ event, referrerEmail, referrerName, candidateName, taName, taCompany, briefTitle, bonusAmount }) {
  if (!referrerEmail) return;
  try {
    await fetch("/.netlify/functions/notify-referrer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, referrerEmail, referrerName, candidateName, taName, taCompany, briefTitle, bonusAmount }),
    });
  } catch (e) { console.error("Notify referrer error:", e); }
}

// ============================================
// ============================================
async function agentAnalyse(r) {
  const raw = await callClaude(
    `You are PeerRefer's Referral Analyser. Return ONLY valid JSON:
{"summary":"2-3 sentence candidate brief","strength":"single strongest point","watchout":"one honest flag or null","readiness":"immediate|3-6 months|exploratory","confidenceScore":85}`,
    `Candidate: ${r.candidateName}, Skills: ${r.skills.join(", ")}, Referrer: ${r.referrerName} (${r.referrerRelationship}), Track record: ${r.referrerStats.placed}/${r.referrerStats.total} placed, Scores: Skills ${r.skillsRating}/5 WorkEthic ${r.workEthicRating}/5 Coachability ${r.coachabilityRating}/5, Note: "${r.note}"`
  );
  return parseJSON(raw);
}

async function agentDraft(r) {
  const raw = await callClaude(
    `You are PeerRefer's Outreach Drafter. Write warm, human, non-transactional first-touch emails. No "exciting opportunity" or "touch base". 4-5 sentences. Return ONLY valid JSON: {"subject":"subject line","body":"email body"}`,
    `Candidate: ${r.candidateName}, skill: ${r.skills[0]}, Referred by: ${r.referrerName} (${r.referrerRelationship}), Note: "${r.note}", TA: Mia Thorne, Talent Lead at Acme Co`
  );
  return parseJSON(raw);
}

async function agentCoach(note, skills) {
  const raw = await callClaude(
    `You are PeerRefer's Referrer Coach. Review referral notes for quality AND privacy. Flag if the note includes identifying details like company names, specific project names, or anything that could identify the candidate without their consent. Max 2 sentences of feedback. Return ONLY valid JSON: {"score":75,"feedback":"feedback","suggestion":"one specific improvement or null","privacyWarning": true or false}`,
    `Skills: ${skills.join(", ")}, Note: "${note}"`
  );
  return parseJSON(raw);
}

async function agentMatch(referrals) {
  const raw = await callClaude(
    `You are PeerRefer's Match Engine. Analyse a TA inbox and surface insights. Use real names. Return ONLY valid JSON: {"insights":[{"type":"pattern|nudge|opportunity","message":"text","urgency":"high|medium|low"}],"topPriority":"who to contact first and why (1-2 sentences)"}`,
    `Inbox: ${JSON.stringify(referrals.map(r => ({ name: r.candidateName, skills: r.skills, status: r.status, score: ((r.skillsRating + r.workEthicRating + r.coachabilityRating) / 3).toFixed(1), age: r.submittedAt })))}`
  );
  return parseJSON(raw);
}


async function agentJobMatch(brief, referrals) {
  if (!referrals || referrals.length === 0) return [];

  const briefSummary = {
    title: brief.title,
    skills: (brief.skills || []).slice(0, 6),
    seniority: brief.seniority || "",
    location: brief.location || "",
  };

  // Cap at 8, find top 3
  const candidateSummaries = referrals.slice(0, 8).map(r => ({
    id: r.id,
    name: r.candidateName || "Unknown",
    skills: (r.skills || []).slice(0, 5),
    note: (r.note || "").substring(0, 80),
    ratings: `${r.skillsRating || 3}/${r.workEthicRating || 3}/${r.coachabilityRating || 3}`,
    placed: r.referrerStats?.placed || 0,
  }));

  if (candidateSummaries.length === 0) return [];

  const raw = await callClaude(
    `Pick the top 3 candidates for this job. Return ONLY a JSON array, no markdown:
[{"id":"id","score":85,"flag":"hot","reasons":["reason1","reason2"]}]
flag must be: hot=80+, good=60-79, weak=below 60. Max 2 short reasons each.`,
    `JOB: ${JSON.stringify(briefSummary)}\nCANDIDATES: ${JSON.stringify(candidateSummaries)}`
  );

  try {
    const cleaned = raw.replace(/```json|```/g, "").trim();
    const results = JSON.parse(cleaned);
    if (!Array.isArray(results)) return [];
    return results.sort((a, b) => b.score - a.score);
  } catch (e) {
    console.error("agentJobMatch parse error:", e, "raw:", raw?.substring(0, 200));
    return [];
  }
}

async function agentBriefWriter(partialBrief) {
  const raw = await callClaude(
    `You are PeerRefer's Brief Writer. Help a TA write a clear, referrer-friendly job brief. Return ONLY valid JSON:
{"title":"refined title","skills":["skill1","skill2","skill3"],"seniority":"Senior (6+ years)","description":"2 sentence role description for referrers"}`,
    `TA input: "${partialBrief}"`
  );
  try { return JSON.parse(raw.replace(/\`\`\`json|\`\`\`/g, "").trim()); }
  catch { return null; }
}

async function agentReferrerRecruiter(referral, referrer, activeBriefs) {
  const raw = await callClaude(
    `You are PeerRefer's Referrer Recruiter agent. A referral just led to a successful placement.
Write a warm, genuine, personalised message to the referrer celebrating the placement, confirming their bonus, sharing their reputation stats, and nudging them toward the next referral opportunity.
Keep it conversational, human, not corporate. Max 4 sentences. End with one specific brief they could refer for.
Return ONLY valid JSON: {"subject":"message subject","message":"the full message","suggestedBrief":"brief title or null"}`,
    `Referrer: ${referrer.name}, Candidate placed: ${referral.candidateName}, Bonus earned: $${referral.bonusOffered || 2000}, Referrer stats: ${referrer.total_placed || 1} placements from ${referrer.total_referred || 1} referrals, Active briefs: ${activeBriefs.slice(0,3).map(b => b.title + " ($" + b.bonus + " bonus)").join(", ")}`
  );
  try { return JSON.parse(raw.replace(/\`\`\`json|\`\`\`/g, "").trim()); }
  catch { return null; }
}

async function agentMarketIntel(brief, referrals) {
  const raw = await callClaude(
    `You are PeerRefer's Market Intelligence agent. Analyse a job brief and talent pool to give the TA honest market intelligence.
Use your knowledge of the Australian job market, Melbourne specifically, salary benchmarks, and demand levels for this role type.
Be specific, honest, and recruiter-focused. Return ONLY valid JSON:
{"marketDemand":"high|medium|low","salaryRange":"e.g. $120K-$145K","avgDaysToFill":21,"bonusBenchmark":2500,"insights":["insight 1","insight 2","insight 3"],"recommendation":"one specific actionable recommendation"}`,
    `Role: ${brief.title}, Skills: ${(brief.skills||[]).join(", ")}, Seniority: ${brief.seniority}, Location: ${brief.location}, Current bonus: $${brief.bonus}, Pool size: ${referrals.length} candidates, Pool skills: ${[...new Set(referrals.flatMap(r => r.skills||[]))].slice(0,10).join(", ")}`
  );
  try { return JSON.parse(raw.replace(/\`\`\`json|\`\`\`/g, "").trim()); }
  catch { return null; }
}
async function agentLinkedInFill(linkedinUrl) {
  const raw = await callClaude(
    `You are PeerRefer's LinkedIn Profile Extractor. Given a LinkedIn profile URL, infer as much as possible about the candidate.
Return ONLY valid JSON with no markdown:
{
  "name": "Full Name",
  "currentRole": "Current Job Title",
  "currentCompany": "Current Company",
  "skills": ["skill1", "skill2", "skill3", "skill4"],
  "note": "2-3 sentence professional vouch note written as if from a colleague who knows them well. Mention their likely strengths based on their role.",
  "seniority": "Junior/Mid/Senior/Lead/Executive"
}
If you cannot determine a field, make a reasonable inference based on the URL slug or leave as empty string.`,
    `LinkedIn URL: ${linkedinUrl}
Extract profile information and generate a professional vouch note.`
  );
  try { return JSON.parse(raw.replace(/\`\`\`json|\`\`\`/g, "").trim()); }
  catch { return null; }
}

async function agentNudge(referrals) {
  const stale = referrals.filter(r => r.status === "new");
  const raw = await callClaude(
    `You are PeerRefer's Pipeline Nudger. Write a brief direct daily digest. Mention specific names. 2-3 sentences max. Return ONLY valid JSON: {"message":"nudge text","urgentCount":1}`,
    `${stale.length} referrals at new status: ${stale.map(r => r.candidateName).join(", ")}`
  );
  return parseJSON(raw);
}

// ============================================
// SIMULATED USER ACCOUNTS
// ============================================
// Auth handled via Supabase — see LoginScreen component

// ============================================
// SEED DATA
// ============================================
const avg = r => ((r.skillsRating + r.workEthicRating + r.coachabilityRating) / 3).toFixed(1);
const inits = n => n.split(" ").map(x => x[0]).join("").toUpperCase();
const blurName = n => n.split(" ").map((w, i) => i === 0 ? w : w[0] + ".").join(" ");

const TA_REFERRALS = [
  { id: "r1", candidateName: "Sarah Chen", candidateEmail: "sarah.chen@gmail.com", skills: ["Product Management", "Agile", "SaaS"], note: "Worked with Sarah at Atlassian for 3 years. She ran the entire onboarding squad — exceptional delivery under pressure. Shipped 4 major product releases on time with zero escalations.", referrerName: "James Okafor", referrerRelationship: "Former colleague", referrerStats: { total: 8, placed: 3 }, skillsRating: 5, workEthicRating: 5, coachabilityRating: 4, submittedAt: "2 hours ago", status: "new", aiAnalysis: null, draftEmail: null, blurred: false },
  { id: "r2", candidateName: "Marcus Webb", candidateEmail: "marcus.webb@gmail.com", skills: ["Talent Acquisition", "Executive Search", "Boolean Search"], note: "Marcus is the sharpest recruiter I've encountered in 10 years. Placed C-suite at two unicorns and built a 40-person TA function from scratch.", referrerName: "Priya Nair", referrerRelationship: "Peer / Industry contact", referrerStats: { total: 5, placed: 2 }, skillsRating: 5, workEthicRating: 4, coachabilityRating: 5, submittedAt: "1 day ago", status: "contacted", aiAnalysis: null, draftEmail: null, blurred: false },
  { id: "r3", candidateName: "Anika Russo", candidateEmail: "anika.russo@gmail.com", skills: ["Data Analytics", "SQL", "Tableau", "Python"], note: "Anika built our entire reporting stack from scratch in 6 months — technically brilliant and communicates to non-technical execs with real clarity. Ready for a senior step up.", referrerName: "Tom Haggerty", referrerRelationship: "Manager", referrerStats: { total: 3, placed: 1 }, skillsRating: 4, workEthicRating: 5, coachabilityRating: 5, submittedAt: "3 days ago", status: "saved", aiAnalysis: null, draftEmail: null, blurred: false },
];

const JAMES_REFERRALS = [
  { id: "jr1", candidateName: "Sarah Chen", initials: "SC", skills: ["Product Management", "Agile"], referredTo: "Mia Thorne · Acme Co", submittedAt: "2 Apr 2026", status: "contacted", consentStatus: "consented", bonusOffered: 2000, bonusPaid: false, note: "Worked with Sarah at Atlassian for 3 years.", timeline: [{ e: "You submitted referral", d: "2 Apr", done: true }, { e: "Sarah gave consent", d: "2 Apr", done: true }, { e: "Mia viewed referral", d: "3 Apr", done: true }, { e: "Mia reached out", d: "5 Apr", done: true }, { e: "Placement confirmed", d: "—", done: false }] },
  { id: "jr2", candidateName: "Anika Russo", initials: "AR", skills: ["Data Analytics", "SQL"], referredTo: "Mia Thorne · Acme Co", submittedAt: "19 Mar 2026", status: "placed", consentStatus: "consented", bonusOffered: 2000, bonusPaid: true, note: "Anika built our entire reporting stack from scratch.", timeline: [{ e: "You submitted referral", d: "19 Mar", done: true }, { e: "Anika gave consent", d: "19 Mar", done: true }, { e: "Mia viewed referral", d: "20 Mar", done: true }, { e: "Mia reached out", d: "21 Mar", done: true }, { e: "Placement confirmed", d: "28 Mar", done: true }] },
  { id: "jr3", candidateName: "Tom Brennan", initials: "TB", skills: ["Engineering", "AWS"], referredTo: "Priya Nair · TechScale", submittedAt: "5 Mar 2026", status: "new", consentStatus: "pending", bonusOffered: 3000, bonusPaid: false, note: "Tom led the platform team at Canva for 2 years.", timeline: [{ e: "You submitted referral", d: "5 Mar", done: true }, { e: "Consent email sent", d: "5 Mar", done: true }, { e: "Tom gives consent", d: "—", done: false }, { e: "TA reviews referral", d: "—", done: false }, { e: "Placement confirmed", d: "—", done: false }] },
];


const STATUS_TA = { new: { label: "New", color: "#E8896A", bg: "rgba(0,0,0,0.07)" }, contacted: { label: "Contacted", color: "#F59E0B", bg: "#FBF2E3" }, saved: { label: "Saved", color: "#38BDF8", bg: "#E8F2F8" }, placed: { label: "Placed ⭐", color: "#6B4FBB", bg: "#F0EDFB" } };
const STATUS_REF = { new: { label: "In Review", color: "#38BDF8", bg: "#E8F2F8", dot: "#3A6B8A" }, contacted: { label: "Contacted ✓", color: "#E8896A", bg: "rgba(0,0,0,0.07)", dot: "#E8896A" }, placed: { label: "Placed 🎉", color: "#6B4FBB", bg: "#F0EDFB", dot: "#6B4FBB" }, declined: { label: "Declined", color: "#9CA3AF", bg: "#F0EDEA", dot: "#C4BAB2" } };

// ============================================
// SMALL COMPONENTS
// ============================================
function Spin({ color = "#E8896A" }) {
  return <div style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.08)", borderTop: "2px solid " + color, borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />;
}

function Tag({ children, color = "#E8896A", bg = "rgba(0,0,0,0.07)" }) {
  return <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontFamily: "DM Mono, monospace", fontWeight: 500 }}>{children}</span>;
}

function Dots({ value }) {
  return <div style={{ display: "flex", gap: 3 }}>{[1,2,3,4,5].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i <= value ? "#E8896A" : "rgba(0,0,0,0.08)" }} />)}</div>;
}

// ============================================
// SERVICE WORKER + PUSH NOTIFICATIONS
async function registerSW() {
  if (!("serviceWorker" in navigator)) return;
  try {
    // Unregister ALL service workers and clear all caches
    // PWA disabled to prevent stale cache issues
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) { await reg.unregister(); }
    const cacheKeys = await caches.keys();
    for (const key of cacheKeys) { await caches.delete(key); }
  } catch (e) { console.log("SW cleanup failed:", e); }
}

async function requestPushPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const permission = await Notification.requestPermission();
  return permission === "granted";
}

function showLocalNotification(title, body, url = "/") {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  navigator.serviceWorker.ready.then(registration => {
    registration.showNotification(title, {
      body,
      icon: "/favicon.svg",
      badge: "/favicon.svg",
      data: { url },
      vibrate: [100, 50, 100],
    });
  }).catch(() => {
    new Notification(title, { body, icon: "/favicon.svg" });
  });
}

// Register SW on load
if (typeof window !== "undefined") registerSW();

// ============================================
function getReferrerTier(totalPlaced) {
  if (totalPlaced >= 6) return { name: "Platinum", emoji: "💎", color: "#6B4FBB", bg: "rgba(107,79,187,0.1)", border: "rgba(107,79,187,0.25)", next: null, nextAt: null };
  if (totalPlaced >= 3) return { name: "Gold", emoji: "🥇", color: "#C47D2B", bg: "rgba(196,125,43,0.1)", border: "rgba(196,125,43,0.25)", next: "Platinum", nextAt: 6 };
  if (totalPlaced >= 1) return { name: "Silver", emoji: "🥈", color: "#6B7280", bg: "rgba(107,114,128,0.1)", border: "rgba(107,114,128,0.25)", next: "Gold", nextAt: 3 };
  return { name: "Bronze", emoji: "🥉", color: "#C4623E", bg: "rgba(196,98,62,0.08)", border: "rgba(196,98,62,0.2)", next: "Silver", nextAt: 1 };
}

function TierBadge({ totalPlaced, size = "sm" }) {
  const tier = getReferrerTier(totalPlaced || 0);
  const isLg = size === "lg";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: isLg ? 7 : 4, background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: isLg ? 12 : 20, padding: isLg ? "6px 12px" : "2px 8px" }}>
      <span style={{ fontSize: isLg ? 18 : 12 }}>{tier.emoji}</span>
      <span style={{ fontSize: isLg ? 13 : 10, fontWeight: 700, color: tier.color, fontFamily: "DM Mono, monospace", letterSpacing: "0.04em" }}>{tier.name}</span>
    </div>
  );
}

function RepRing({ score }) {
  const r = 42, c = 2 * Math.PI * r;
  const label = score >= 80 ? "Elite" : score >= 60 ? "Trusted" : score >= 40 ? "Active" : "New";
  const color = score >= 80 ? "#C4623E" : score >= 60 ? "#5B6EAF" : "#D97706";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", width: 104, height: 104 }}>
        <svg width="104" height="104" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="52" cy="52" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="7" />
          <circle cx="52" cy="52" r={r} fill="none" stroke={color} strokeWidth="7" strokeDasharray={(score / 100 * c) + " " + c} strokeLinecap="round" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, color: "#fff", lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontFamily: "DM Mono, monospace" }}>/ 100</div>
      </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.9)", fontFamily: "DM Mono, monospace", letterSpacing: "0.04em" }}>{label} Referrer</div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginTop: 1 }}>Your reputation score</div>
      </div>
    </div>
  );
}

function Timeline({ steps }) {
  return (
    <div>
      {steps.map((s, i) => {
        const isDeclined = s.declined;
        const dotColor = isDeclined ? "#EF4444" : s.done ? "#E8896A" : "rgba(0,0,0,0.07)";
        const lineColor = s.done && !isDeclined ? "#E8896A" : "rgba(0,0,0,0.07)";
        return (
          <div key={i} style={{ display: "flex", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: dotColor, border: (s.done || isDeclined) ? "none" : "2px dashed rgba(26,22,18,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isDeclined && <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1l6 6M7 1L1 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                {s.done && !isDeclined && <svg width="8" height="7" viewBox="0 0 8 7" fill="none"><path d="M1 3.5l2 2L7 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              {i < steps.length - 1 && <div style={{ width: 2, height: 20, background: lineColor, margin: "2px 0" }} />}
            </div>
            <div style={{ paddingTop: 1 }}>
              <div style={{ fontSize: 12, color: isDeclined ? "#EF4444" : s.done ? "#1A1612" : "#9CA3AF", fontWeight: (s.done || isDeclined) ? 600 : 400, marginBottom: 1 }}>{s.e}</div>
              <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: i < steps.length - 1 ? 5 : 0 }}>{s.d}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// SUBMIT MODAL — used by BOTH portals
// ============================================
// ============================================
function ReReferModal({ candidate, activeBriefs, onClose, onSubmit, user }) {
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [vouchNote, setVouchNote] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter out briefs the candidate was already referred for
  const alreadyReferredIds = candidate?.referredBriefIds || [];
  const availableBriefs = (activeBriefs || []).filter(b =>
    !alreadyReferredIds.includes(b.id) &&
    (!locationFilter ||
      (b.location || "").toLowerCase().includes(locationFilter.toLowerCase()) ||
      (b.location || "").toLowerCase().includes("remote"))
  );

  const handleSubmit = async () => {
    if (!selectedBrief) { alert("Please select a role."); return; }
    setSubmitting(true);
    await onSubmit({
      candidateName: candidate.candidateName,
      candidateEmail: candidate.candidateEmail,
      skills: candidate.skills || [],
      note: vouchNote,
      briefId: selectedBrief.id,
      briefTitle: selectedBrief.title,
      refName: user?.name || "",
      refRel: candidate.referrerRelationship || "",
      s1: candidate.skillsRating || 5,
      s2: candidate.workEthicRating || 5,
      s3: candidate.coachabilityRating || 5,
    });
    setSubmitting(false);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#FFFFFF", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        
        {/* Header */}
        <div style={{ padding: "24px 24px 16px", borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 20, color: "#1C1917" }}>
              Refer to another role
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9CA3AF", padding: 0, lineHeight: 1 }}>×</button>
          </div>
          {/* Candidate pill */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F8F5F2", borderRadius: 20, padding: "6px 12px" }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
              {(candidate.candidateName || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2)}
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{candidate.candidateName}</span>
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>· details pre-filled</span>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          
          {/* Step 1: Pick role */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 8 }}>
              Which role? <span style={{ color: "#EF4444" }}>*</span>
            </label>
            
            {/* Location filter */}
            <div style={{ position: "relative", marginBottom: 10 }}>
              <input
                type="text"
                value={locationFilter}
                onChange={e => setLocationFilter(e.target.value)}
                placeholder="Filter by location..."
                style={{ width: "100%", background: "#F9F7F5", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, padding: "8px 12px 8px 30px", fontFamily: "inherit", fontSize: 12, outline: "none", color: "#1C1917", boxSizing: "border-box" }}
              />
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 13 }}>📍</span>
              {locationFilter && <button onClick={() => setLocationFilter("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#9CA3AF", padding: 0 }}>×</button>}
            </div>

            {/* Brief list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {availableBriefs.length === 0 && (
                <div style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", padding: "20px 0" }}>
                  {locationFilter ? `No roles in "${locationFilter}"` : "No other active roles available"}
                </div>
              )}
              {availableBriefs.map(brief => {
                const isSel = selectedBrief?.id === brief.id;
                return (
                  <div key={brief.id}
                    onClick={() => setSelectedBrief(isSel ? null : brief)}
                    style={{ borderRadius: 10, border: "1.5px solid", borderColor: isSel ? "#D85A30" : "rgba(0,0,0,0.1)", background: isSel ? "rgba(216,90,48,0.04)" : "#FAFAF8", padding: "12px 14px", cursor: "pointer", transition: "all 0.15s ease" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 3 }}>{brief.title}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>
                          {[brief.location, brief.seniority].filter(Boolean).join(" · ")}
                        </div>
                        {brief.bonus > 0 && (
                          <div style={{ fontSize: 10, color: "#E8896A", fontWeight: 700, marginTop: 3, fontFamily: "DM Mono, monospace" }}>
                            ${brief.bonus.toLocaleString()} bonus
                          </div>
                        )}
                      </div>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid", borderColor: isSel ? "#D85A30" : "rgba(0,0,0,0.15)", background: isSel ? "#D85A30" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        {isSel && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FFFFFF" }}/>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 2: Vouch note */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
              Why is {candidate.candidateName?.split(" ")[0]} a great fit for this role?
              <span style={{ fontWeight: 400, color: "#9CA3AF", marginLeft: 4 }}>(optional but recommended)</span>
            </label>
            <textarea
              value={vouchNote}
              onChange={e => setVouchNote(e.target.value)}
              placeholder={`e.g. "${candidate.candidateName?.split(" ")[0]} has specific experience in this area that makes them perfect for this role..."`}
              rows={3}
              style={{ width: "100%", background: "#F9F7F5", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 13, outline: "none", color: "#1C1917", resize: "vertical", boxSizing: "border-box" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.07)", display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, background: "transparent", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 9, padding: "12px", fontSize: 13, fontWeight: 600, color: "#6B7280", cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!selectedBrief || submitting}
            style={{ flex: 2, background: selectedBrief ? "#D85A30" : "#E5E7EB", border: "none", borderRadius: 9, padding: "12px", fontSize: 13, fontWeight: 700, color: selectedBrief ? "#FFFFFF" : "#9CA3AF", cursor: selectedBrief ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "all 0.15s ease" }}>
            {submitting ? "Sending consent..." : `Refer ${candidate.candidateName?.split(" ")[0]} for this role →`}
          </button>
        </div>
      </div>
    </div>
  );
}


function SubmitModal({ onClose, onSubmit, isReferrer, taBonus, activeBriefs: propBriefs, taName, taPrefs, taSlug, referrerLocation, prefilledCandidate }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", linkedin: "", currentRole: "", skillsInput: "", skills: [], note: "", refName: "", refRel: "", refRelType: "", refDuration: "", refStandout: "", refWorkAgain: "", s1: 0, s2: 0, s3: 0 });
  const [coaching, setCoaching] = useState(null);
  const [coachLoad, setCoachLoad] = useState(false);
  const [timer, setTimer] = useState(null);
  const allBriefs = propBriefs || [];

  // Smart filtering: TA's own briefs first, then location matches, then rest
  const activeBriefs = (() => {
    if (!allBriefs.length) return [];
    const taBriefs = taSlug ? allBriefs.filter(b => b.ta_slug === taSlug || b.taSlug === taSlug) : [];
    const locationBriefs = referrerLocation
      ? allBriefs.filter(b => !taBriefs.find(t => t.id === b.id) && (
          (b.location || "").toLowerCase().includes(referrerLocation.toLowerCase()) ||
          (b.location || "").toLowerCase().includes("remote")
        ))
      : [];
    const rest = allBriefs.filter(b =>
      !taBriefs.find(t => t.id === b.id) && !locationBriefs.find(l => l.id === b.id)
    );
    return [...taBriefs, ...locationBriefs, ...rest];
  })();
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [locationFilter, setLocationFilter] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [profileText, setProfileText] = useState("");
  const [filling, setFilling] = useState(false);
  const [filled, setFilled] = useState(false);
  const [passportPrefill, setPassportPrefill] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Pre-fill form if re-referring an existing candidate
  useEffect(() => {
    if (prefilledCandidate) {
      setForm(f => ({
        ...f,
        name: prefilledCandidate.candidateName || "",
        email: prefilledCandidate.candidateEmail || "",
        skills: prefilledCandidate.skills || [],
        skillsInput: (prefilledCandidate.skills || []).join(", "),
        refName: prefilledCandidate.referrerName || f.refName,
        refRel: prefilledCandidate.referrerRelationship || f.refRel,
        note: "", // always clear vouch note for new role
      }));
      // Skip to step 2 (role selection) if candidate pre-filled
      setStep(2);
    }
  }, [prefilledCandidate]); // eslint-disable-line

  // Check if candidate was previously referred — pre-fill from Passport
  const checkPassport = async (email) => {
    if (!email.includes("@")) return;
    const { data } = await supabase.from("candidates").select("*").eq("email", email.trim().toLowerCase()).maybeSingle();
    if (data) {
      setPassportPrefill(data);
      if (data.name) set("name", data.name);
      if (data.skills?.length) set("skills", data.skills);
      if (data.current_role) set("currentRole", data.current_role);
    }
  };

  const quickFill = async () => {
    if (!linkedinUrl.trim()) return;
    setFilling(true);
    const data = await agentLinkedInFill(linkedinUrl.trim(), profileText.trim());
    if (data) {
      if (data.name) set("name", data.name);
      if (data.skills) set("skills", data.skills);
      if (data.note) set("note", data.note);
      if (data.currentRole) set("currentRole", data.currentRole);
      setFilled(true);
    }
    setFilling(false);
  };
  const addSkill = s => { const t = s.trim(); if (t && !form.skills.includes(t)) { set("skills", [...form.skills, t]); set("skillsInput", ""); } };
  const QUICK = ["Product Management", "Engineering", "Data Analytics", "UX Design", "Talent Acquisition", "Sales", "Marketing", "Operations"];

  const handleNote = v => {
    set("note", v);
    if (timer) clearTimeout(timer);
    if (v.length > 50) {
      setTimer(setTimeout(async () => {
        setCoachLoad(true);
        const r = await agentCoach(v, form.skills.length ? form.skills : ["Not specified"]);
        setCoaching(r);
        setCoachLoad(false);
      }, 1400));
    } else setCoaching(null);
  };

  const submit = () => {
    if (!form.name || !form.email || !form.refName || form.skills.length === 0) return;
    onSubmit({
      candidateName: form.name,
      candidateEmail: form.email,
      candidateLinkedin: form.linkedin,
      skills: form.skills,
      note: form.note,
      referrerName: form.refName,
      referrerRelationship: form.refRelType || form.refRel || "Professional contact",
      referrerDuration: form.refDuration || "",
      referrerStandout: form.refStandout || "",
      referrerWorkAgain: form.refWorkAgain || "",
      referrerStats: { total: 1, placed: 0 },
      skillsRating: form.s1 || 4,
      workEthicRating: form.s2 || 4,
      coachabilityRating: form.s3 || 4,
      blurred: isReferrer ? true : false,
      briefId: selectedBrief?.id !== "general" ? selectedBrief?.id : null,
      briefTitle: selectedBrief?.id !== "general" ? selectedBrief?.title : null,
    });
    onClose();
  };

  const cc = coaching ? (coaching.score >= 75 ? "#E8896A" : coaching.score >= 50 ? "#C47D2B" : "#B85450") : "#E8896A";

  function StarRow({ label, val, onChange }) {
    const [hov, setHov] = useState(0);
    return (
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", marginBottom: 5 }}>{label}</div>
        <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
          {[1,2,3,4,5].map(n => (
            <span key={n} onMouseEnter={() => setHov(n)} onMouseLeave={() => setHov(0)} onClick={() => onChange(n)}
              style={{ fontSize: 20, cursor: "pointer", filter: n <= (hov || val) ? "none" : "grayscale(1) opacity(0.3)" }}>⭐</span>
          ))}
          {val > 0 && <span style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 6 }}>{["","Developing","Solid","Strong","Excellent","Exceptional"][val]}</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div style={{ background: "#FAFAF8", borderRadius: 20, padding: "30px 26px", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(26,18,24,0.25)", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 22, color: "#1C1917" }}>Refer Someone</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>🤖 AI coach rates your note live</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9CA3AF" }}>✕</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
          {[1,2].map(n => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: step >= n ? "#1A1612" : "rgba(0,0,0,0.07)", color: step >= n ? "#FFFFFF" : "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>{n}</div>
              <span style={{ fontSize: 12, color: step === n ? "#1A1612" : "#9CA3AF", fontWeight: step === n ? 700 : 400 }}>{n === 1 ? "About them" : "About you"}</span>
              {n < 2 && <div style={{ width: 24, height: 2, background: step > n ? "#1A1612" : "rgba(0,0,0,0.07)", borderRadius: 1 }} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* TA Preferences - only show if TA has real data */}
            {(taName || selectedBrief?.ta_profiles?.name) && (taPrefs || selectedBrief) && <div style={{ background: "#FAFAF8", borderRadius: 11, padding: "14px 16px", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, fontFamily: "DM Mono, monospace" }}>
                WHAT {selectedBrief?.ta_profiles?.name?.split(" ")[0]?.toUpperCase() || taName?.split(" ")[0]?.toUpperCase() || "THIS TA"} IS LOOKING FOR
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {[
                  { label: "Roles", value: (selectedBrief?.ta_profiles?.lookingFor || selectedBrief?.ta_profiles?.looking_for || taPrefs?.lookingFor || taPrefs?.looking_for || "") },
                  { label: "Industries", value: (selectedBrief?.ta_profiles?.industries || taPrefs?.industries || "") },
                  { label: "Seniority", value: (selectedBrief?.ta_profiles?.seniority || taPrefs?.seniority || "") },
                  { label: "Location", value: (selectedBrief?.ta_profiles?.location || selectedBrief?.location || taPrefs?.location || "") },
                ].filter(item => item.value).map((item, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#374151" }}>
                    <strong style={{ color: "#1C1917" }}>{item.label}:</strong> {item.value}
                  </div>
                ))}
              </div>
              {(selectedBrief?.ta_profiles?.referrer_note || taPrefs?.referrerNote) && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(232,137,106,0.08)", borderRadius: 8, fontSize: 12, color: "#C4623E", fontStyle: "italic" }}>
                  "{selectedBrief?.ta_profiles?.referrer_note || taPrefs?.referrerNote}"
                </div>
              )}
            </div>}

            {/* Which role */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 8 }}>Which role are you referring for?</label>
              
              {/* Location filter */}
              <div style={{ position: "relative", marginBottom: 10 }}>
                <input
                  type="text"
                  value={locationFilter}
                  onChange={e => setLocationFilter(e.target.value)}
                  placeholder="Filter by location (e.g. Melbourne, Remote)"
                  style={{ width: "100%", background: "#F9F7F5", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, padding: "8px 12px 8px 32px", fontFamily: "inherit", fontSize: 12, outline: "none", color: "#1C1917", boxSizing: "border-box" }}
                />
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14 }}>📍</span>
                {locationFilter && (
                  <button onClick={() => setLocationFilter("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#9CA3AF", padding: 0 }}>×</button>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(() => {
                  const filtered = activeBriefs.filter(b =>
                    !locationFilter ||
                    (b.location || "").toLowerCase().includes(locationFilter.toLowerCase()) ||
                    (b.location || "").toLowerCase().includes("remote") ||
                    locationFilter.toLowerCase() === "remote"
                  );
                  return (
                    <>
                      {filtered.length > 0 && (
                        <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>
                          {filtered.length} role{filtered.length !== 1 ? "s" : ""}{locationFilter ? ` in "${locationFilter}"` : " — best matches first"}
                        </div>
                      )}
                      {filtered.length === 0 && locationFilter && (
                        <div style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", padding: "16px 0" }}>
                          No roles in "{locationFilter}" — try "Remote" or clear the filter
                        </div>
                      )}
                      {filtered.map(brief => {
                  const isSel = selectedBrief?.id === brief.id;
                  return (
                    <div key={brief.id} style={{ borderRadius: 9, border: "1.5px solid", borderColor: isSel ? "#5B6EAF" : "rgba(0,0,0,0.1)", background: isSel ? "rgba(91,110,175,0.04)" : "#FFFFFF", overflow: "hidden" }}>
                      <div onClick={() => setSelectedBrief(isSel ? null : brief)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", cursor: "pointer" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 2 }}>{brief.title}</div>
                          <div style={{ fontSize: 11, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(brief.skills||[]).slice(0,3).join(" · ")}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 12, flexShrink: 0 }}>
                          
                          <span style={{ fontSize: 11, color: "#9CA3AF" }}>{isSel ? "▲" : "▼"}</span>
                        </div>
                      </div>
                      {isSel && (
                        <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: "12px 14px", background: "rgba(91,110,175,0.02)" }}>
                          {brief.logoUrl && <img src={brief.logoUrl} alt="logo" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", border: "1px solid rgba(0,0,0,0.06)", background: "#fff", padding: 3, marginBottom: 8 }} />}
                          {brief.positionDescription && <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.65, margin: "0 0 8px" }}>{brief.positionDescription}</p>}
                          {brief.evp && (
                            <div style={{ background: "rgba(91,110,175,0.06)", borderRadius: 8, padding: "8px 10px", marginBottom: 8, border: "1px solid rgba(91,110,175,0.12)" }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#5B6EAF", letterSpacing: "0.06em", marginBottom: 3 }}>WHY JOIN US</div>
                              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{brief.evp}</div>
                            </div>
                          )}
                          {brief.description && <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.65, margin: "0 0 10px" }}>{brief.description}</p>}
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                            {(brief.skills||[]).map(s => <span key={s} style={{ background: "rgba(0,0,0,0.06)", color: "#374151", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontFamily: "DM Mono, monospace" }}>{s}</span>)}
                          </div>
                          {brief.seniority && <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>📈 {brief.seniority}</div>}
                          {brief.location && <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10 }}>📍 {brief.location}</div>}
                          <div style={{ background: "rgba(232,137,106,0.1)", borderRadius: 7, padding: "8px 12px", fontSize: 12, color: "#C4623E", fontWeight: 600 }}>✓ Referring for: {brief.title}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
                    </>
                  );
                })()}

              </div>
            </div>

            {/* Candidate details */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Their full name *</label>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Sarah Chen"
                  style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#1C1917" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                  Their email * <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF" }}>— required to send consent request</span>
                </label>
                <input value={form.email} onChange={e => set("email", e.target.value)} onBlur={e => checkPassport(e.target.value)} placeholder="candidate@email.com" type="email"
                  style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: passportPrefill ? 6 : 0 }} />
                {passportPrefill && (
                  <div style={{ background: "rgba(72,187,120,0.08)", border: "1px solid rgba(72,187,120,0.2)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#059669", fontWeight: 600 }}>
                    ✓ Talent Passport found — {passportPrefill.name || "details"} pre-filled from previous referral
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Their LinkedIn (optional)</label>
                <input value={form.linkedin} onChange={e => set("linkedin", e.target.value)} placeholder="linkedin.com/in/their-profile"
                  style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#1C1917" }} />
              </div>
            </div>

            {/* Key skills */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Key skills *</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input value={form.skillsInput} onChange={e => set("skillsInput", e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && form.skillsInput.trim()) { set("skills", [...form.skills, form.skillsInput.trim()]); set("skillsInput", ""); }}}
                  placeholder="Add skill and press Enter..."
                  style={{ flex: 1, background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#1C1917" }} />
                <button onClick={() => { if (form.skillsInput.trim()) { set("skills", [...form.skills, form.skillsInput.trim()]); set("skillsInput", ""); }}}
                  style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 9, padding: "10px 18px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Add</button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {form.skills.map(s => (
                  <span key={s} style={{ background: "rgba(0,0,0,0.06)", color: "#374151", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontFamily: "DM Mono, monospace", display: "flex", alignItems: "center", gap: 6 }}>
                    {s} <span onClick={() => set("skills", form.skills.filter(x => x !== s))} style={{ cursor: "pointer", color: "#9CA3AF", fontSize: 14, lineHeight: 1 }}>×</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Vouch note */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Why are you vouching? * <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— AI coach rates live</span>
              </label>
              <textarea value={form.note} onChange={e => handleNote(e.target.value)}
                placeholder="Specific examples carry the most weight — how you've worked together, achievements, what sets them apart..."
                style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", minHeight: 100, resize: "vertical", color: "#1C1917" }} />
              {(coachLoad || coaching) && (
                <div style={{ marginTop: 7, background: "#FFFFFF", borderRadius: 9, padding: "11px 13px", borderLeft: "4px solid", borderLeftColor: coaching ? (coaching.score >= 70 ? "#059669" : coaching.score >= 40 ? "#D97706" : "#EF4444") : "#E5E7EB" }}>
                  {coachLoad
                    ? <div style={{ display: "flex", alignItems: "center", gap: 7, color: "#9CA3AF", fontSize: 12 }}><Spin />AI coach rating...</div>
                    : coaching && (
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: coaching.score >= 70 ? "#059669" : coaching.score >= 40 ? "#D97706" : "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "DM Mono, monospace" }}>{coaching.rating}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: coaching.score >= 70 ? "#059669" : coaching.score >= 40 ? "#D97706" : "#EF4444" }}>{coaching.score}/100</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>{coaching.feedback}</div>
                        {coaching.privacyWarning && <div style={{ fontSize: 11, color: "#EF4444", marginTop: 6, padding: "5px 8px", background: "rgba(239,68,68,0.06)", borderRadius: 6 }}>{coaching.privacyWarning}</div>}
                        {coaching.suggestion && <div style={{ fontSize: 11, color: coaching.score >= 70 ? "#059669" : "#D97706", fontStyle: "italic", marginTop: 4 }}>💡 {coaching.suggestion}</div>}
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Consent notice */}
            <div style={{ background: "rgba(91,110,175,0.06)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(91,110,175,0.15)", fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
              ✅ <strong>Candidate consents before TA sees anything.</strong> After submitting, they receive a consent email. Only after they agree does their profile become visible to the TA.
            </div>

            {/* LinkedIn fill - collapsible */}
            <details style={{ background: "#F8F8FF", borderRadius: 10, border: "1px solid rgba(91,110,175,0.15)" }}>
              <summary style={{ padding: "11px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#5B6EAF", listStyle: "none", userSelect: "none" }}>
                ✦ Fill details from LinkedIn (optional AI assist)
              </summary>
              <div style={{ padding: "0 14px 14px" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8, marginTop: 10 }}>
                  <input value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} placeholder="linkedin.com/in/their-profile"
                    style={{ flex: 1, background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 7, padding: "8px 11px", fontFamily: "inherit", fontSize: 12, outline: "none", color: "#1C1917" }} />
                  <button onClick={() => quickFill()} disabled={filling || !linkedinUrl.trim()}
                    style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontFamily: "inherit", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                    {filling ? "Filling..." : "Fill with AI"}
                  </button>
                </div>
                <textarea value={profileText || ""} onChange={e => setProfileText(e.target.value)}
                  placeholder="Optional: paste their LinkedIn About section for a more specific vouch note..."
                  style={{ width: "100%", background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 7, padding: "8px 11px", fontFamily: "inherit", fontSize: 12, outline: "none", color: "#6B7280", minHeight: 50, resize: "vertical" }} />
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>AI fills name, skills and a vouch note. Paste their About section for better accuracy.</div>
              </div>
            </details>

            <button onClick={() => {
                if (!form.name.trim()) { alert("Please add the candidate's name."); return; }
                if (!form.email.trim()) { alert("Please add the candidate's email so we can send the consent request."); return; }
                setStep(2);
              }}
              style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 10, padding: "14px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer", width: "100%" }}>
              Continue → Your Details
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "#FAFAF8", borderRadius: 11, padding: "16px", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontFamily: "DM Mono, monospace" }}>Rate them across 3 dimensions</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <StarRow label="Skills Depth" val={form.s1} onChange={v => set("s1", v)} />
                <StarRow label="Work Ethic" val={form.s2} onChange={v => set("s2", v)} />
                <StarRow label="Coachability" val={form.s3} onChange={v => set("s3", v)} />
              </div>
            </div>
            <div style={{ borderTop: "1px solid rgba(0,0,0,0.07)", paddingTop: 14 }}>
              {/* Your name */}
              <div style={{ marginBottom: 11 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Your name *</label>
                <input value={form.refName} onChange={e => set("refName", e.target.value)} placeholder="Your full name"
                  style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
              </div>

              {/* Relationship type */}
              <div style={{ marginBottom: 11 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>How did you work together? *</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["Colleague", "I managed them", "They managed me", "Client relationship", "Project collaborator", "Mentor / mentee", "Industry contact"].map(opt => (
                    <button key={opt} type="button" onClick={() => { set("refRelType", opt); set("refRel", opt); }}
                      style={{ padding: "6px 12px", borderRadius: 20, border: "1.5px solid", borderColor: form.refRelType === opt ? "#D85A30" : "rgba(0,0,0,0.1)", background: form.refRelType === opt ? "#D85A30" : "#FAFAF8", color: form.refRelType === opt ? "#FFFFFF" : "#6B7280", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div style={{ marginBottom: 11 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>How long did you work together?</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["Less than 6 months", "6–12 months", "1–2 years", "2+ years"].map(opt => (
                    <button key={opt} type="button" onClick={() => set("refDuration", opt)}
                      style={{ padding: "6px 12px", borderRadius: 20, border: "1.5px solid", borderColor: form.refDuration === opt ? "#5B6EAF" : "rgba(0,0,0,0.1)", background: form.refDuration === opt ? "#5B6EAF" : "#FAFAF8", color: form.refDuration === opt ? "#FFFFFF" : "#6B7280", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Standout capability */}
              <div style={{ marginBottom: 11 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>
                  What's their standout capability? <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(one sentence)</span>
                </label>
                <input value={form.refStandout} onChange={e => set("refStandout", e.target.value)}
                  placeholder="e.g. Their real superpower is translating complex data into plain English for stakeholders"
                  style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 13, outline: "none" }} />
              </div>

              {/* Would work again */}
              <div style={{ marginBottom: 11 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Would you work with them again?</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[["yes", "✅ Absolutely"], ["maybe", "🤔 In the right role"], ["no", "Not sure"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => set("refWorkAgain", val)}
                      style={{ flex: 1, padding: "8px", borderRadius: 9, border: "1.5px solid", borderColor: form.refWorkAgain === val ? "#059669" : "rgba(0,0,0,0.1)", background: form.refWorkAgain === val ? "rgba(5,150,105,0.08)" : "#FAFAF8", color: form.refWorkAgain === val ? "#059669" : "#6B7280", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, background: "transparent", border: "1.5px solid rgba(26,22,18,0.18)", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#9CA3AF" }}>← Back</button>
              <button onClick={submit} style={{ flex: 2, background: "#E8896A", color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Submit Referral →</button>
            </div>
            <p style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>By submitting you confirm this is a genuine referral and you have a real relationship with this person.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// LOGIN SCREEN
// ============================================
// ============================================
// PRIVACY POLICY + T&C MODALS
function LegalModal({ type, onClose }) {
  const isPrivacy = type === "privacy";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#FFFFFF", borderRadius: 16, width: "100%", maxWidth: 600, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(0,0,0,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917" }}>{isPrivacy ? "Privacy Policy" : "Terms & Conditions"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9CA3AF" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "24px", fontSize: 13, color: "#374151", lineHeight: 1.8 }}>
          {isPrivacy ? (
            <>
              <p style={{ color: "#9CA3AF", fontSize: 11, marginBottom: 20, fontFamily: "DM Mono, monospace" }}>Last updated: May 2026 · © CollabPathways Pty Ltd 2026</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", marginBottom: 8 }}>1. What we collect</h3>
              <p>PeerRefer collects information you provide directly: name, email address, professional skills, work history, and career preferences. For candidates, we collect information submitted as part of the referral consent process including your Talent Passport.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>2. How we use your data</h3>
              <p>Your data is used solely to facilitate referrals between referrers and talent acquisition professionals. Candidate data is never shared with any third party without explicit consent. We do not sell your data. Ever.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>3. Consent</h3>
              <p>Candidates must give explicit consent before their details are visible to any talent acquisition professional. You can withdraw consent at any time by contacting us at privacy@peerrefer.com. Withdrawn consent results in immediate deletion of your profile from that referral.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>4. Data retention</h3>
              <p>Declined referral data is automatically deleted after 90 days. Active candidate profiles are retained until you request deletion. You may request deletion of your data at any time by emailing privacy@peerrefer.com.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>5. Security</h3>
              <p>All data is stored securely via Supabase with row-level security. Passwords are never stored in plain text. All connections are encrypted via HTTPS.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>6. Contact</h3>
              <p>For privacy enquiries: privacy@peerrefer.com<br/>CollabPathways Pty Ltd, Melbourne, Victoria, Australia</p>
            </>
          ) : (
            <>
              <p style={{ color: "#9CA3AF", fontSize: 11, marginBottom: 20, fontFamily: "DM Mono, monospace" }}>Last updated: May 2026 · © CollabPathways Pty Ltd 2026</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", marginBottom: 8 }}>1. Platform use</h3>
              <p>PeerRefer is a consent-first talent referral platform operated by CollabPathways Pty Ltd. By using PeerRefer, you agree to these terms. The platform is intended for professional use only.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>2. Referral bonuses</h3>
              <p>Talent acquisition professionals who advertise a referral bonus on a role enter into a binding commitment to pay that bonus. Payment terms:</p>
              <ul style={{ paddingLeft: 20, margin: "8px 0", lineHeight: 2 }}>
                <li>Bonus must be paid within <strong>30 days</strong> of a confirmed placement</li>
                <li>Placement is confirmed when the TA marks the candidate as "Hired" in PeerRefer</li>
                <li>Referrer confirmation via the confirmation link sent by email constitutes double-verification</li>
                <li>Non-payment within 30 days constitutes a breach of these Terms and may result in account suspension</li>
                <li>PeerRefer mediates disputes but is not liable for non-payment by talent acquisition professionals</li>
                <li>Referrers may flag non-payment via the "Flag Dispute" button — PeerRefer will respond within 2 business days</li>
              </ul>
              <p>CollabPathways Pty Ltd reserves the right to suspend or terminate accounts of talent acquisition professionals who repeatedly fail to honour bonus commitments.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>3. Candidate consent</h3>
              <p>Candidate details must never be shared outside the PeerRefer platform without their explicit consent. Talent acquisition professionals who breach candidate privacy will be permanently suspended.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>4. Prohibited conduct</h3>
              <p>You may not scrape, copy, or extract data from PeerRefer. You may not use automated tools to access the platform. You may not impersonate another person or organisation. Violation results in immediate account termination and potential legal action.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>5. Subscriptions</h3>
              <p>Pro subscriptions are billed monthly via Stripe. The first 30 days are free. You may cancel at any time. No refunds are provided for partial months.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>6. Intellectual property</h3>
              <p>PeerRefer, Talent Passport™, and all associated branding are the intellectual property of CollabPathways Pty Ltd. Unauthorised use is prohibited.</p>
              <h3 style={{ fontFamily: "Playfair Display, Georgia, serif", color: "#1C1917", margin: "16px 0 8px" }}>7. Contact</h3>
              <p>legal@peerrefer.com<br/>CollabPathways Pty Ltd, Melbourne, Victoria, Australia</p>
            </>
          )}
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.07)", flexShrink: 0, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>© CollabPathways Pty Ltd 2026 · All rights reserved</div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// FOOTER — shown on consent page and public pages
function PeerReferFooter({ minimal }) {
  const [legal, setLegal] = useState(null);
  return (
    <>
      {legal && <LegalModal type={legal} onClose={() => setLegal(null)} />}
      <div style={{ padding: minimal ? "16px 24px" : "24px 32px", borderTop: "1px solid rgba(0,0,0,0.06)", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.8 }}>
          <button onClick={() => setLegal("privacy")} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 11, textDecoration: "underline", fontFamily: "inherit" }}>Privacy Policy</button>
          {" · "}
          <button onClick={() => setLegal("terms")} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 11, textDecoration: "underline", fontFamily: "inherit" }}>Terms & Conditions</button>
          {" · "}
          <span>© CollabPathways Pty Ltd 2026</span>
        </div>
      </div>
    </>
  );
}

// ============================================
// EMAIL VERIFICATION PENDING SCREEN
function EmailVerificationPending({ email, portalType, onBack }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const resendEmail = async () => {
    setResending(true);
    await supabase.auth.resend({ type: "signup", email });
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 4000);
  };

  const isTA = portalType === "ta";
  return (
    <div style={{ minHeight: "100vh", background: isTA ? "#FFFFFF" : "linear-gradient(135deg, #1A0D12 0%, #3A1520 50%, #6B2D3E 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 20 }}>📬</div>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 26, color: isTA ? "#1A1612" : "#fff", letterSpacing: "-0.02em", marginBottom: 10 }}>Check your email</div>
        <div style={{ fontSize: 14, color: isTA ? "#6B7280" : "rgba(255,255,255,0.6)", lineHeight: 1.7, marginBottom: 8 }}>
          We've sent a verification link to
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: isTA ? "#1A1612" : "#fff", marginBottom: 24 }}>{email}</div>
        <div style={{ background: isTA ? "#F8F5F2" : "rgba(0,0,0,0.15)", borderRadius: 14, padding: "20px 24px", marginBottom: 24, textAlign: "left" }}>
          {["Click the link in your email to verify your account", "You'll be taken straight to your dashboard", "Link expires in 24 hours"].map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < 2 ? 12 : 0 }}>
              <span style={{ color: "#E8896A", fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: isTA ? "#6B7280" : "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>{s}</span>
            </div>
          ))}
        </div>
        <button onClick={resendEmail} disabled={resending || resent}
          style={{ width: "100%", background: resent ? "rgba(72,187,120,0.1)" : isTA ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)", border: resent ? "1px solid #48BB78" : "1px solid rgba(0,0,0,0.1)", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: resent ? "default" : "pointer", color: resent ? "#059669" : isTA ? "#6B7280" : "rgba(255,255,255,0.7)", marginBottom: 12 }}>
          {resent ? "✓ Verification email resent!" : resending ? "Sending..." : "Resend verification email"}
        </button>
        <button onClick={onBack}
          style={{ background: "none", border: "none", fontSize: 13, color: isTA ? "#9CA3AF" : "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit" }}>
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}

// ============================================
function LoginScreen({ onLogin, portalType, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nudge, setNudge] = useState("");
  const isTA = portalType === "ta";
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [referrerLocation, setReferrerLocation] = useState("");
  const [pendingVerification, setPendingVerification] = useState(false);

  if (pendingVerification) return <EmailVerificationPending email={email} portalType={portalType} onBack={() => { setPendingVerification(false); setMode("signin"); }} />;

  const handleSignIn = async () => {
    setLoading(true);
    setError("");
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) { setError(authError.message); setLoading(false); return; }
    // Block unverified users
    if (!data.user.email_confirmed_at) {
      await supabase.auth.signOut();
      setPendingVerification(true);
      setLoading(false);
      return;
    }
    // Load profile based on portal type
    if (isTA) {
      // Use service-key function to bypass RLS
      let profileData = { taProfile: null, referrer: null };
      try {
        const res = await fetch("/.netlify/functions/get-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: data.user.id, email: data.user.email }),
        });
        profileData = await res.json();
      } catch(e) { console.error("get-profile error:", e); }

      let profile = profileData.taProfile;

      if (!profile) {
        // Auto-create TA profile for new user
        const slug = data.user.email.split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "-") + "-" + Math.random().toString(36).slice(2,5);
        const { data: newProfile } = await supabase.from("ta_profiles").insert({
          auth_user_id: data.user.id, email: data.user.email,
          name: data.user.email.split("@")[0], company: "",
          slug, bonus_enabled: false, bonus_amount: 2000, is_pro: false,
        }).select().single();
        if (newProfile) { profile = newProfile; }
        else { setError("Could not load profile. Please try again."); await supabase.auth.signOut(); setLoading(false); return; }
      }

      const hasBoth = !!(profileData.referrer);
      // Save portal preference BEFORE calling onLogin
      localStorage.setItem("peerrefer_portal", "ta");
      localStorage.setItem("peerrefer_user_email", data.user.email);
      onLogin({ role: "ta", name: profile.name, company: profile.company, email: profile.email, initials: (profile.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), slug: profile.slug, id: profile.id, is_pro: profile.is_pro, hasBoth });
    } else {
      // Use service-key function to bypass RLS
      let profile = null;
      try {
        const res = await fetch("/.netlify/functions/get-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: data.user.id, email: data.user.email }),
        });
        const profileData = await res.json();
        profile = profileData.referrer || null;
      } catch(e) { console.error("get-profile error:", e); }

      // If no referrer profile, check if there's a pending signup that wasn't processed
      if (!profile) {
        const pending = localStorage.getItem("pendingReferrer");
        if (pending) {
          try {
            const { name: rName, email: rEmail, location: rLocation } = JSON.parse(pending);
            if (rEmail === data.user.email) {
              localStorage.removeItem("pendingReferrer");
              const { data: newRef } = await supabase.from("referrers").insert({
                auth_user_id: data.user.id, name: rName, email: rEmail,
                location: rLocation || "", reputation_score: 50, total_referred: 0, total_placed: 0,
              }).select().single();
              if (newRef) profile = newRef;
            }
          } catch(e) { console.error("Pending referrer recovery:", e); }
        }
      }

      if (!profile) {
        // Auto-create referrer profile — user is verified, just missing the row
        const { data: newRef, error: refErr } = await supabase.from("referrers").insert({
          auth_user_id: data.user.id,
          name: name.trim() || data.user.email.split("@")[0],
          email: data.user.email,
          location: referrerLocation.trim() || "",
          reputation_score: 50,
          total_referred: 0,
          total_placed: 0,
        }).select().single();
        if (newRef) {
          profile = newRef;
        } else {
          console.error("Auto-create referrer failed:", refErr);
          setError("Could not create profile. Please try again."); await supabase.auth.signOut(); setLoading(false); return;
        }
      }
      // Save portal preference BEFORE calling onLogin so session restore sees it
      localStorage.setItem("peerrefer_portal", "referrer");
      localStorage.setItem("peerrefer_user_email", data.user.email);
      onLogin({ role: "referrer", name: profile.name, email: profile.email, initials: (profile.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), reputationScore: profile.reputation_score || 50, totalReferred: profile.total_referred || 0, totalConsented: 0, totalContacted: 0, totalPlaced: profile.total_placed || 0, pendingBonus: 0, paidBonus: 0, id: profile.id });
    }
    setLoading(false);
  };

  const handleSignUp = async () => {
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (isTA && !company.trim()) { setError("Please enter your company name."); return; }

    // TA portal: soft nudge for personal emails — don't block, just warn
    if (isTA) {
      const personalDomains = ["gmail.com","googlemail.com","yahoo.com","yahoo.com.au","hotmail.com","hotmail.com.au","outlook.com","outlook.com.au","live.com","icloud.com","me.com","mac.com","aol.com","protonmail.com","proton.me","tutanota.com","zoho.com","mail.com","ymail.com","msn.com"];
      const emailDomain = email.trim().toLowerCase().split("@")[1];
      if (!emailDomain) { setError("Please enter a valid email address."); return; }
      if (personalDomains.includes(emailDomain)) {
        setNudge("💡 Many TAs use a work email to build trust with their referral network — but personal emails work too.");
      } else {
        setNudge("");
      }
    }

    setLoading(true);
    setError("");
    const { data, error: authError } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: "https://www.peerrefer.com/?verified=true" } });
    if (authError) { setError(authError.message); setLoading(false); return; }
    const userId = data.user?.id;
    if (!userId) { setError("Signup failed — please try again."); setLoading(false); return; }
    if (isTA) {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-" + Math.random().toString(36).slice(2,6);
      // Try to create profile immediately using service key
      try {
        const res = await fetch("/.netlify/functions/get-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, email: email.trim(), createTA: { name: name.trim(), email: email.trim(), company: company.trim(), slug } }),
        });
      } catch(e) {}
      // Always store pending as backup in case function fails
      localStorage.setItem("pendingTA", JSON.stringify({ name: name.trim(), email: email.trim(), company: company.trim(), slug }));
      setPendingVerification(true);
    } else {
      // Create referrer profile immediately using service key — don't wait for verification
      try {
        const res = await fetch("/.netlify/functions/get-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, email: email.trim(), createReferrer: { name: name.trim(), email: email.trim(), location: referrerLocation.trim() } }),
        });
      } catch(e) {}
      // Always store pending as backup
      localStorage.setItem("pendingReferrer", JSON.stringify({ name: name.trim(), email: email.trim(), location: referrerLocation.trim() }));
      setPendingVerification(true);
    }
    setLoading(false);
  };

  const handle = () => mode === "signin" ? handleSignIn() : handleSignUp();

  return (
    <div style={{ minHeight: "100vh", background: isTA ? "#FFFFFF" : "linear-gradient(135deg, #1A0D12 0%, #3A1520 50%, #6B2D3E 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36, justifyContent: "center" }}>
          <div style={{ width: 38, height: 38, background: isTA ? "#1A1612" : "rgba(0,0,0,0.12)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isTA ? "#FFFFFF" : "#fff"} strokeWidth="2.2" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 20, color: isTA ? "#1A1612" : "#fff", letterSpacing: "-0.02em" }}>PeerRefer</div>
            <div style={{ fontSize: 10, color: isTA ? "#9CA3AF" : "rgba(255,255,255,0.5)", fontFamily: "DM Mono, monospace" }}>by CollabPathways</div>
          </div>
        </div>
        <div style={{ background: isTA ? "#fff" : "rgba(0,0,0,0.08)", borderRadius: 20, padding: "36px 32px", border: isTA ? "1px solid rgba(26,22,18,0.08)" : "1px solid rgba(0,0,0,0.1)", boxShadow: isTA ? "0 8px 32px rgba(26,22,18,0.1)" : "0 8px 32px rgba(0,0,0,0.2)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: isTA ? "rgba(0,0,0,0.07)" : "rgba(0,0,0,0.1)", marginBottom: 20 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: isTA ? "#E8896A" : "#C4623E" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: isTA ? "#E8896A" : "#C4623E", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.06em" }}>{isTA ? "TA Professional Portal" : "Referrer Portal"}</span>
          </div>
          <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 26, color: isTA ? "#1A1612" : "#fff", letterSpacing: "-0.02em", marginBottom: 6, lineHeight: 1.1 }}>
            {isTA ? "Your talent inbox awaits." : "Track your referrals."}
          </h1>
          <p style={{ fontSize: 13, color: isTA ? "#9CA3AF" : "rgba(255,255,255,0.6)", marginBottom: 28, lineHeight: 1.6 }}>
            {isTA ? "Sign in to access your consented referral inbox and AI agents." : "Sign in to see the status of everyone you've referred and your reputation score."}
          </p>
          {isTA && mode === "signup" && (
            <div style={{ background: "rgba(91,110,175,0.08)", border: "1px solid rgba(91,110,175,0.15)", borderRadius: 9, padding: "10px 13px", marginBottom: 8, fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
              <strong style={{ color: "#1C1917" }}>For talent acquisition professionals only.</strong> Use your work email to build trust with your referral network. Personal emails are welcome too.
            </div>
          )}
          {/* Sign in / Sign up toggle */}
          <div style={{ display: "flex", background: isTA ? "#F8F5F2" : "rgba(0,0,0,0.1)", borderRadius: 10, padding: 4, marginBottom: 24 }}>
            {["signin", "signup"].map(m => (
              <button key={m} onClick={() => setMode(m)} style={{ flex: 1, background: mode === m ? (isTA ? "#FFFFFF" : "rgba(255,255,255,0.15)") : "transparent", border: "none", borderRadius: 8, padding: "8px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", color: isTA ? (mode === m ? "#1C1917" : "#9CA3AF") : (mode === m ? "#fff" : "rgba(255,255,255,0.5)"), transition: "all 0.15s" }}>
                {m === "signin" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {mode === "signup" && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: isTA ? "#374151" : "rgba(255,255,255,0.7)", display: "block", marginBottom: 5 }}>Your name *</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
                  style={{ width: "100%", background: isTA ? "#FFFFFF" : "rgba(0,0,0,0.15)", border: "1.5px solid " + (isTA ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"), borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, color: isTA ? "#1C1917" : "#fff", outline: "none" }} />
              </div>
            )}
            {mode === "signup" && isTA && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Company *</label>
                <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Your company name"
                  style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, color: "#1C1917", outline: "none" }} />
              </div>
            )}
            {mode === "signup" && !isTA && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.7)", display: "block", marginBottom: 5 }}>
                  Your location <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span>
                </label>
                <input value={referrerLocation} onChange={e => setReferrerLocation(e.target.value)}
                  placeholder="e.g. Melbourne, Sydney, Remote"
                  style={{ width: "100%", background: "rgba(0,0,0,0.15)", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, color: "#fff", outline: "none" }} />
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 4 }}>Helps surface relevant roles near you</div>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: isTA ? "#374151" : "rgba(255,255,255,0.7)", display: "block", marginBottom: 5 }}>
                Work Email *
                {isTA && mode === "signup" && <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF", marginLeft: 6 }}>— work email recommended</span>}
              </label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setNudge(""); }} onKeyDown={e => e.key === "Enter" && handle()} placeholder={isTA && mode === "signup" ? "you@yourcompany.com" : "your@email.com"}
                style={{ width: "100%", background: isTA ? "#FFFFFF" : "rgba(0,0,0,0.15)", border: "1.5px solid " + (isTA ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"), borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, color: isTA ? "#1C1917" : "#fff", outline: "none" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: isTA ? "#374151" : "rgba(255,255,255,0.7)", display: "block", marginBottom: 5 }}>Password *</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()} placeholder={mode === "signup" ? "Min 6 characters" : "Your password"}
                style={{ width: "100%", background: isTA ? "#FFFFFF" : "rgba(0,0,0,0.15)", border: "1.5px solid " + (isTA ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"), borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, color: isTA ? "#1C1917" : "#fff", outline: "none" }} />
            </div>
            {nudge && <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 9, padding: "10px 13px", fontSize: 12, color: "#92400E", marginBottom: 4 }}>{nudge}</div>}
            {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 9, padding: "10px 13px", fontSize: 13, color: isTA ? "#DC2626" : "#FCA5A5" }}>⚠ {error}</div>}
            <button onClick={handle} disabled={loading}
              style={{ background: isTA ? "#5B6EAF" : "rgba(255,255,255,0.95)", color: isTA ? "#FFFFFF" : "#2A3D80", border: "none", borderRadius: 10, padding: "13px", fontFamily: "inherit", fontWeight: 700, fontSize: 15, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.7 : 1, marginTop: 4 }}>
              {loading ? <><Spin color={isTA ? "#FFFFFF" : "#2A3D80"} />{mode === "signin" ? "Signing in..." : "Creating account..."}</> : (mode === "signin" ? "Sign In →" : "Create Account →")}
            </button>
          </div>

          <div style={{ marginTop: 16, textAlign: "center", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: isTA ? "#9CA3AF" : "rgba(255,255,255,0.4)", cursor: "pointer" }}
              onClick={onBack}>← Back</span>
            {isTA && <a href="/?pricing=true" style={{ fontSize: 12, color: "#5B6EAF", textDecoration: "none", fontWeight: 600 }}>See pricing →</a>}
            {mode === "signin" && (
              <span style={{ fontSize: 12, color: isTA ? "#5B6EAF" : "rgba(255,255,255,0.6)", cursor: "pointer", textDecoration: "underline" }}
                onClick={async () => {
                  if (!email.trim()) { setError("Enter your email address first."); return; }
                  setLoading(true);
                  const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                    redirectTo: "https://www.peerrefer.com/?reset=true",
                  });
                  setLoading(false);
                  if (resetError) { setError(resetError.message); }
                  else { setError(""); alert("Password reset email sent to " + email + ". Check your inbox."); }
                }}>
                Forgot password?
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================
// BRIEF MODAL — Create / Edit Job Brief
// ============================================
function BriefModal({ brief, onClose, onSave }) {
  const [form, setForm] = useState({
    title: brief.title || "",
    skillsInput: "",
    skills: brief.skills || [],
    seniority: brief.seniority || "",
    location: brief.location || "",
    bonus: brief.bonus || 2000,
    description: brief.description || "",
    positionDescription: brief.positionDescription || "",
    evp: brief.evp || "",
    logoUrl: brief.logoUrl || "",
    pdFilename: brief.pdFilename || "",
    pdUploading: false,
    status: brief.status || "active",
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Logo must be under 2MB."); return; }
    if (!file.type.startsWith("image/")) { alert("Please upload an image file."); return; }
    setLogoUploading(true);
    const fileName = "logo_" + Date.now() + "." + file.name.split(".").pop();
    const { data: uploadData, error } = await supabase.storage.from("logos").upload(fileName, file, {
      upsert: true,
      contentType: file.type,
      cacheControl: "3600",
    });
    if (!error) {
      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(fileName);
      set("logoUrl", urlData.publicUrl);
    } else {
      console.error("Logo upload error:", error);
      alert("Logo upload failed: " + error.message);
    }
    setLogoUploading(false);
  };

  const handlePDUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert("PD must be under 10MB."); return; }
    if (file.type !== "application/pdf") { alert("Please upload a PDF file."); return; }
    set("pdUploading", true);
    const fileName = "pd_" + Date.now() + ".pdf";
    const { error } = await supabase.storage.from("pds").upload(fileName, file, {
      upsert: true,
      contentType: "application/pdf",
    });
    if (!error) {
      set("pdFilename", fileName);
      // Extract text for position description field
      try {
        const extractRes = await fetch("/.netlify/functions/extract-cv", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cvFilename: fileName, bucket: "pds" }),
        });
        if (extractRes.ok) {
          const { text } = await extractRes.json();
          if (text && text.length > 50) set("positionDescription", text.slice(0, 3000));
        }
      } catch (err) { console.error("PD text extraction failed:", err); }
    } else {
      alert("PD upload failed: " + error.message);
    }
    set("pdUploading", false);
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));


  const addSkill = s => { const t = s.trim(); if (t && !form.skills.includes(t)) { set("skills", [...form.skills, t]); set("skillsInput", ""); } };

  const aiAssist = async () => {
    if (!form.title) return;
    setAiLoading(true);
    const result = await agentBriefWriter(form.title + " " + form.skills.join(", "));
    if (result) {
      set("skills", result.skills || form.skills);
      set("description", result.description || form.description);
      if (result.seniority && !form.seniority) set("seniority", result.seniority);
    }
    setAiLoading(false);
  };

  const save = () => {
    if (!form.title) return;
    onSave({ ...brief, ...form, id: brief.id === "new" ? "b" + Date.now() : brief.id });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div style={{ background: "#FAFAF8", borderRadius: 20, padding: "30px 28px", width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(26,18,24,0.25)", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 22, color: "#1C1917" }}>{brief.id === "new" ? "New Job Brief" : "Edit Brief"}</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>🤖 AI can auto-fill based on your role title</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9CA3AF" }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Role title *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Senior Product Manager"
                style={{ flex: 1, background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
              <button onClick={aiAssist} disabled={aiLoading || !form.title}
                style={{ background: aiLoading ? "#9CA3AF" : "#E8896A", color: "#fff", border: "none", borderRadius: 9, padding: "0 14px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: aiLoading ? "default" : "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                {aiLoading ? <><Spin color="#fff" /> AI...</> : "🤖 AI Fill"}
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Key skills</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input value={form.skillsInput} onChange={e => set("skillsInput", e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(form.skillsInput); }}}
                placeholder="Add skill and press Enter..."
                style={{ flex: 1, background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
              <button onClick={() => addSkill(form.skillsInput)} style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 9, padding: "0 13px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Add</button>
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {form.skills.map(s => <span key={s} onClick={() => set("skills", form.skills.filter(x => x !== s))} style={{ background: "rgba(232,137,106,0.12)", color: "#C4623E", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontFamily: "DM Mono, monospace", cursor: "pointer" }}>{s} ×</span>)}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Seniority</label>
              <select value={form.seniority} onChange={e => set("seniority", e.target.value)}
                style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", appearance: "none" }}>
                <option value="">Any level</option>
                <option>Junior (0-3 years)</option>
                <option>Mid-level (3-6 years)</option>
                <option>Senior (6+ years)</option>
                <option>Lead / Manager</option>
                <option>Executive / C-Suite</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Location</label>
              <input value={form.location} onChange={e => set("location", e.target.value)} placeholder="e.g. Melbourne / Remote"
                style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Bonus for this role (AUD)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 20, color: "#E8896A" }}>$</span>
              <input type="number" value={form.bonus} onChange={e => set("bonus", Number(e.target.value))} placeholder="2000"
                style={{ width: 120, background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "10px 13px", fontFamily: "Playfair Display, Georgia, serif", fontSize: 18, fontWeight: 700, outline: "none" }} />
              <span style={{ fontSize: 13, color: "#9CA3AF" }}>per placement</span>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 5 }}>Brief description for referrers</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="2-3 sentences describing the role for referrers. Keep it concise and referrer-friendly."
              style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", minHeight: 80, resize: "vertical" }} />
          </div>

          {/* Position Description */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 8 }}>
              Position Description
              <span style={{ fontWeight: 400, color: "#9CA3AF", marginLeft: 6 }}>— shown to referrers</span>
            </label>
            {/* PD Upload dropzone */}
            <label style={{ display: "block", border: "2px dashed rgba(91,110,175,0.25)", borderRadius: 10, padding: "14px 16px", textAlign: "center", cursor: "pointer", background: form.pdFilename ? "rgba(91,110,175,0.04)" : "#FAFAF8", marginBottom: 8 }}>
              <input type="file" accept=".pdf" onChange={handlePDUpload} style={{ display: "none" }} />
              {form.pdUploading ? (
                <div style={{ fontSize: 13, color: "#5B6EAF" }}>Uploading PD...</div>
              ) : form.pdFilename ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B6EAF" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span style={{ fontSize: 12, color: "#5B6EAF", fontWeight: 600 }}>PD uploaded — text extracted below</span>
                  <button onClick={e => { e.preventDefault(); set("pdFilename", ""); set("positionDescription", ""); }} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 13 }}>×</button>
                </div>
              ) : (
                <div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 4 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Drop PD here or click to upload</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>PDF only · Text auto-extracted into field below</div>
                </div>
              )}
            </label>
            <textarea value={form.positionDescription} onChange={e => set("positionDescription", e.target.value)}
              placeholder="Or type position description here — responsibilities, what success looks like, day-to-day..."
              style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 13, outline: "none", minHeight: 100, resize: "vertical", color: "#1C1917" }} />
          </div>

          {/* EVP */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 8 }}>
              Why join us? (EVP)
              <span style={{ fontWeight: 400, color: "#9CA3AF", marginLeft: 6 }}>— your employer value proposition</span>
            </label>
            <textarea value={form.evp} onChange={e => set("evp", e.target.value)}
              placeholder="What makes your company a great place to work? Culture, benefits, mission, growth opportunities..."
              style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "10px 13px", fontFamily: "inherit", fontSize: 13, outline: "none", minHeight: 80, resize: "vertical", color: "#1C1917" }} />
          </div>

          {/* Company Logo */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 8 }}>Company Logo</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {form.logoUrl ? (
                <div style={{ position: "relative" }}>
                  <img src={form.logoUrl} alt="logo" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "contain", border: "1px solid rgba(0,0,0,0.08)", background: "#fff", padding: 4 }} />
                  <button onClick={() => set("logoUrl", "")} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#EF4444", color: "#fff", border: "none", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ) : (
                <div style={{ width: 56, height: 56, borderRadius: 10, background: "#F0F2FA", border: "1.5px dashed rgba(91,110,175,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B6EAF" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </div>
              )}
              <label style={{ cursor: "pointer" }}>
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                <div style={{ background: "#F0F2FA", color: "#5B6EAF", border: "1px solid rgba(91,110,175,0.2)", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {logoUploading ? "Uploading..." : form.logoUrl ? "Change Logo" : "Upload Logo"}
                </div>
              </label>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>PNG, JPG · Max 2MB</div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 8 }}>Status</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["active", "paused"].map(s => (
                <button key={s} onClick={() => set("status", s)}
                  style={{ padding: "7px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", border: "1.5px solid", borderColor: form.status === s ? "#1A1612" : "rgba(0,0,0,0.1)", background: form.status === s ? "#1A1612" : "transparent", color: form.status === s ? "#FFFFFF" : "#9CA3AF" }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 9, marginTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, background: "transparent", border: "1.5px solid rgba(26,22,18,0.18)", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#9CA3AF" }}>Cancel</button>
            <button onClick={save} style={{ flex: 2, background: "#E8896A", color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              {brief.id === "new" ? "Create Brief →" : "Save Changes →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ============================================
// UPGRADE MODAL — Pro tier paywall
// ============================================
function UpgradeModal({ onClose, onUpgrade, user, taProfile }) {
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const features = [
    { icon: "👤", label: "Full candidate names revealed", free: false },
    { icon: "✉️", label: "Email and LinkedIn visible", free: false },
    { icon: "🤖", label: "All 9 AI agents unlocked", free: false },
    { icon: "✍️", label: "AI outreach drafter", free: false },
    { icon: "📬", label: "Unlimited talent pool access", free: true },
    { icon: "💼", label: "Unlimited job briefs", free: true },
    { icon: "🔗", label: "Shareable referral links", free: true },
    { icon: "👁", label: "Blurred candidate previews", free: true },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 20 }}>
      <div style={{ background: "#FFFFFF", borderRadius: 24, width: "100%", maxWidth: 520, boxShadow: "0 24px 80px rgba(0,0,0,0.14)", fontFamily: "Inter, DM Sans, system-ui, sans-serif", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #3D5296 0%, #5B6EAF 100%)", padding: "32px 32px 28px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", fontFamily: "DM Mono, monospace", letterSpacing: "0.1em", marginBottom: 8 }}>PEERREFER PRO</div>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 26, color: "#FFFFFF", letterSpacing: "-0.02em", marginBottom: 6 }}>Unlock the full talent pool</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>See who's waiting in your inbox. Reach out directly. Place faster.</div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ marginTop: 20, display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontFamily: "Playfair Display, serif", fontWeight: 800, fontSize: 42, color: "#FFFFFF" }}>$49</span>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>/ month AUD</span>
            
          </div>
        </div>

        {/* Features */}
        <div style={{ padding: "24px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: f.free ? "#FAFAF8" : "rgba(0,0,0,0.04)", borderRadius: 8, border: f.free ? "none" : "1px solid rgba(0,0,0,0.07)" }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{f.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1917" }}>{f.label}</div>
                  <div style={{ fontSize: 10, color: f.free ? "#9CA3AF" : "#5B6EAF", fontWeight: 600, fontFamily: "DM Mono, monospace" }}>{f.free ? "FREE" : "PRO"}</div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={async () => {
            setUpgradeLoading(true);
            try {
              const res = await fetch("/.netlify/functions/create-checkout", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: user?.email, taProfileId: taProfile?.id, trial: true }),
              });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
              else {
                // Fallback to direct Stripe payment link
                window.location.href = `https://buy.stripe.com/fZuaEW1ZLc6N4YK2k97ss06?prefilled_email=${encodeURIComponent(user?.email || "")}`;
              }
            } catch (err) {
              // Fallback to direct Stripe payment link
              window.location.href = `https://buy.stripe.com/fZuaEW1ZLc6N4YK2k97ss06?prefilled_email=${encodeURIComponent(user?.email || "")}`;
            }
            setUpgradeLoading(false);
          }} disabled={upgradeLoading}
            style={{ width: "100%", background: upgradeLoading ? "#9CA3AF" : "linear-gradient(135deg, #5B6EAF 0%, #7082C0 100%)", color: "#FFFFFF", border: "none", borderRadius: 12, padding: "15px", fontFamily: "inherit", fontWeight: 800, fontSize: 16, cursor: upgradeLoading ? "default" : "pointer", marginBottom: 10, letterSpacing: "-0.01em" }}>
            {upgradeLoading ? "Starting checkout..." : "Start Free 30-Day Trial →"}
          </button>
          <button onClick={onClose}
            style={{ width: "100%", background: "transparent", border: "none", color: "#9CA3AF", fontFamily: "inherit", fontSize: 13, cursor: "pointer", padding: "8px" }}>
            Continue on free plan
          </button>


          <div style={{ marginTop: 16, fontSize: 11, color: "#9CA3AF", textAlign: "center", lineHeight: 1.6 }}>
            30 days free · Then $49/month · Cancel anytime · Stripe-secured
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SHARE MODAL — Share a brief or referral link
// ============================================
function ShareModal({ brief, taProfile, onClose }) {
  const [copied, setCopied] = useState(false);
  const baseUrl = window.location.origin;
  const slug = taProfile?.slug || "mia-thorne";
  const roleSlug = brief ? brief.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "";
  // Social share URL — uses brief-meta function which serves pre-rendered OG tags for LinkedIn crawler
  const socialShareUrl = brief
    ? `${baseUrl}/.netlify/functions/brief-meta?briefId=${brief.id}&taSlug=${slug}`
    : `${baseUrl}?refer=${slug}`;
  // Direct URL — uses briefId for public job page
  const shareUrl = brief
    ? `${baseUrl}?briefId=${brief.id}&refer=${slug}`
    : `${baseUrl}?refer=${slug}`;

  const taCaption = brief
    ? `I'm looking for a ${brief.title} in ${brief.location || "Melbourne"}. Know someone great?\n\nRefer them through PeerRefer and earn $${(brief.bonus || 0).toLocaleString()} if they get placed.\n\n👉 ${shareUrl}`
    : `I'm actively hiring through PeerRefer — a consent-first talent referral platform.\n\nKnow great people? Refer them and earn a bonus if they're placed.\n👉 ${shareUrl}`;

  const copy = () => {
    navigator.clipboard?.writeText(shareUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const platforms = [
    {
      name: "LinkedIn",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
      color: "#0A66C2",
      bg: "#E8F0FA",
      action: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(socialShareUrl)}`, "_blank"),
    },
    {
      name: "Facebook",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
      color: "#1877F2",
      bg: "#E8F0FE",
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialShareUrl)}`, "_blank"),
    },
    {
      name: "Twitter / X",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.631L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>,
      color: "#000000",
      bg: "#F0F0F0",
      action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(taCaption)}`, "_blank"),
    },
    {
      name: "Email",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E8896A" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
      color: "#E8896A",
      bg: "#FDF0EA",
      action: () => window.open(`mailto:?subject=${encodeURIComponent(brief ? `Refer someone for ${brief.title}` : "Know someone great? Refer them and earn")}&body=${encodeURIComponent(taCaption)}`, "_blank"),
    },
    {
      name: "WhatsApp",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
      color: "#25D366",
      bg: "#E8F8EE",
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(taCaption)}`, "_blank"),
    },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}>
      <div className="modal-enter" style={{ background: "#FFFFFF", borderRadius: 20, padding: "32px 28px", width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.12)", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#1C1917", marginBottom: 4 }}>
              {brief ? `Share: ${brief.title}` : "Share Your Referral Link"}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>
              {brief ? `Earn $${(brief.bonus || 0).toLocaleString()} per placement · ${brief.location || "Melbourne"}` : "Let your network refer great people"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9CA3AF", lineHeight: 1 }}>✕</button>
        </div>

        {/* Share link */}
        <div style={{ background: "#FAFAF8", borderRadius: 10, padding: "12px 14px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, fontSize: 12, color: "#374151", fontFamily: "DM Mono, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shareUrl}</div>
          <button onClick={copy} style={{ background: copied ? "#5B6EAF" : "#FFFFFF", color: copied ? "#FFFFFF" : "#5B6EAF", border: "1.5px solid rgba(0,0,0,0.12)", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, transition: "all 0.2s" }}>
            {copied ? "✓ Copied" : "Copy"}
          </button>
        </div>

        {/* Platform buttons */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
          {platforms.map(p => (
            <button key={p.name} onClick={p.action}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", background: p.bg || "#FAFAF8", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "#1C1917", transition: "all 0.15s", textAlign: "left" }}>
              <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{p.icon}</span>
              {p.name}
            </button>
          ))}
        </div>

        {/* Message preview */}
        <div style={{ background: "#FAFAF8", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 6 }}>MESSAGE PREVIEW</div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-line" }}>{taCaption}</div>
        </div>

        <div style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>
          Anyone who clicks this link can refer someone — no account needed.
        </div>
      </div>
    </div>
  );
}

// ============================================
// REFERRER SHARE MODAL — Share a role to find candidates
// ============================================
function ReferrerShareModal({ brief, onClose }) {
  const [copied, setCopied] = useState(false);
  const baseUrl = window.location.origin;
  const roleSlug = brief ? brief.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "";
  const shareUrl = `${baseUrl}?refer=mia-thorne&role=${roleSlug}`;

  const message = `Hey — thought of you for this.

A recruiter I know is looking for a ${brief?.title || "great candidate"} in ${brief?.location || "Melbourne"} and I can refer you directly${brief?.bonus > 0 ? ` — I earn $${brief.bonus.toLocaleString()} if you get placed` : ""}.

No spam, fully privacy-first. You consent before anyone sees your details.

Interested? Here's the link: ${shareUrl}`;

  const copy = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}>
      <div className="modal-enter" style={{ background: "#FFFFFF", borderRadius: 20, padding: "32px 28px", width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.12)", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#1C1917", marginBottom: 4 }}>Share this role</div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>Send to someone you think would be a great fit</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9CA3AF" }}>✕</button>
        </div>

        <div style={{ background: "#FAFAF8", borderRadius: 12, padding: "16px", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 8 }}>READY-TO-SEND MESSAGE</div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-line", marginBottom: 12 }}>{message}</div>
          <button onClick={() => copy(message)} style={{ background: copied ? "#5B6EAF" : "#FFFFFF", color: copied ? "#FFFFFF" : "#5B6EAF", border: "1.5px solid rgba(0,0,0,0.12)", borderRadius: 7, padding: "6px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
            {copied ? "✓ Copied" : "📋 Copy message"}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          {[
            { name: "WhatsApp", icon: "💬", action: () => window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank") },
            { name: "Email", icon: "✉️", action: () => window.open(`mailto:?subject=${encodeURIComponent("Thought of you for this role")}&body=${encodeURIComponent(message)}`, "_blank") },
            { name: "LinkedIn", icon: "💼", action: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank") },
          ].map(p => (
            <button key={p.name} onClick={p.action}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "12px 8px", background: "#FAFAF8", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "#1C1917" }}>
              <span style={{ fontSize: 22 }}>{p.icon}</span>
              {p.name}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>
          🔒 The person you share with controls their own consent — their details stay private until they agree.
        </div>
      </div>
    </div>
  );
}

// ============================================
// TA DASHBOARD
// ============================================
// ============================================
// WARM INTRO BUTTON
function WarmIntroButton({ referral, taUser, onRequested }) {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!referral.referrerEmail) return null;

  const send = async () => {
    setSending(true);
    await fetch("/.netlify/functions/warm-intro", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referralId: referral.id,
        taName: taUser.name,
        taEmail: taUser.email,
        taCompany: taUser.company,
        referrerEmail: referral.referrerEmail,
        referrerName: referral.referrerName,
        candidateName: referral.candidateName,
        message: message.trim(),
      }),
    });
    setSending(false);
    setShow(false);
    onRequested();
  };

  if (show) return (
    <div style={{ background: "rgba(91,110,175,0.06)", border: "1px solid rgba(91,110,175,0.15)", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#5B6EAF", marginBottom: 8, fontFamily: "DM Mono, monospace" }}>REQUEST INTRO CALL</div>
      <textarea value={message} onChange={e => setMessage(e.target.value)}
        placeholder={`Optional: Add a personal note to ${referral.referrerName} about why you'd like to chat...`}
        rows={2} style={{ width: "100%", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 7, padding: "8px 10px", fontFamily: "inherit", fontSize: 12, lineHeight: 1.5, outline: "none", resize: "none", marginBottom: 8, boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={send} disabled={sending}
          style={{ flex: 1, background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "8px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: sending ? "default" : "pointer" }}>
          {sending ? "Sending..." : "Send Request →"}
        </button>
        <button onClick={() => setShow(false)}
          style={{ background: "none", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 7, padding: "8px 12px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", color: "#9CA3AF" }}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <button onClick={() => setShow(true)}
      style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(91,110,175,0.06)", border: "1px solid rgba(91,110,175,0.15)", borderRadius: 8, padding: "7px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#5B6EAF" }}>
      🤝 Request Intro Call with {referral.referrerName}
    </button>
  );
}

// ============================================
// INVITE REFERRERS PANEL
function InviteReferrersPanel({ taProfile, user }) {
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    const emailList = emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes("@"));
    if (emailList.length === 0) { alert("Please enter at least one valid email address."); return; }
    setSending(true);
    setResult(null);
    try {
      const res = await fetch("/.netlify/functions/invite-referrers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taName: user.name,
          taCompany: user.company,
          taSlug: taProfile?.slug || user.slug,
          emails: emailList,
          personalNote: note.trim(),
        }),
      });
      const data = await res.json();
      setResult(data);
      if (data.sent > 0) setEmails("");
    } catch (e) { setResult({ error: "Failed to send. Please try again." }); }
    setSending(false);
  };

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", padding: "24px 28px", marginBottom: 16 }}>
      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Invite Your Network</div>
      <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16, lineHeight: 1.6 }}>
        Invite people from your existing network to refer candidates for your roles. They'll get a branded email explaining how PeerRefer works.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Email addresses <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(one per line, or comma separated — max 50)</span></label>
          <textarea
            value={emails}
            onChange={e => setEmails(e.target.value)}
            placeholder={"john@company.com\njane@startup.io\nmark@agency.com"}
            rows={4}
            style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, lineHeight: 1.6, outline: "none", resize: "vertical", boxSizing: "border-box" }}
          />
          {emails && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
            {emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes("@")).length} valid email{emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes("@")).length !== 1 ? "s" : ""} detected
          </div>}
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Personal note <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional — shown in the email)</span></label>
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. I'm actively hiring and would love your help finding great people!"
            style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <button onClick={send} disabled={sending || !emails.trim()}
          style={{ background: sending ? "#9CA3AF" : "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "11px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: sending || !emails.trim() ? "default" : "pointer", alignSelf: "flex-start" }}>
          {sending ? "Sending..." : "📨 Send Invites →"}
        </button>
        {result && (
          <div style={{ padding: "10px 14px", borderRadius: 9, background: result.error ? "rgba(239,68,68,0.06)" : "rgba(72,187,120,0.08)", border: `1px solid ${result.error ? "rgba(239,68,68,0.15)" : "rgba(72,187,120,0.2)"}`, fontSize: 13, color: result.error ? "#EF4444" : "#059669", fontWeight: 600 }}>
            {result.error || `✓ ${result.sent} invite${result.sent !== 1 ? "s" : ""} sent${result.failed > 0 ? ` · ${result.failed} failed` : ""}`}
          </div>
        )}
        <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.6 }}>
          Invitees will see your name, company, and a link to your PeerRefer profile. They can refer candidates for any of your active roles.
        </div>
      </div>
    </div>
  );
}

// ============================================
function TADashboard({ user, referrals, setReferrals, poolLoading, taProfile, onLogout, sharedBriefs, setSharedBriefs, isProTA, setIsProTA, setTaPrefs, hasBothPortals }) {
  const isMobile = useIsMobile();
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("inbox");
  const [load, setLoad] = useState({});
  const [intel, setIntel] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [intelLoad, setIntelLoad] = useState(false);
  const [bonusEnabled, setBonusEnabled] = useState(user.bonusEnabled || false);
  const [bonusAmount, setBonusAmount] = useState(user.bonusAmount || 2000);
  const [bonusTrigger, setBonusTrigger] = useState(user.bonusTrigger || "placement");
  const [lookingFor, setLookingFor] = useState("Product Managers, Data Analysts, TA professionals");
  const [industries, setIndustries] = useState("SaaS, Tech, Fintech");
  const [seniority, setSeniority] = useState("Mid-level (3-6 years)");
  const [location, setLocation] = useState("Melbourne / Remote");
  const [referrerNote, setReferrerNote] = useState("I'm always open to strong talent in the Melbourne tech space. Culture fit matters as much as skills.");
  const [searchQuery, setSearchQuery] = useState("");
  const briefs = sharedBriefs;
  const setBriefs = setSharedBriefs;
  const [activeBriefFilter, setActiveBriefFilter] = useState(null);
  const [showBriefModal, setShowBriefModal] = useState(false);
  const [editingBrief, setEditingBrief] = useState(null);
  const [matchLoading, setMatchLoading] = useState({});
  const [placementMessage, setPlacementMessage] = useState(null);
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [intelResults, setIntelResults] = useState({});
  const [intelLoading, setIntelLoading] = useState({});
  const [sharingBrief, setSharingBrief] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [milestone, setMilestone] = useState(null); // { type, title, body, cta }

  const setL = (id, k, v) => setLoad(l => ({ ...l, [id + "_" + k]: v }));
  const isL = (id, k) => load[id + "_" + k];
  const selRef = referrals.find(r => r.id === sel);

  // Notify referrer when TA views their candidate (fires after 3s of viewing)
  const _viewedRefs = { current: new Set() };
  useEffect(() => {
    if (!sel || !selRef?.referrerEmail || !selRef?.consented) return;
    if (_viewedRefs.current.has(sel)) return;
    _viewedRefs.current.add(sel);
    const timer = setTimeout(async () => {
      try {
        await fetch("/.netlify/functions/notify-referrer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "candidate_viewed",
            referrerEmail: selRef.referrerEmail,
            referrerName: selRef.referrerName,
            candidateName: selRef.candidateName,
            taName: user?.name || taProfile?.name || "The recruiter",
            taCompany: user?.company || taProfile?.company || "",
            briefTitle: selRef.briefTitle,
          }),
        });
      } catch(e) { console.error("View notification:", e); }
    }, 3000);
    return () => clearTimeout(timer);
  }, [sel]); // eslint-disable-line
  const [selCvFilename, setSelCvFilename] = useState(null);
  useEffect(() => {
    setSelCvFilename(null);
    if (!sel) return;
    supabase.from("referrals").select("cv_filename").eq("id", sel).single()
      .then(({ data, error }) => { 
        if (error) console.error("CV fetch error:", error);
        if (data?.cv_filename) setSelCvFilename(data.cv_filename); 
      });
  }, [sel]); // eslint-disable-line
  const baseFiltered = filter === "all" ? referrals : referrals.filter(r => r.status === filter);
  const filtered = searchQuery.trim() === "" ? baseFiltered : baseFiltered.filter(r => {
    const q = searchQuery.toLowerCase();
    return (
      (r.candidateName || "").toLowerCase().includes(q) ||
      (r.skills || []).some(s => s.toLowerCase().includes(q)) ||
      (r.referrerName || "").toLowerCase().includes(q) ||
      (r.note || "").toLowerCase().includes(q)
    );
  });
  const stats = { total: referrals.length, new: referrals.filter(r => r.status === "new").length, contacted: referrals.filter(r => r.status === "contacted").length, avgScore: referrals.length ? (referrals.reduce((a, r) => a + parseFloat(avg(r)), 0) / referrals.length).toFixed(1) : "—" };

  // Sync bonus settings from loaded taProfile
  useEffect(() => {
    if (taProfile) {
      setBonusEnabled(taProfile.bonus_enabled || false);
      setBonusAmount(taProfile.bonus_amount || 2000);
      setBonusTrigger(taProfile.bonus_trigger || "placement");
    }
  }, [taProfile]); // eslint-disable-line

  useEffect(() => {
    if (selRef && !selRef.aiAnalysis) {
      setL(selRef.id, "a", true);
      agentAnalyse(selRef).then(async res => {
        setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, aiAnalysis: res } : r));
        if (res) await supabase.from("referrals").update({ ai_analysis: res }).eq("id", selRef.id);
        setL(selRef.id, "a", false);
      });
    }
  }, [sel]); // eslint-disable-line

  const draftEmail = async r => {
    setL(r.id, "d", true);
    const res = await agentDraft(r);
    setReferrals(prev => prev.map(x => x.id === r.id ? { ...x, draftEmail: res } : x));
    setL(r.id, "d", false);
  };

  const runIntel = async () => {
    if (!referrals || referrals.length === 0) {
      setIntel({ topPriority: null, ranked: [], summary: "No candidates in your pool yet. Add referrals to get match results." });
      setNudge({ message: "Your pipeline is empty. Share your referral link to start receiving warm candidates.", urgentCount: 0 });
      return;
    }
    setIntelLoad(true);
    try {
      const [m, n] = await Promise.all([agentMatch(referrals), agentNudge(referrals)]);
      setIntel(m); setNudge(n);
    } catch(e) {
      console.error("Intel error:", e);
    }
    setIntelLoad(false);
  };

  const addReferral = data => setReferrals(prev => [{ ...data, id: "r" + Date.now(), submittedAt: "Just now", status: "new", aiAnalysis: null, draftEmail: null }, ...prev]);
  const updateStatus = async (id, status) => {
    setReferrals(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    await supabase.from("referrals").update({ status }).eq("id", id);
    // Agent 10 — fire Referrer Recruiter when placement confirmed
    if (status === "placed") {
      const placedRef = referrals.find(r => r.id === id);
      if (placedRef) {
        const referrerData = { name: placedRef.referrerName, total_placed: placedRef.referrerStats?.placed + 1, total_referred: placedRef.referrerStats?.total };
        agentReferrerRecruiter(placedRef, referrerData, briefs).then(msg => {
          if (msg) {
            setPlacementMessage(msg);
            setShowPlacementModal(true);
          }
        });
      }
    }
  };

  // Send pipeline notification to referrer
  const notifyReferrerPipeline = async (referral, status) => {
    if (!["shortlisted", "hired"].includes(status)) return;
    const referrerEmail = referral.referrerEmail || referral.referrers?.email || "";
    if (!referrerEmail) { console.log("No referrer email for notification:", referral.referrerName); return; }
    try {
      await fetch("/.netlify/functions/notify-referrer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: status === "hired" ? "placed" : "shortlisted",
          referrerEmail: referrerEmail,
          referrerName: referral.referrerName,
          candidateName: referral.candidateName,
          taName: user?.name || taProfile?.name || "The recruiter",
          taCompany: user?.company || taProfile?.company || "",
          briefTitle: referral.briefTitle,
        }),
      });
    } catch(e) { console.error("Pipeline notification error:", e); }
  };

  const deleteReferral = async id => {
    const ref = referrals.find(r => r.id === id);
    if (!ref) return;
    if (window.confirm(`Remove ${ref.candidateName || "this candidate"} from your pool? A notification will be sent to both the candidate and referrer.`)) {
      setReferrals(prev => prev.filter(r => r.id !== id));
      setSel(null);
      // Send notifications before deleting
      try {
        await fetch("/.netlify/functions/notify-referrer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "removed_from_pool",
            referrerEmail: ref.referrerEmail,
            referrerName: ref.referrerName,
            candidateName: ref.candidateName,
            taName: user.name || taProfile?.name || "The recruiter",
            taCompany: user.company || taProfile?.company || "",
            briefTitle: ref.briefTitle,
          }),
        });
        // Notify candidate
        if (ref.candidateEmail) {
          await fetch("/.netlify/functions/send-consent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "removed_from_pool",
              candidateEmail: ref.candidateEmail,
              candidateName: ref.candidateName,
              referrerName: ref.referrerName,
              taName: user.name || taProfile?.name || "The recruiter",
              taCompany: user.company || taProfile?.company || "",
              briefTitle: ref.briefTitle,
              referralId: ref.id,
            }),
          });
        }
      } catch(e) { console.error("Notification error:", e); }
      await supabase.from("referrals").delete().eq("id", id);
    }
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#FAFAF8", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600&family=Inter:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.99); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes slideUpSheet { from { transform:translateY(100%); } to { transform:translateY(0); } }
        @keyframes slideInRight { from { transform:translateX(100%); opacity:0; } to { transform:translateX(0); opacity:1; } }
        .card-enter { animation: fadeUp 0.3s ease forwards; }
        .modal-enter { animation: slideUp 0.3s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        .sheet-enter { animation: slideUpSheet 0.35s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        .detail-enter { animation: slideInRight 0.25s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      <nav style={{ background: "rgba(250,250,248,0.97)", borderBottom: "1px solid rgba(0,0,0,0.08)", padding: isMobile ? "0 14px" : "0 28px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, background: "#1C1917", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em" }}>PeerRefer</div>
              {!isMobile && <div style={{ fontSize: 9, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>TA Portal · CollabPathways</div>}
            </div>
          </div>
          {!isMobile && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: isProTA ? "rgba(0,0,0,0.07)" : "rgba(232,137,106,0.1)", borderRadius: 20, border: isProTA ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(232,137,106,0.18)" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: isProTA ? "#5B6EAF" : "#E8896A" }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: isProTA ? "#5B6EAF" : "#C4623E", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.06em" }}>{isProTA ? "✓ PRO" : "FREE PLAN"}</span>
              </div>
              {!isProTA && (
                <button onClick={() => setShowUpgrade(true)} style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "4px 10px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Upgrade →</button>
              )}
              <div style={{ display: "flex", gap: 2, background: "rgba(0,0,0,0.04)", borderRadius: 9, padding: "2px" }}>
                {[["inbox", "📬 Inbox"], ["jobs", `💼 Jobs (${sharedBriefs ? sharedBriefs.filter(b => b.status === "active").length : 0})`], ["intelligence", "🧠 Intel"], ["analytics", "📊 Analytics"], ["settings", "⚙️ Settings"]].map(([k, l]) => (
                  <button key={k} onClick={() => { setTab(k); if (k === "intelligence" && !intel) runIntel(); }} style={{ background: tab === k ? "#fff" : "transparent", border: "none", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: tab === k ? "#1A1612" : "#9CA3AF", cursor: "pointer", boxShadow: tab === k ? "0 1px 3px rgba(26,22,18,0.1)" : "none" }}>{l}</button>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
          {!isMobile && <button onClick={() => setSharingBrief("general")} style={{ background: "rgba(232,137,106,0.1)", border: "1px solid rgba(232,137,106,0.18)", borderRadius: 7, padding: "5px 12px", fontSize: 11, color: "#C4623E", fontFamily: "inherit", fontWeight: 700, cursor: "pointer" }}>↗ Share My Link</button>}
          {isMobile && !isProTA && <button onClick={() => setShowUpgrade(true)} style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Upgrade</button>}
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {!isMobile && <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{user.name}</div>
              <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{user.company}</div>
            </div>}
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 12 }}>{user.initials}</div>
          </div>
          {hasBothPortals && <button onClick={async () => {
              localStorage.setItem("peerrefer_portal", "referrer");
              localStorage.setItem("peerrefer_user_email", user?.email || "");
              try { await supabase.auth.updateUser({ data: { preferred_portal: "referrer" } }); } catch(e) {}
              window.location.replace("/?portal=referrer");
            }} style={{ background: "rgba(91,110,175,0.08)", border: "1px solid rgba(91,110,175,0.2)", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#5B6EAF", marginRight: 6 }}>Referrer Portal</button>}
          <button onClick={onLogout} style={{ background: "transparent", border: "1px solid rgba(26,22,18,0.15)", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#9CA3AF" }}>Out</button>
        </div>
      </nav>
      {/* Mobile bottom tab bar */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(250,250,248,0.97)", borderTop: "1px solid rgba(0,0,0,0.08)", display: "flex", zIndex: 100, paddingBottom: "env(safe-area-inset-bottom)" }}>
          {[["inbox", "📬", "Inbox"], ["jobs", "💼", `Jobs`], ["intelligence", "🧠", "Intel"], ["analytics", "📊", "Analytics"], ["settings", "⚙️", "Settings"]].map(([k, emoji, label]) => (
            <button key={k} onClick={() => { setTab(k); setSel(null); if (k === "intelligence" && !intel) runIntel(); }}
              style={{ flex: 1, background: "none", border: "none", padding: "10px 4px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <span style={{ fontSize: 20 }}>{emoji}</span>
              <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", color: tab === k ? "#E8896A" : "#9CA3AF" }}>{label}</span>
              {tab === k && <div style={{ width: 16, height: 2, background: "#E8896A", borderRadius: 1 }} />}
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {tab === "inbox" && (
          <div style={{ padding: "24px 28px" }}>
            {poolLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: "#9CA3AF", fontSize: 14 }}>
                <Spin /> Loading talent pool from database...
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em", marginBottom: 3 }}>Talent Pool</h1>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>Every candidate has consented. <span style={{ color: "#E8896A", fontWeight: 600 }}>🔒 Pro candidates require upgrade to contact.</span></p>

            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
              {[{ l: "Total", v: stats.total, i: "📬" }, { l: "New", v: stats.new, i: "✨" }, { l: "Contacted", v: stats.contacted, i: "📞" }, { l: "Avg Score", v: stats.avgScore + "/5", i: "⭐" }].map((s, i) => (
                <div key={i} style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 11, padding: "14px 16px" }}>
                  <div style={{ fontSize: 18, marginBottom: 5 }}>{s.i}</div>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, color: "#E8896A", marginBottom: 1 }}>{s.v}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Search bar */}
            <div style={{ position: "relative", marginBottom: 14 }}>
              <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none" }}>🔍</div>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, skill, or referrer..."
                style={{ width: "100%", background: "#FFFFFF", border: "1.5px solid rgba(26,22,18,0.1)", borderRadius: 10, padding: "10px 14px 10px 38px", fontFamily: "inherit", fontSize: 14, outline: "none", boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 16 }}>✕</button>
              )}
            </div>

            <div style={{ display: "flex", gap: 5, marginBottom: 16 }}>
              {["all", "new", "contacted", "saved"].map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{ background: filter === f ? "#1A1612" : "transparent", color: filter === f ? "#FFFFFF" : "#9CA3AF", border: "1.5px solid", borderColor: filter === f ? "#E8896A" : "rgba(0,0,0,0.08)", borderRadius: 7, padding: "4px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              {searchQuery && (
                <div style={{ marginLeft: "auto", fontSize: 12, color: "#9CA3AF", display: "flex", alignItems: "center" }}>
                  {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "{searchQuery}"
                </div>
              )}
            </div>

            <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: selRef ? "1fr 400px" : "1fr", gap: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {!poolLoading && filtered.length === 0 && (
                  <div style={{ background: "#FFFFFF", borderRadius: 12, padding: "40px 24px", textAlign: "center", border: "1px solid rgba(0,0,0,0.06)" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                    <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No referrals yet</div>
                    <div style={{ fontSize: 13, color: "#9CA3AF" }}>Referrals will appear here once candidates have consented.</div>
                  </div>
                )}
                {(() => {
                  // Group referrals by candidate email - one card per candidate
                  const groups = [];
                  const seen = new Set();
                  filtered.forEach(r => {
                    const key = r.candidateEmail || r.candidateName;
                    if (!seen.has(key)) {
                      seen.add(key);
                      const allForCandidate = filtered.filter(x => (x.candidateEmail || x.candidateName) === key);
                      groups.push({ key, referrals: allForCandidate, primary: allForCandidate.find(x => x.id === sel) || allForCandidate[0] });
                    }
                  });
                  return groups.map(group => {
                    const r = group.referrals.find(x => x.id === sel) || group.primary;
                    const isSelected = group.referrals.some(x => x.id === sel);
                    const hasMultiple = group.referrals.length > 1;
                    return (
                      <div key={group.key} className="card-enter"
                        style={{ background: "#FFFFFF", borderRadius: 12, border: "1.5px solid", borderColor: isSelected ? "#E8896A" : "rgba(0,0,0,0.06)", transition: "all 0.15s", boxShadow: isSelected ? "0 0 0 3px rgba(232,137,106,0.07)" : "0 1px 3px rgba(26,22,18,0.05)" }}>
                        {/* Main card - click to select */}
                        <div onClick={() => setSel(r.id === sel ? null : r.id)}
                          style={{ padding: "15px 18px", cursor: "pointer" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", gap: 11 }}>
                              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{inits(r.candidateName)}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2, flexWrap: "wrap" }}>
                                  <span style={{ fontWeight: 700, fontSize: 14 }}>{r.blurred ? blurName(r.candidateName) : r.candidateName}</span>
                                  {r.blurred && <span style={{ fontSize: 10, background: "rgba(245,158,11,0.1)", color: "#F59E0B", padding: "1px 7px", borderRadius: 20, fontFamily: "DM Mono, monospace", fontWeight: 700 }}>🔒 Pro</span>}
                                  <span style={{ padding: "1px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: STATUS_TA[r.status]?.bg, color: STATUS_TA[r.status]?.color }}>{STATUS_TA[r.status]?.label}</span>
                                  {r.aiAnalysis && <span style={{ padding: "1px 6px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: "rgba(232,137,106,0.12)", color: "#C4623E", fontFamily: "DM Mono, monospace" }}>🤖 AI</span>}
                                </div>
                                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 6 }}>
                                  via <strong style={{ color: "#6B7280" }}>{r.referrerName}</strong>
                                  {r.referrerLinkedinVerified && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 4, background: "rgba(10,102,194,0.1)", borderRadius: 10, padding: "1px 5px" }}><svg width="8" height="8" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg><span style={{ fontSize: 9, color: "#0A66C2", fontWeight: 700, fontFamily: "DM Mono, monospace" }}>LI</span></span>}
                                  {" "}<TierBadge totalPlaced={r.referrerStats?.placed || 0} />
                                  {" · "}{r.referrerRelationship}
                                </div>
                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{r.skills.map(s => <Tag key={s}>{s}</Tag>)}</div>
                              </div>
                            </div>
                            <div style={{ textAlign: "center", flexShrink: 0, marginLeft: 8 }}>
                              <div style={{ background: "rgba(0,0,0,0.07)", borderRadius: 8, padding: "5px 9px", marginBottom: 3 }}>
                                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 19, color: "#E8896A" }}>{avg(r)}</div>
                                <div style={{ fontSize: 9, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>vouch</div>
                              </div>
                              <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{r.submittedAt}</div>
                            </div>
                          </div>
                        </div>

                        {/* Role pills - one per referral for this candidate */}
                        <div style={{ padding: "0 18px 12px", display: "flex", gap: 6, flexWrap: "wrap", borderTop: hasMultiple ? "1px solid rgba(0,0,0,0.05)" : "none", paddingTop: hasMultiple ? 10 : 0 }}>
                          {group.referrals.map(ref => {
                            const isActive = ref.id === sel || (!sel && ref.id === group.primary.id && isSelected);
                            const statusColor = ref.status === "placed" ? "#059669" : ref.status === "contacted" ? "#3B82F6" : ref.status === "new" ? "#9CA3AF" : "#6B7280";
                            return (
                              <button key={ref.id}
                                onClick={(e) => { e.stopPropagation(); setSel(ref.id); }}
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "4px 10px", borderRadius: 20, border: "1.5px solid",
                                  borderColor: isActive ? "#D85A30" : "rgba(0,0,0,0.1)",
                                  background: isActive ? "#D85A30" : "#F9F7F5",
                                  color: isActive ? "#FFFFFF" : "#6B7280",
                                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                                  fontFamily: "inherit", transition: "all 0.15s",
                                }}>
                                <span style={{ fontSize: 9 }}>📋</span>
                                {ref.briefTitle || "General"}
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: isActive ? "rgba(255,255,255,0.6)" : statusColor, flexShrink: 0 }}></span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Mobile detail panel — slide up sheet */}
              {selRef && isMobile && (
                <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                  <div onClick={() => setSelRef(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
                  <div style={{ position: "relative", background: "#FAFAF8", borderRadius: "20px 20px 0 0", maxHeight: "90vh", overflowY: "auto", padding: "0 0 40px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px 12px", borderBottom: "1px solid rgba(0,0,0,0.07)", position: "sticky", top: 0, background: "#FAFAF8", zIndex: 1 }}>
                      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16 }}>{selRef.blurred ? blurName(selRef.candidateName) : selRef.candidateName}</div>
                      <button onClick={() => setSelRef(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9CA3AF" }}>✕</button>
                    </div>
                    <div style={{ padding: "16px 20px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 14 }}>
                        {selRef.skills.map(s => <Tag key={s}>{s}</Tag>)}
                      </div>
                      <div style={{ background: "#F8F5F2", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontFamily: "DM Mono, monospace" }}>Vouch Scores</div>
                        {[{ l: "Skills", v: selRef.skillsRating }, { l: "Work Ethic", v: selRef.workEthicRating }, { l: "Coachability", v: selRef.coachabilityRating }].map(s => (
                          <div key={s.l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#9CA3AF", marginBottom: 5 }}>
                            <span style={{ width: 76, flexShrink: 0 }}>{s.l}</span><Dots value={s.v} /><span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#E8896A" }}>{s.v}/5</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, fontFamily: "DM Mono, monospace" }}>Referrer Note</div>
                        <div style={{ background: "#FAFAF8", borderRadius: 9, padding: "10px 12px", fontSize: 12, lineHeight: 1.65, color: "#6B7280", borderLeft: "3px solid #D4845A" }}>
                          "{selRef.blurred ? selRef.note.split(". ").slice(0, 2).join(". ") + "..." : selRef.note}"
                          {selRef.blurred && <div style={{ marginTop: 6, fontSize: 11, color: "#F59E0B" }}>🔒 Upgrade to Pro to see full note</div>}
                        </div>
                        {(selRef.referrerStandout || selRef.referrerDuration || selRef.referrerWorkAgain) && (
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 5 }}>
                            {selRef.referrerRelationship && <div style={{ display: "flex", gap: 6, fontSize: 11 }}><span style={{ color: "#9CA3AF", minWidth: 80 }}>Relationship</span><span style={{ color: "#1C1917", fontWeight: 600 }}>{selRef.referrerRelationship}</span></div>}
                            {selRef.referrerDuration && <div style={{ display: "flex", gap: 6, fontSize: 11 }}><span style={{ color: "#9CA3AF", minWidth: 80 }}>Duration</span><span style={{ color: "#1C1917", fontWeight: 600 }}>{selRef.referrerDuration}</span></div>}
                            {selRef.referrerStandout && <div style={{ display: "flex", gap: 6, fontSize: 11 }}><span style={{ color: "#9CA3AF", minWidth: 80 }}>Standout</span><span style={{ color: "#1C1917", fontWeight: 600, fontStyle: "italic" }}>"{selRef.referrerStandout}"</span></div>}
                            {selRef.referrerWorkAgain && <div style={{ display: "flex", gap: 6, fontSize: 11 }}><span style={{ color: "#9CA3AF", minWidth: 80 }}>Work again?</span><span style={{ color: selRef.referrerWorkAgain === "yes" ? "#059669" : "#6B7280", fontWeight: 600 }}>{selRef.referrerWorkAgain === "yes" ? "✅ Absolutely" : selRef.referrerWorkAgain === "maybe" ? "🤔 In the right role" : "Not sure"}</span></div>}
                          </div>
                        )}
                      </div>
                      {isProTA && <CandidatePassportDisplay email={selRef.candidateEmail} />}
                      {selCvFilename && isProTA && (
                        <button onClick={async () => {
                          const { data } = await supabase.storage.from("cvs").createSignedUrl(selCvFilename, 300);
                          if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                        }} style={{ width: "100%", background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 14 }}>
                          📄 Download CV
                        </button>
                      )}
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontFamily: "DM Mono, monospace" }}>Update Pipeline</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {["shortlisted", "interviewed", "offer_made", "hired"].map(status => (
                          <button key={status} onClick={async () => {
                            const now = new Date().toISOString();
                            await supabase.from("referrals").update({ pipeline_status: status, pipeline_updated_at: now, status: status === "hired" ? "placed" : "contacted", ...(status === "hired" ? { placed_at: now } : {}) }).eq("id", selRef.id);
                            setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, pipelineStatus: status, status: status === "hired" ? "placed" : "contacted" } : r));
                            setSelRef(prev => ({ ...prev, pipelineStatus: status }));
                            notifyReferrerPipeline(selRef, status);
                          }} style={{ background: selRef.pipelineStatus === status ? "#1C1917" : "rgba(0,0,0,0.06)", color: selRef.pipelineStatus === status ? "#fff" : "#6B7280", border: "none", borderRadius: 7, padding: "7px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            {status === "shortlisted" ? "Shortlisted" : status === "interviewed" ? "Interviewed" : status === "offer_made" ? "Offer Made" : "✅ Hired"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selRef && !isMobile && (
                <div style={{ position: "sticky", top: 20, height: "fit-content", maxHeight: "calc(100vh - 130px)", overflowY: "auto" }}>
                  <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 0 0 1px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                    <div style={{ padding: "18px 20px", borderBottom: "1px solid rgba(26,22,18,0.07)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 14 }}>Referral Detail</span>
                        <button onClick={() => setSel(null)} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 16 }}>✕</button>
                      </div>
                      <div style={{ display: "flex", gap: 11 }}>
                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{inits(selRef.candidateName)}</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
                            {selRef.blurred ? blurName(selRef.candidateName) : selRef.candidateName}
                            {selRef.blurred && <span style={{ marginLeft: 8, fontSize: 11, color: "#F59E0B", fontWeight: 600 }}>🔒 Upgrade to reveal</span>}
                          </div>
                          <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>
                            {selRef.blurred ? "••••••@••••••.com" : selRef.candidateEmail}
                          </div>
                          {selRef.candidateLinkedin && !selRef.blurred && (
                            <a href={"https://" + selRef.candidateLinkedin.replace("https://","")} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11, color: "#0077B5", textDecoration: "none", display: "flex", alignItems: "center", gap: 4, marginBottom: 7 }}>
                              🔗 LinkedIn Profile
                            </a>
                          )}
                          {selRef.blurred && selRef.candidateLinkedin && (
                            <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 7 }}>🔗 LinkedIn hidden — upgrade to reveal</div>
                          )}
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{selRef.skills.map(s => <Tag key={s}>{s}</Tag>)}</div>
                          {selRef.briefTitle && <div style={{ marginTop: 5, fontSize: 11, color: "#38BDF8", fontFamily: "DM Mono, monospace", fontWeight: 600, background: "rgba(56,189,248,0.1)", padding: "3px 8px", borderRadius: 6, display: "inline-block" }}>📋 Referred for: {selRef.briefTitle}</div>}
                        </div>
                      </div>
                    </div>

                    <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
                      <div>
                        {/* CV Download - Pro only */}
                        {selCvFilename ? (
                          <div style={{ background: "rgba(91,110,175,0.08)", borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: "1px solid rgba(91,110,175,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6EAF" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#5B6EAF" }}>CV attached</div>
                                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{isProTA ? "Click Download to open" : "Upgrade to Pro to access"}</div>
                              </div>
                            </div>
                            {isProTA ? (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={async () => {
                                const { data } = await supabase.storage.from("cvs").createSignedUrl(selCvFilename, 300);
                                if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                                else alert("Could not generate download link. Check storage permissions.");
                              }} style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontFamily: "inherit", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                                Download
                              </button>
                              <button onClick={async () => {
                                setLoad(p => ({ ...p, [sel + "_cvanalysis"]: true }));
                                try {
                                  // Step 1: Extract CV text via Netlify function
                                  let cvText = "";
                                  const extractRes = await fetch("/.netlify/functions/extract-cv", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ cvFilename: selCvFilename }),
                                  });
                                  if (extractRes.ok) {
                                    const extractData = await extractRes.json();
                                    cvText = extractData.text || "";
                                  }

                                  // Step 2: Send CV text + context to Claude
                                  const raw = await callClaude(
                                    `You are an expert talent acquisition specialist. A colleague has referred a candidate and uploaded their CV. Analyse this candidate thoroughly.

REFERRAL CONTEXT:
- Role referred for: ${selRef?.briefTitle || "General referral"}
- Vouch note from referrer: "${selRef?.note || "No note provided"}"
- Skills listed: ${(selRef?.skills || []).join(", ") || "None listed"}
- Referrer scores - Skills: ${selRef?.skillsRating}/5, Work ethic: ${selRef?.workEthicRating}/5, Coachability: ${selRef?.coachabilityRating}/5

CV CONTENT:
${cvText || "CV text could not be extracted."}

Provide a structured analysis with:
1. **Role Fit** — How well does their experience and skills match the role? Be specific, citing their actual CV content.
2. **Key Strengths** — 2-3 concrete strengths from their CV
3. **Gaps or Concerns** — Any missing experience, skills gaps, or red flags
4. **Interview Questions** — 3 specific questions based on their actual background
5. **Recommendation** — Interview / Screen call first / Pass (with reasoning)

Be direct and specific. Reference actual content from their CV.`,
                                    "Analyse this candidate's CV and fit for the role."
                                  );
                                  setLoad(p => ({ ...p, [sel + "_cvanalysisResult"]: raw }));
                                } catch (err) {
                                  setLoad(p => ({ ...p, [sel + "_cvanalysisResult"]: "Analysis failed: " + err.message }));
                                }
                                setLoad(p => ({ ...p, [sel + "_cvanalysis"]: false }));
                              }} style={{ background: "#F0F2FA", color: "#5B6EAF", border: "1px solid rgba(91,110,175,0.2)", borderRadius: 7, padding: "7px 12px", fontFamily: "inherit", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                                {load[sel + "_cvanalysis"] ? "Analysing..." : "AI Analyse"}
                              </button>
                            </div>
                            ) : (
                              <button onClick={() => setShowUpgrade(true)} style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                                🔒 Upgrade to Access
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ background: "#FAFAF8", borderRadius: 10, padding: "10px 14px", marginBottom: 14, border: "1px solid rgba(0,0,0,0.06)" }}>
                            <div style={{ fontSize: 11, color: "#9CA3AF" }}>No CV attached yet — candidate will be prompted to upload after giving consent.</div>
                          </div>
                        )}
                        {load[sel + "_cvanalysisResult"] && (
                          <div style={{ background: "rgba(91,110,175,0.06)", borderRadius: 10, padding: "14px", marginBottom: 14, border: "1px solid rgba(91,110,175,0.15)" }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#5B6EAF", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace", marginBottom: 8 }}>CV + FIT ANALYSIS</div>
                            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{load[sel + "_cvanalysisResult"]}</div>
                          </div>
                        )}

                        {/* Talent Passport display */}
                        <CandidatePassportDisplay email={selRef.candidateEmail} />
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 9, fontFamily: "DM Mono, monospace" }}>Vouch Scores</div>
                        {[{ l: "Skills", v: selRef.skillsRating }, { l: "Work Ethic", v: selRef.workEthicRating }, { l: "Coachability", v: selRef.coachabilityRating }].map(s => (
                          <div key={s.l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#9CA3AF", marginBottom: 5 }}>
                            <span style={{ width: 76, flexShrink: 0 }}>{s.l}</span><Dots value={s.v} /><span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#E8896A" }}>{s.v}/5</span>
                          </div>
                        ))}
                      </div>

                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Referrer Note</div>
                        <div style={{ background: "#FAFAF8", borderRadius: 9, padding: "10px 12px", fontSize: 12, lineHeight: 1.65, color: "#6B7280", borderLeft: "3px solid #D4845A" }}>
                          "{selRef.blurred
                            ? selRef.note.split(". ").slice(0, 2).join(". ") + (selRef.note.split(". ").length > 2 ? "..." : "")
                            : selRef.note}"
                          {selRef.blurred && <div style={{ marginTop: 6, fontSize: 11, color: "#F59E0B", fontStyle: "normal" }}>🔒 Full note visible after upgrading to Pro</div>}
                        </div>
                        <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 4, fontFamily: "DM Mono, monospace" }}>— {selRef.referrerName} · {selRef.referrerStats.placed}/{selRef.referrerStats.total} referred placed</div>

                        {/* Trust Map */}
                        <div style={{ marginTop: 14 }}>
                          <TrustMap referral={selRef} allReferrals={referrals} />
                        </div>

                        {/* Warm intro request */}
                        <div style={{ marginTop: 10 }}>
                          {selRef.introRequested ? (
                            <div style={{ padding: "8px 12px", background: selRef.introAccepted === true ? "rgba(72,187,120,0.08)" : selRef.introAccepted === false ? "rgba(239,68,68,0.06)" : "rgba(91,110,175,0.08)", border: `1px solid ${selRef.introAccepted === true ? "rgba(72,187,120,0.2)" : selRef.introAccepted === false ? "rgba(239,68,68,0.15)" : "rgba(91,110,175,0.2)"}`, borderRadius: 8, fontSize: 11, color: selRef.introAccepted === true ? "#059669" : selRef.introAccepted === false ? "#EF4444" : "#5B6EAF", fontWeight: 600 }}>
                              {selRef.introAccepted === true ? "✅ Intro accepted — check your email" : selRef.introAccepted === false ? "❌ Referrer declined" : "⏳ Intro requested — awaiting response"}
                            </div>
                          ) : (
                            <WarmIntroButton referral={selRef} taUser={user} onRequested={() => setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, introRequested: true } : r))} />
                          )}
                        </div>
                      </div>

                      <div style={{ background: "#FAFAF8", borderRadius: 11, padding: "13px 15px", border: "1px solid rgba(45,90,39,0.14)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
                          <span>🤖</span><span style={{ fontSize: 10, fontWeight: 700, color: "#E8896A", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>Agent 1 · Analyser</span>{isL(selRef.id, "a") && <Spin />}
                        </div>
                        {isL(selRef.id, "a") && <div style={{ fontSize: 12, color: "#9CA3AF" }}>Analysing referral intelligence...</div>}
                        {selRef.aiAnalysis && !isL(selRef.id, "a") && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>{selRef.aiAnalysis.summary}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                              <div style={{ background: "#FFFFFF", borderRadius: 7, padding: "7px 9px" }}><div style={{ fontSize: 9, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 2 }}>READINESS</div><div style={{ fontSize: 11, fontWeight: 700, color: "#E8896A" }}>{selRef.aiAnalysis.readiness}</div></div>
                              <div style={{ background: "#FFFFFF", borderRadius: 7, padding: "7px 9px" }}><div style={{ fontSize: 9, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 2 }}>CONFIDENCE</div><div style={{ fontSize: 11, fontWeight: 700, color: "#E8896A" }}>{selRef.aiAnalysis.confidenceScore}%</div></div>
                            </div>
                            {selRef.aiAnalysis.strength && <div style={{ fontSize: 11, color: "#E8896A", padding: "6px 9px", background: "rgba(0,0,0,0.07)", borderRadius: 7 }}>✓ {selRef.aiAnalysis.strength}</div>}
                            {selRef.aiAnalysis.watchout && <div style={{ fontSize: 11, color: "#F59E0B", padding: "6px 9px", background: "rgba(245,158,11,0.1)", borderRadius: 7 }}>⚠ {selRef.aiAnalysis.watchout}</div>}
                          </div>
                        )}
                      </div>

                      <div style={{ background: "rgba(56,189,248,0.1)", borderRadius: 11, padding: "13px 15px", border: "1px solid rgba(58,107,138,0.14)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span>✉️</span><span style={{ fontSize: 10, fontWeight: 700, color: "#38BDF8", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>Agent 2 · Drafter</span>{isL(selRef.id, "d") && <Spin color="#3A6B8A" />}
                          </div>
                          {!selRef.draftEmail && !isL(selRef.id, "d") && !selRef.blurred && (
                            <button onClick={() => draftEmail(selRef)} style={{ background: "#3A6B8A", color: "#fff", border: "none", borderRadius: 6, padding: "3px 10px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Draft</button>
                          )}
                        </div>
                        {selRef.blurred
                          ? <div style={{ fontSize: 12, color: "#38BDF8" }}>🔒 Upgrade to Pro to draft outreach for this candidate.</div>
                          : !selRef.draftEmail && !isL(selRef.id, "d")
                            ? <div style={{ fontSize: 12, color: "#38BDF8" }}>Click to generate personalised outreach for {selRef.candidateName.split(" ")[0]}.</div>
                            : isL(selRef.id, "d")
                              ? <div style={{ fontSize: 12, color: "#9CA3AF" }}>Crafting personalised outreach...</div>
                              : selRef.draftEmail && (
                                <div>
                                  <div style={{ fontSize: 10, fontFamily: "DM Mono, monospace", color: "#9CA3AF", marginBottom: 3 }}>SUBJECT</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1917", marginBottom: 8 }}>{selRef.draftEmail.subject}</div>
                                  <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.7, background: "#FFFFFF", borderRadius: 7, padding: "9px 11px", whiteSpace: "pre-line" }}>{selRef.draftEmail.body}</div>
                                  <button onClick={() => navigator.clipboard?.writeText(selRef.draftEmail.body).catch(() => {})} style={{ marginTop: 6, background: "transparent", border: "1px solid rgba(26,22,18,0.12)", borderRadius: 6, padding: "3px 9px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#9CA3AF" }}>Copy</button>
                                </div>
                              )
                        }
                      </div>

                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Update Status</div>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                          {Object.entries(STATUS_TA).map(([k, v]) => (
                            <button key={k} onClick={() => updateStatus(selRef.id, k)}
                              style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "DM Mono, monospace", cursor: "pointer", border: "none", background: selRef.status === k ? v.color : v.bg, color: selRef.status === k ? "#fff" : v.color, outline: selRef.status === k ? "2px solid " + v.color : "none", outlineOffset: 2 }}>
                              {v.label}
                            </button>
                          ))}
                        </div>
                        {/* Pipeline stage — updates referrer's timeline */}
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Pipeline Stage <span style={{ fontWeight: 400, color: "#C4623E", textTransform: "none", letterSpacing: 0 }}>· referrer sees this</span></div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {[
                            { status: "shortlisted", label: "Shortlisted", emoji: "📋" },
                            { status: "interviewed", label: "Interviewed", emoji: "🤝" },
                            { status: "offer_made", label: "Offer Made", emoji: "📄" },
                            { status: "hired", label: "Hired", emoji: "🎉" },
                          ].map(({ status, label, emoji }) => {
                            const isActive = selRef.pipelineStatus === status;
                            return (
                              <button key={status} onClick={async () => {
                                const now = new Date().toISOString();
                                const updateData = {
                                  pipeline_status: status,
                                  pipeline_updated_at: now,
                                  status: status === "hired" ? "placed" : "contacted",
                                };
                                if (status === "hired") updateData.placed_at = now;
                                await supabase.from("referrals").update(updateData).eq("id", selRef.id);
                                setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, pipelineStatus: status, status: status === "hired" ? "placed" : "contacted" } : r));
                            notifyReferrerPipeline(selRef, status);
                                notifyReferrer({
                                  event: status,
                                  referrerEmail: selRef.referrerEmail,
                                  referrerName: selRef.referrerName,
                                  candidateName: selRef.candidateName,
                                  taName: user.name,
                                  taCompany: user.company,
                                  briefTitle: selRef.briefTitle,
                                  bonusAmount: selRef.bonusOffered || 0,
                                });
                                // Send placement confirmation request to referrer
                                if (status === "hired" && selRef.referrerEmail) {
                                  await fetch("/.netlify/functions/notify-referrer", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                      event: "placement_confirm_request",
                                      referrerEmail: selRef.referrerEmail,
                                      referrerName: selRef.referrerName,
                                      candidateName: selRef.candidateName,
                                      taName: user.name,
                                      taCompany: user.company,
                                      briefTitle: selRef.briefTitle,
                                      bonusAmount: selRef.bonusOffered || 0,
                                      confirmUrl: `https://www.peerrefer.com/.netlify/functions/confirm-placement?ref=${selRef.id}`,
                                    }),
                                  });
                                }
                                // Send candidate feedback request
                                if (status === "hired" && selRef.candidateEmail) {
                                  fetch("/.netlify/functions/candidate-feedback", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                      referralId: selRef.id,
                                      candidateEmail: selRef.candidateEmail,
                                      candidateName: selRef.candidateName,
                                      taName: user.name,
                                      taCompany: user.company,
                                    }),
                                  }).catch(() => {});
                                }
                              }}
                                style={{ display: "flex", alignItems: "center", gap: 8, background: isActive ? "rgba(232,137,106,0.12)" : "#FAFAF8", border: isActive ? "1.5px solid rgba(232,137,106,0.4)" : "1px solid rgba(0,0,0,0.07)", borderRadius: 8, padding: "7px 11px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", color: isActive ? "#C4623E" : "#374151", textAlign: "left" }}>
                                <span>{emoji}</span>
                                <span style={{ flex: 1 }}>{label}</span>
                                {isActive && <span style={{ fontSize: 10, color: "#E8896A", fontFamily: "DM Mono, monospace" }}>● CURRENT</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {selRef.blurred
                        ? <button onClick={() => setShowUpgrade(true)}
                            style={{ width: "100%", background: "#C47D2B", color: "#fff", border: "none", textAlign: "center", padding: "11px", borderRadius: 9, fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                            🔒 Upgrade to Pro to Reach Out
                          </button>
                        : <a href={"mailto:" + selRef.candidateEmail}
                            style={{ display: "block", background: "#E8896A", color: "#fff", textDecoration: "none", textAlign: "center", padding: "11px", borderRadius: 9, fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>
                            ✉ Reach Out to {selRef.candidateName.split(" ")[0]}
                          </a>
                      }

                      {/* TA Notes - phone screening */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontFamily: "DM Mono, monospace" }}>📋 Phone Screen Notes</div>
                        <textarea
                          value={selRef.taNotes || ""}
                          onChange={async (e) => {
                            const val = e.target.value;
                            setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, taNotes: val } : r));
                          }}
                          onBlur={async (e) => {
                            await supabase.from("referrals").update({ ta_notes: e.target.value }).eq("id", selRef.id);
                          }}
                          placeholder="Add phone screen notes, interview feedback, next steps..."
                          rows={4}
                          style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, color: "#1C1917", resize: "vertical", outline: "none", lineHeight: 1.6, boxSizing: "border-box" }}
                        />
                        <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 4 }}>Auto-saved when you click away</div>
                      </div>

                      <button onClick={() => deleteReferral(selRef.id)}
                        style={{ width: "100%", background: "transparent", border: "1px solid rgba(184,84,80,0.3)", borderRadius: 9, padding: "8px", fontFamily: "inherit", fontWeight: 600, fontSize: 12, cursor: "pointer", color: "#EF4444" }}>
                        🗑 Remove from pool
                      </button>

                      {/* Watermark */}
                      <div style={{ textAlign: "center", fontSize: 10, color: "rgba(0,0,0,0.1)", fontFamily: "DM Mono, monospace", marginTop: 8, lineHeight: 1.4 }}>
                        Viewed by {user.name} · {user.company} · {new Date().toLocaleDateString("en-AU")} · PeerRefer
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Mobile full-screen detail panel */}
            {selRef && isMobile && (
              <div className="detail-enter" style={{ position: "fixed", inset: 0, background: "#FAFAF8", zIndex: 200, overflowY: "auto", paddingBottom: 90 }}>
                <div style={{ background: "#FFFFFF", borderBottom: "1px solid rgba(0,0,0,0.08)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0, zIndex: 10 }}>
                  <button onClick={() => setSel(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#1C1917", padding: 0, lineHeight: 1 }}>←</button>
                  <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15 }}>Referral Detail</span>
                </div>
                <div style={{ padding: "16px" }}>
                  <div style={{ display: "flex", gap: 11, marginBottom: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{inits(selRef.candidateName)}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{selRef.blurred ? blurName(selRef.candidateName) : selRef.candidateName}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{selRef.candidateEmail || "Email hidden"}</div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>{selRef.skills.map(s => <Tag key={s}>{s}</Tag>)}</div>
                    </div>
                  </div>
                  {selRef.briefTitle && <div style={{ fontSize: 11, color: "#38BDF8", fontFamily: "DM Mono, monospace", fontWeight: 600, marginBottom: 12 }}>📋 Referred for: {selRef.briefTitle}</div>}
                  <div style={{ display: "flex", gap: 5, marginBottom: 14, flexWrap: "wrap" }}>
                    {Object.entries(STATUS_TA).map(([k, v]) => (
                      <button key={k} onClick={() => updateStatus(selRef.id, k)}
                        style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "DM Mono, monospace", cursor: "pointer", border: "none", background: selRef.status === k ? v.color : v.bg, color: selRef.status === k ? "#fff" : v.color, outline: selRef.status === k ? "2px solid " + v.color : "none", outlineOffset: 2 }}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {[["Skills", selRef.skillsRating], ["Work Ethic", selRef.workEthicRating], ["Coachability", selRef.coachabilityRating]].map(([l, v]) => (
                      <div key={l} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FFFFFF", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(0,0,0,0.06)" }}>
                        <span style={{ fontSize: 12, color: "#6B7280" }}>{l}</span>
                        <span style={{ fontFamily: "DM Mono, monospace", fontWeight: 700, color: "#E8896A" }}>{v}/5</span>
                      </div>
                    ))}
                  </div>
                  {selRef.note && <div style={{ marginTop: 12, background: "#FFFFFF", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#6B7280", lineHeight: 1.6, borderLeft: "3px solid #D4845A", fontStyle: "italic" }}>"{selRef.note}"</div>}
                  <div style={{ marginTop: 12 }}>
                    <TrustMap referral={selRef} allReferrals={referrals} />
                  </div>
                  <div style={{ marginTop: 12, background: "#1C1917", borderRadius: 12, padding: "14px 16px", color: "#FAFAF8" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", color: "#9CA3AF", marginBottom: 8 }}>🤖 AGENT 1 · ANALYSER</div>
                    {isL(selRef.id, "a") ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Spin /><span style={{ fontSize: 12, color: "#9CA3AF" }}>Analysing candidate...</span></div>
                    ) : selRef.aiAnalysis ? (
                      <div style={{ fontSize: 12, lineHeight: 1.7 }}>{selRef.aiAnalysis.summary}</div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#9CA3AF" }}>Analysis will appear here once complete.</div>
                    )}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontFamily: "DM Mono, monospace" }}>Pipeline Stage · referrer sees this</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {["shortlisted", "interviewed", "offer_made", "hired"].map(status => (
                        <button key={status} onClick={async () => {
                          const now = new Date().toISOString();
                          await supabase.from("referrals").update({ pipeline_status: status, pipeline_updated_at: now, status: status === "hired" ? "placed" : "contacted", ...(status === "hired" ? { placed_at: now } : {}) }).eq("id", selRef.id);
                          setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, pipelineStatus: status, status: status === "hired" ? "placed" : "contacted" } : r));
                          notifyReferrerPipeline(selRef, status);
                        }} style={{ background: selRef.pipelineStatus === status ? "#1C1917" : "rgba(0,0,0,0.04)", color: selRef.pipelineStatus === status ? "#fff" : "#6B7280", border: "1px solid", borderColor: selRef.pipelineStatus === status ? "#1C1917" : "rgba(0,0,0,0.1)", borderRadius: 8, padding: "10px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          {status === "shortlisted" ? "📋 Shortlisted" : status === "interviewed" ? "🤝 Interviewed" : status === "offer_made" ? "📄 Offer Made" : "🎉 Hired"}
                          {selRef.pipelineStatus === status && <span style={{ fontSize: 10, background: "rgba(255,255,255,0.15)", padding: "1px 6px", borderRadius: 10 }}>CURRENT</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                    {[["shortlisted", "Shortlisted", "📋"], ["interviewed", "Interviewed", "🤝"], ["offer_made", "Offer Made", "📄"], ["hired", "Hired", "🎉"]].map(([status, label, emoji]) => {
                      const isActive = selRef.pipelineStatus === status;
                      return (
                        <button key={status} onClick={async () => {
                          await supabase.from("referrals").update({ pipeline_status: status, pipeline_updated_at: new Date().toISOString(), status: status === "hired" ? "placed" : "contacted" }).eq("id", selRef.id);
                          setReferrals(prev => prev.map(r => r.id === selRef.id ? { ...r, pipelineStatus: status, status: status === "hired" ? "placed" : "contacted" } : r));
                            notifyReferrerPipeline(selRef, status);
                          notifyReferrer({
                            event: status,
                            referrerEmail: selRef.referrerEmail,
                            referrerName: selRef.referrerName,
                            candidateName: selRef.candidateName,
                            taName: user.name,
                            taCompany: user.company,
                            briefTitle: selRef.briefTitle,
                            bonusAmount: selRef.bonusOffered || 0,
                          });
                        }} style={{ display: "flex", alignItems: "center", gap: 10, background: isActive ? "rgba(232,137,106,0.12)" : "#FFFFFF", border: isActive ? "1.5px solid rgba(232,137,106,0.4)" : "1px solid rgba(0,0,0,0.07)", borderRadius: 10, padding: "10px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", color: isActive ? "#C4623E" : "#374151" }}>
                          <span>{emoji}</span><span style={{ flex: 1 }}>{label}</span>
                          {isActive && <span style={{ fontSize: 10, color: "#E8896A", fontFamily: "DM Mono, monospace" }}>● CURRENT</span>}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => { removeFromPool(selRef.id); setSel(null); }} style={{ marginTop: 14, width: "100%", background: "rgba(239,68,68,0.06)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>🗑 Remove from pool</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "intelligence" && (
          <div style={{ padding: "24px 28px", maxWidth: 800 }}>
            <div style={{ marginBottom: 20 }}>
              <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em", marginBottom: 3 }}>Pipeline Intelligence</h1>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>Agents 4 & 5 — Match Engine and Pipeline Nudger — analysing your pool.</p>
            </div>
            {intelLoad && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {["🧠 Running Match Engine...", "📊 Scanning pipeline patterns...", "💬 Generating nudges..."].map((m, i) => (
                  <div key={i} style={{ background: "#FFFFFF", borderRadius: 11, padding: "14px 18px", border: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 9 }}><Spin /><span style={{ fontSize: 13, color: "#9CA3AF" }}>{m}</span></div>
                ))}
              </div>
            )}
            {!intelLoad && nudge && (
              <div style={{ background: "#1C1917", borderRadius: 14, padding: "20px 24px", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                  <span>💬</span><span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "DM Mono, monospace" }}>Agent 5 · Pipeline Nudger</span>
                  {nudge.urgentCount > 0 && <span style={{ background: "#B85450", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace" }}>{nudge.urgentCount} urgent</span>}
                </div>
                <div style={{ fontSize: 15, color: "#FAFAF8", lineHeight: 1.7, fontFamily: "Playfair Display, Georgia, serif" }}>{nudge.message}</div>
              </div>
            )}
            {!intelLoad && intel && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                  <span>🧠</span><span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "DM Mono, monospace" }}>Agent 4 · Match Engine</span>
                </div>
                {intel.topPriority && (
                  <div style={{ background: "rgba(0,0,0,0.07)", borderRadius: 12, padding: "16px 18px", marginBottom: 12, border: "1px solid rgba(45,90,39,0.18)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#E8896A", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5, fontFamily: "DM Mono, monospace" }}>TOP PRIORITY</div>
                    <div style={{ fontSize: 14, color: "#1C1917", lineHeight: 1.65 }}>{intel.topPriority}</div>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {intel.insights?.map((ins, i) => {
                    const cols = { pattern: { bg: "#E8F2F8", b: "#3A6B8A" }, nudge: { bg: "#FBF2E3", b: "#C47D2B" }, opportunity: { bg: "rgba(0,0,0,0.07)", b: "#E8896A" } };
                    const c = cols[ins.type] || cols.pattern;
                    return (
                      <div key={i} style={{ background: c.bg, borderRadius: 11, padding: "13px 15px", borderLeft: "4px solid " + c.b }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: c.b, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>{ins.type}</span>
                          {ins.urgency === "high" && <span style={{ fontSize: 10, background: "#B85450", color: "#fff", padding: "1px 6px", borderRadius: 9, fontFamily: "DM Mono, monospace", fontWeight: 700 }}>HIGH</span>}
                        </div>
                        <div style={{ fontSize: 13, color: "#1C1917", lineHeight: 1.6 }}>{ins.message}</div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={runIntel} style={{ marginTop: 16, background: "transparent", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "8px 16px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#9CA3AF" }}>↻ Refresh Intelligence</button>
              </div>
            )}

            {/* Referrer Leaderboard */}
            {referrals.length > 0 && (
              <div style={{ marginTop: 24, background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", padding: "20px 22px" }}>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Your Referrer Leaderboard</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 18 }}>Your top connectors ranked by placement rate — invest time in these relationships.</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(() => {
                    const referrerMap = {};
                    referrals.forEach(r => {
                      const name = r.referrerName || "Unknown";
                      if (!referrerMap[name]) referrerMap[name] = { name, referred: 0, placed: 0, linkedin: r.referrerLinkedinVerified };
                      referrerMap[name].referred++;
                      if (r.status === "placed") referrerMap[name].placed++;
                    });
                    return Object.values(referrerMap)
                      .sort((a, b) => (b.placed / Math.max(b.referred, 1)) - (a.placed / Math.max(a.referred, 1)))
                      .slice(0, 10)
                      .map((r, i) => {
                        const rate = Math.round((r.placed / Math.max(r.referred, 1)) * 100);
                        const tier = getReferrerTier(r.placed);
                        return (
                          <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i < 3 ? `${tier.bg}` : "#FAFAF8", borderRadius: 10, border: i < 3 ? `1px solid ${tier.border}` : "1px solid rgba(0,0,0,0.04)" }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i < 3 ? tier.color : "#E5E7EB", color: i < 3 ? "#fff" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "#1C1917" }}>{r.name}</span>
                                {r.linkedin && <svg width="11" height="11" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>}
                                <TierBadge totalPlaced={r.placed} />
                              </div>
                              <div style={{ height: 4, background: "rgba(0,0,0,0.06)", borderRadius: 2, marginTop: 5 }}>
                                <div style={{ height: "100%", background: tier.color, borderRadius: 2, width: `${rate}%` }} />
                              </div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 800, color: tier.color, fontFamily: "Playfair Display, Georgia, serif" }}>{rate}%</div>
                              <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{r.placed}/{r.referred} placed</div>
                            </div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "jobs" && (
          <div style={{ padding: isMobile ? "16px 12px" : "24px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: isMobile ? 20 : 24, letterSpacing: "-0.02em", marginBottom: 3 }}>Job Briefs</h1>
                {!isMobile && <p style={{ fontSize: 12, color: "#9CA3AF" }}>Create briefs for roles you're hiring. <span style={{ color: "#E8896A", fontWeight: 600 }}>Agent 6 auto-matches your talent pool to each brief.</span></p>}
              </div>
              <button onClick={() => { setEditingBrief({ id: "new", title: "", skills: [], skillsInput: "", seniority: "", location: "", bonus: 2000, status: "active", description: "", positionDescription: "", evp: "", logoUrl: "", pdFilename: "" }); setShowBriefModal(true); }}
                style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 9, padding: "9px 14px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 }}>
                + New Brief
              </button>
            </div>

            {/* Brief cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
              {briefs.map((brief, bi) => (
                <div key={brief.id} className="card-enter" style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", padding: isMobile ? "14px 12px" : "20px 22px", boxShadow: "0 1px 4px rgba(26,22,18,0.05)", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "flex-start", justifyContent: "space-between", marginBottom: 14, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 44, height: 44, background: brief.status === "active" ? "rgba(0,0,0,0.07)" : "#F0EDEA", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, overflow: "hidden" }}>
                        {(brief.logo_url || brief.logoUrl)
                          ? <img src={brief.logo_url || brief.logoUrl} alt={brief.title} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }} />
                          : "💼"
                        }
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 17, marginBottom: 2 }}>{brief.title}</div>
                          {brief.featured && <span style={{ background: "rgba(196,125,43,0.12)", color: "#C47D2B", border: "1px solid rgba(196,125,43,0.2)", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace" }}>⭐ FEATURED</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{brief.seniority} · {brief.location}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: isMobile ? "wrap" : "nowrap", justifyContent: "flex-end" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "DM Mono, monospace", background: brief.status === "active" ? "rgba(0,0,0,0.07)" : "#F0EDEA", color: brief.status === "active" ? "#E8896A" : "#9CA3AF" }}>
                        {brief.status === "active" ? "● Active" : "○ Paused"}
                      </span>
                      {!isMobile && <button onClick={async () => {
                        await supabase.from("briefs").update({ featured: !brief.featured }).eq("id", brief.id);
                        setBriefs(prev => prev.map(b => b.id === brief.id ? { ...b, featured: !b.featured } : b));
                      }} style={{ background: brief.featured ? "rgba(196,125,43,0.1)" : "transparent", border: "1px solid rgba(196,125,43,0.2)", borderRadius: 7, padding: "4px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#C47D2B", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {brief.featured ? "⭐" : "☆"}
                      </button>}
                      <button onClick={() => setSharingBrief(brief)}
                        style={{ background: "rgba(232,137,106,0.1)", border: "1px solid rgba(232,137,106,0.18)", borderRadius: 7, padding: "4px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#C4623E", fontWeight: 600 }}>↗ Share</button>
                      {!isMobile && <><button onClick={() => { setEditingBrief({...brief, skillsInput: "", positionDescription: brief.position_description || brief.positionDescription || "", evp: brief.evp || "", logoUrl: brief.logo_url || brief.logoUrl || "", pdFilename: brief.pd_filename || brief.pdFilename || ""}); setShowBriefModal(true); }}
                        style={{ background: "transparent", border: "1px solid rgba(26,22,18,0.12)", borderRadius: 7, padding: "4px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#9CA3AF" }}>Edit</button>
                      <button onClick={async () => {
                          if (window.confirm("Delete this brief?")) {
                            await supabase.from("briefs").delete().eq("id", brief.id);
                            setBriefs(prev => prev.filter(b => b.id !== brief.id));
                          }
                        }}
                        style={{ background: "transparent", border: "1px solid rgba(184,84,80,0.2)", borderRadius: 7, padding: "4px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "#EF4444" }}>Delete</button></>}
                    </div>
                  </div>

                  <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, marginBottom: 12 }}>{brief.description}</div>

                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
                    {brief.skills.map(s => <span key={s} style={{ background: "rgba(232,137,106,0.12)", color: "#C4623E", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontFamily: "DM Mono, monospace" }}>{s}</span>)}
                  </div>

                  {brief.bonus > 0 && (
                    <div style={{ fontSize: 12, color: "#F59E0B", marginBottom: 14, fontWeight: 600 }}>💰 ${brief.bonus.toLocaleString()} referral bonus</div>
                  )}

                  {/* Match results */}
                  <div style={{ borderTop: "1px solid rgba(0,0,0,0.07)", paddingTop: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>
                        🤖 Agent 6 · Auto-matched profiles
                      </div>
                      <button
                        onClick={async () => {
                          setMatchLoading(l => ({ ...l, [brief.id]: true }));
                          try {
                            const results = await agentJobMatch(brief, referrals);
                            setBriefs(prev => prev.map(b => b.id === brief.id ? { ...b, matchResults: results } : b));
                          } catch (e) {
                            console.error("Match failed:", e);
                          }
                          setMatchLoading(l => ({ ...l, [brief.id]: false }));
                        }}
                        style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                        {matchLoading[brief.id] ? <><Spin color="#FFFFFF" /> Matching (15-20s)...</> : "🎯 Run Match"}
                      </button>
                      <button
                        onClick={async () => {
                          setIntelLoading(l => ({...l, [brief.id]: true}));
                          const intel = await agentMarketIntel(brief, referrals);
                          setIntelResults(r => ({...r, [brief.id]: intel}));
                          setIntelLoading(l => ({...l, [brief.id]: false}));
                        }}
                        style={{ background: "rgba(232,137,106,0.12)", color: "#C4623E", border: "1px solid rgba(232,137,106,0.18)", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                        {intelLoading[brief.id] ? <><Spin color="#C4623E" /> Analysing...</> : "📊 Market Intel"}
                      </button>
                    </div>

                    {brief.matchResults && brief.matchResults.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {brief.matchResults.length} candidates ranked · click to view
                          </div>
                          <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>
                            🔥 {brief.matchResults.filter(r => r.flag === "hot").length} hot · ✓ {brief.matchResults.filter(r => r.flag === "good").length} good · {brief.matchResults.filter(r => r.flag === "weak").length} weak
                          </div>
                        </div>
                        {brief.matchResults.map(result => {
                            const candidate = referrals.find(r => r.id === result.id);
                            if (!candidate) return null;
                            const isHot = result.flag === "hot";
                            const isGood = result.flag === "good";
                            const flagColor = isHot ? "#B85450" : isGood ? "#E8896A" : "#9CA3AF";
                            const flagBg = isHot ? "#FAEAEA" : isGood ? "rgba(232,137,106,0.08)" : "#F0EDEA";
                            const borderColor = isHot ? "rgba(184,84,80,0.2)" : isGood ? "rgba(232,137,106,0.15)" : "transparent";
                            return (
                              <div key={result.id}
                                style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", background: isHot ? "rgba(184,84,80,0.04)" : "#FAFAF8", borderRadius: 10, cursor: "pointer", border: `1px solid ${borderColor}`, transition: "all 0.15s" }}
                                onClick={() => { setTab("inbox"); setSel(result.id); setActiveBriefFilter(brief.id); }}>
                                {/* Score ring */}
                                <div style={{ width: 48, height: 48, borderRadius: "50%", background: flagBg, border: `2px solid ${flagColor}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 15, color: flagColor, lineHeight: 1 }}>{result.score}</div>
                                  <div style={{ fontSize: 8, color: flagColor, fontFamily: "DM Mono, monospace" }}>/ 100</div>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1C1917" }}>
                                      {candidate.blurred ? blurName(candidate.candidateName) : candidate.candidateName}
                                    </span>
                                    <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: flagBg, color: flagColor }}>
                                      {isHot ? "🔥 Hot" : isGood ? "✓ Good" : "Weak"}
                                    </span>
                                    {candidate.cvFilename && <span style={{ fontSize: 10, color: "#5B6EAF", fontFamily: "DM Mono, monospace" }}>📄 CV</span>}
                                    {candidate.aiAnalysis && <span style={{ fontSize: 10, color: "#C4623E", fontFamily: "DM Mono, monospace" }}>🤖 AI</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5 }}>
                                    {(result.reasons || []).map((r, i) => (
                                      <div key={i} style={{ display: "flex", gap: 5, marginBottom: 1 }}>
                                        <span style={{ color: flagColor, flexShrink: 0 }}>·</span>
                                        <span>{r}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {result.matchBreakdown && (
                                    <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                                      {Object.entries(result.matchBreakdown).map(([k, v]) => (
                                        <div key={k} style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 6, padding: "2px 7px", fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>
                                          {k === "skills" ? "Skills" : k === "location" ? "Location" : k === "seniority" ? "Seniority" : k === "vouchQuality" ? "Vouch" : k === "referrerRep" ? "Referrer" : "Evidence"}: <span style={{ color: v >= 8 ? "#059669" : v >= 5 ? "#E8896A" : "#9CA3AF", fontWeight: 700 }}>{v}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>→</span>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#9CA3AF", padding: "10px 0" }}>
                        Click "Run Match" to auto-score your talent pool against this brief using AI.
                      </div>
                    )}
                  </div>

                  {/* Agent 11 — Market Intel results */}
                  {intelResults[brief.id] && (
                    <div style={{ marginTop: 14, background: "rgba(232,137,106,0.08)", borderRadius: 11, padding: "16px 18px", border: "1px solid rgba(232,137,106,0.15)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                        <span>📊</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#C4623E", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>Agent 11 · Market Intelligence</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
                        {[
                          { l: "Market Demand", v: intelResults[brief.id].marketDemand, icon: "📈" },
                          { l: "Salary Range", v: intelResults[brief.id].salaryRange, icon: "💰" },
                          { l: "Avg Days to Fill", v: intelResults[brief.id].avgDaysToFill + " days", icon: "⏱" },
                        ].map((s, i) => (
                          <div key={i} style={{ background: "#FFFFFF", borderRadius: 9, padding: "10px 12px" }}>
                            <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace", marginBottom: 3 }}>{s.icon} {s.l}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", textTransform: s.l === "Market Demand" ? "capitalize" : "none" }}>{s.v}</div>
                          </div>
                        ))}
                      </div>
                      {intelResults[brief.id].bonusBenchmark && brief.bonus < intelResults[brief.id].bonusBenchmark && (
                        <div style={{ background: "rgba(217,119,6,0.1)", borderRadius: 8, padding: "9px 12px", marginBottom: 10, fontSize: 12, color: "#8B5020" }}>
                          ⚠ Your bonus (${brief.bonus.toLocaleString()}) is below the market benchmark (${intelResults[brief.id].bonusBenchmark.toLocaleString()}). Consider increasing it to attract better referrals.
                        </div>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                        {(intelResults[brief.id].insights || []).map((ins, i) => (
                          <div key={i} style={{ fontSize: 12, color: "#374151", display: "flex", gap: 8 }}>
                            <span style={{ color: "#E8896A", flexShrink: 0 }}>→</span>{ins}
                          </div>
                        ))}
                      </div>
                      {intelResults[brief.id].recommendation && (
                        <div style={{ background: "#FFFFFF", borderRadius: 9, padding: "10px 12px", fontSize: 12, color: "#5B6EAF", fontWeight: 600, borderLeft: "3px solid #D4845A" }}>
                          💡 {intelResults[brief.id].recommendation}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {briefs.length === 0 && (
                <div style={{ background: "#FFFFFF", borderRadius: 12, padding: "40px 24px", textAlign: "center", border: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>💼</div>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No job briefs yet</div>
                  <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>Create a brief and Agent 6 will automatically match your talent pool to it.</div>
                  <button onClick={() => { setEditingBrief({ id: "new", title: "", skills: [], skillsInput: "", seniority: "", location: "", bonus: 2000, status: "active", description: "", positionDescription: "", evp: "", logoUrl: "", pdFilename: "" }); setShowBriefModal(true); }}
                    style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 9, padding: "10px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    + Create First Brief
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "analytics" && (
          <div style={{ padding: "24px 28px" }}>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em", marginBottom: 3 }}>Analytics</h1>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>Performance insights across your referral pipeline.</p>
            </div>

            {/* Key stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
              {[
                { label: "Total Referred", value: referrals.length, color: "#5B6EAF" },
                { label: "Consented", value: referrals.filter(r => r.consentStatus === "consented").length, color: "#E8896A" },
                { label: "Placed", value: referrals.filter(r => r.status === "placed").length, color: "#48BB78" },
                { label: "Avg Vouch Score", value: referrals.length > 0 ? (referrals.reduce((a, r) => a + parseFloat(avg(r)), 0) / referrals.length).toFixed(1) : "—", color: "#6B4FBB" },
              ].map((s, i) => (
                <div key={i} style={{ background: "#FFFFFF", borderRadius: 12, padding: "18px 20px", border: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{s.label}</div>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 28, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Conversion funnel */}
            <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 16 }}>Conversion Funnel</div>
              <div style={{ display: "flex", gap: 0 }}>
                {[
                  { label: "Referred", value: referrals.length, color: "#5B6EAF" },
                  { label: "Consented", value: referrals.filter(r => r.consentStatus === "consented").length, color: "#E8896A" },
                  { label: "Contacted", value: referrals.filter(r => r.status === "contacted" || r.pipelineStatus).length, color: "#C47D2B" },
                  { label: "Placed", value: referrals.filter(r => r.status === "placed").length, color: "#48BB78" },
                ].map((s, i) => {
                  const pct = referrals.length > 0 ? Math.round((s.value / referrals.length) * 100) : 0;
                  return (
                    <div key={i} style={{ flex: 1, padding: "14px 16px", background: `${s.color}10`, borderLeft: i > 0 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
                      <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 26, color: s.color }}>{s.value}</div>
                      <div style={{ height: 4, background: "rgba(0,0,0,0.06)", borderRadius: 2, margin: "8px 0 4px" }}>
                        <div style={{ height: "100%", background: s.color, borderRadius: 2, width: `${pct}%` }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{pct}% of referred</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pipeline breakdown */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", padding: "20px 22px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 14 }}>Pipeline Status</div>
                {[
                  { label: "Shortlisted", color: "#5B6EAF" },
                  { label: "Interviewed", color: "#C47D2B" },
                  { label: "Offer Made", color: "#6B4FBB" },
                  { label: "Hired", color: "#48BB78" },
                ].map(s => {
                  const count = referrals.filter(r => r.pipelineStatus === s.label.toLowerCase().replace(" ", "_") || r.pipelineStatus === s.label.toLowerCase()).length;
                  return (
                    <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "#6B7280", flex: 1 }}>{s.label}</span>
                      <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, color: s.color }}>{count}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", padding: "20px 22px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 14 }}>Top Referrers</div>
                {(() => {
                  const map = {};
                  referrals.forEach(r => {
                    if (!map[r.referrerName]) map[r.referrerName] = { name: r.referrerName, count: 0, placed: 0 };
                    map[r.referrerName].count++;
                    if (r.status === "placed") map[r.referrerName].placed++;
                  });
                  return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 5).map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#1C1917", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                      <span style={{ fontSize: 12, color: "#1C1917", fontWeight: 600, flex: 1 }}>{r.name}</span>
                      <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{r.count} refs · {r.placed} placed</span>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* CSV Export */}
            <button onClick={() => {
              const headers = ["Candidate", "Referrer", "Brief", "Status", "Pipeline", "Consented", "Referred Date"];
              const rows = referrals.map(r => [
                r.candidateName, r.referrerName, r.briefTitle || "", r.status, r.pipelineStatus || "", r.consentStatus, new Date(r.createdAt || "").toLocaleDateString("en-AU")
              ]);
              const csv = [headers, ...rows].map(row => row.map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "peerrefer-referrals.csv"; a.click();
              URL.revokeObjectURL(url);
            }} style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, padding: "10px 18px", fontFamily: "inherit", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#1C1917" }}>
              ⬇️ Export all referrals as CSV
            </button>
          </div>
        )}

        {tab === "settings" && (
          <div style={{ padding: "24px 28px", maxWidth: 680 }}>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em", marginBottom: 3 }}>TA Settings</h1>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>Tell your network what you're looking for and set a bonus to motivate quality referrals.</p>
            </div>

            {/* What I'm Looking For */}
            <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", padding: "28px 28px", marginBottom: 16 }}>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 4 }}>What I'm Looking For</div>
              <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 20, lineHeight: 1.6 }}>Referrers see this before submitting — it ensures you only receive relevant talent.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Roles / skills I hire for</label>
                  <input value={lookingFor} onChange={e => setLookingFor(e.target.value)} placeholder="e.g. Product Managers, Data Analysts, TA professionals, Engineers"
                    style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Industries</label>
                  <input value={industries} onChange={e => setIndustries(e.target.value)} placeholder="e.g. SaaS, Fintech, Healthcare, Tech"
                    style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Seniority level</label>
                    <select value={seniority} onChange={e => setSeniority(e.target.value)}
                      style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, outline: "none", appearance: "none" }}>
                      <option value="">Any level</option>
                      <option>Junior (0-3 years)</option>
                      <option>Mid-level (3-6 years)</option>
                      <option>Senior (6+ years)</option>
                      <option>Lead / Manager</option>
                      <option>Executive / C-Suite</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Location</label>
                    <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Melbourne, Remote, Australia-wide"
                      style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Note to referrers <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
                  <textarea value={referrerNote} onChange={e => setReferrerNote(e.target.value)} placeholder="e.g. I'm always open to hearing about strong PM talent in the Melbourne SaaS space. Culture fit and attitude matter as much as skills for us."
                    style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, outline: "none", minHeight: 80, resize: "vertical" }} />
                </div>
              </div>
            </div>

            {/* TA Candidate Rating */}
            {taProfile?.rating_count > 0 && (
              <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", padding: "24px 28px", marginBottom: 16 }}>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Candidate Rating</div>
                <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>Rated by candidates after placement via PeerRefer.</div>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 48, color: "#E8896A", lineHeight: 1 }}>{Number(taProfile.rating_avg || 0).toFixed(1)}</div>
                    <div style={{ fontSize: 18, marginTop: 4 }}>{"⭐".repeat(Math.round(taProfile.rating_avg || 0))}</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4, fontFamily: "DM Mono, monospace" }}>{taProfile.rating_count} rating{taProfile.rating_count !== 1 ? "s" : ""}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    {[5, 4, 3, 2, 1].map(star => (
                      <div key={star} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                        <span style={{ fontSize: 11, color: "#9CA3AF", width: 8 }}>{star}</span>
                        <div style={{ flex: 1, height: 5, background: "rgba(0,0,0,0.06)", borderRadius: 3 }}>
                          <div style={{ height: "100%", background: "#E8896A", borderRadius: 3, width: star <= Math.round(taProfile.rating_avg || 0) ? "60%" : "10%", opacity: 0.6 }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8 }}>Candidates rate their experience after placement.</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", padding: "24px 28px", marginBottom: 16 }}>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Subscription & Billing</div>
              <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16, lineHeight: 1.6 }}>
                {isProTA ? "You're on the Pro plan ($49/month). Manage your subscription, update your card, or cancel anytime." : "You're on the free plan. Upgrade to Pro for full access to the talent pool and AI agents."}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {isProTA ? (
                  <button onClick={async () => {
                    if (!taProfile?.id) return;
                    const res = await fetch("/.netlify/functions/billing-portal", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ taProfileId: taProfile.id }),
                    });
                    const data = await res.json();
                    if (data.url) window.location.href = data.url;
                    else alert("Could not open billing portal. Please try again.");
                  }} style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 9, padding: "10px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    💳 Manage Billing & Cancel →
                  </button>
                ) : (
                  <button onClick={() => setShowUpgrade(true)}
                    style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    Upgrade to Pro — Start Free Trial →
                  </button>
                )}
              </div>
              {isProTA && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10 }}>Cancel anytime — no lock-in. You'll retain Pro access until the end of your billing period.</div>}
            </div>

            <div style={{ background: "rgba(232,137,106,0.08)", borderRadius: 12, padding: "14px 16px", marginBottom: 16, border: "1px solid rgba(232,137,106,0.15)" }}>
              <div style={{ fontSize: 13, color: "#C4623E", lineHeight: 1.6 }}>
                💡 <strong>Bonuses are set per role</strong> — go to the <strong>💼 Jobs tab</strong> to set a bonus amount for each brief. Referrers see the bonus when selecting which role to refer for.
              </div>
            </div>

            {/* Invite Referrers */}
            <InviteReferrersPanel taProfile={taProfile} user={user} />


            <button onClick={async () => {
                // Save all settings to Supabase
                console.log("Saving settings, taProfile:", taProfile?.id, "lookingFor:", lookingFor);
                if (taProfile && taProfile.id) {
                  const updateData = {
                    looking_for: lookingFor,
                    industries: industries,
                    seniority: seniority,
                    location: location,
                    referrer_note: referrerNote,
                    bonus_enabled: bonusEnabled,
                    bonus_amount: bonusAmount,
                    bonus_trigger: bonusTrigger,
                  };
                  console.log("Updating with:", updateData);
                  const { error } = await supabase
                    .from("ta_profiles")
                    .update(updateData)
                    .eq("id", taProfile.id);
                  console.log("Save error:", error);
                  if (error) {
                    alert("Save failed: " + error.message);
                  } else {
                    setTaPrefs({ name: taProfile.name || user.name, lookingFor, industries, seniority, location, referrerNote });
                    alert("✓ Settings saved! Referrers will now see your updated preferences.");
                  }
                } else {
                  console.log("No taProfile available:", taProfile);
                  alert("Profile not loaded — try refreshing the page.");
                }
              }}
              style={{ background: "#E8896A", color: "#fff", border: "none", borderRadius: 10, padding: "13px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Save Settings ✓
            </button>
          </div>
        )}
      </div>


      {/* Agent 10 — Referrer Recruiter placement modal */}
      {showPlacementModal && placementMessage && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}>
          <div style={{ background: "#FFFFFF", borderRadius: 20, padding: "32px 28px", width: "100%", maxWidth: 500, boxShadow: "0 20px 60px rgba(0,0,0,0.12)", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 16 }}>🎉</div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 22, color: "#1C1917", marginBottom: 8, textAlign: "center" }}>Placement confirmed!</div>
            <div style={{ fontSize: 12, color: "#E8896A", fontFamily: "DM Mono, monospace", fontWeight: 600, textAlign: "center", marginBottom: 20 }}>🤖 Agent 10 · Referrer Recruiter drafted this</div>
            <div style={{ background: "#FAFAF8", borderRadius: 12, padding: "16px 18px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", marginBottom: 6, fontFamily: "DM Mono, monospace" }}>SUBJECT</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1C1917", marginBottom: 14 }}>{placementMessage.subject}</div>
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-line" }}>{placementMessage.message}</div>
            </div>
            {placementMessage.suggestedBrief && (
              <div style={{ background: "rgba(232,137,106,0.1)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 12, color: "#C4623E" }}>
                💡 Suggested next referral: <strong>{placementMessage.suggestedBrief}</strong>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { navigator.clipboard?.writeText(placementMessage.message).catch(() => {}); }}
                style={{ flex: 1, background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#5B6EAF" }}>
                📋 Copy Message
              </button>
              <button onClick={() => { setShowPlacementModal(false); setPlacementMessage(null); }}
                style={{ flex: 1, background: "#5B6EAF", color: "#FFFFFF", border: "none", borderRadius: 10, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Done ✓
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Milestone contextual upgrade toast */}
      {milestone && !isProTA && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 9999, width: "calc(100% - 32px)", maxWidth: 420,
          background: "#1A0D12", border: "1px solid rgba(216,90,48,0.3)",
          borderRadius: 16, padding: "20px 22px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(216,90,48,0.1)",
          animation: "slideUpSheet 0.4s cubic-bezier(0.16,1,0.3,1) forwards",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <button onClick={() => setMilestone(null)} style={{
            position: "absolute", top: 12, right: 14,
            background: "none", border: "none", color: "rgba(255,255,255,0.3)",
            fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 0,
          }}>×</button>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{milestone.emoji}</div>
            <div>
              <div style={{
                fontSize: 15, fontWeight: 700, color: "#FFFFFF",
                letterSpacing: "-0.02em", marginBottom: 5,
                fontFamily: "Playfair Display, Georgia, serif",
              }}>{milestone.title}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
                {milestone.body}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setMilestone(null); setShowUpgrade(true); }} style={{
              flex: 1, background: "#D85A30", border: "none", borderRadius: 9,
              padding: "11px 16px", fontSize: 13, fontWeight: 700, color: "#FFFFFF",
              cursor: "pointer", fontFamily: "inherit", letterSpacing: "-0.01em",
              transition: "transform 0.15s ease",
            }}
            onMouseEnter={e => e.target.style.transform="translateY(-1px)"}
            onMouseLeave={e => e.target.style.transform=""}>
              {milestone.cta}
            </button>
            <button onClick={() => setMilestone(null)} style={{
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 9, padding: "11px 14px", fontSize: 12, color: "rgba(255,255,255,0.4)",
              cursor: "pointer", fontFamily: "inherit",
            }}>
              Later
            </button>
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", textAlign: "center" }}>
            A$49/month · 30-day free trial · Cancel anytime
          </div>
        </div>
      )}

      {showUpgrade && (
        <UpgradeModal
          onClose={() => setShowUpgrade(false)}
          user={user}
          taProfile={taProfile}
          onUpgrade={() => {
            setIsProTA(true);
            setShowUpgrade(false);
            alert("🎉 Pro activated (demo mode)! All candidate details are now revealed.");
          }}
        />
      )}
      {sharingBrief && (
        <ShareModal
          brief={sharingBrief === "general" ? null : sharingBrief}
          taProfile={taProfile || { slug: user.slug }}
          onClose={() => setSharingBrief(null)}
        />
      )}
      {showBriefModal && editingBrief && (
        <BriefModal
          brief={editingBrief}
          onClose={() => { setShowBriefModal(false); setEditingBrief(null); }}
          onSave={async saved => {
            if (editingBrief.id !== "new") {
              // Update existing brief in Supabase
              await supabase.from("briefs").update({
                title: saved.title, skills: saved.skills, seniority: saved.seniority,
                location: saved.location, bonus: saved.bonus, description: saved.description, status: saved.status,
              }).eq("id", saved.id);
              setBriefs(prev => prev.map(b => b.id === saved.id ? { ...saved, matchResults: b.matchResults || [] } : b));
            } else {
              // Insert new brief to Supabase
              const { data: newBrief } = await supabase.from("briefs").insert({
                ta_profile_id: taProfile?.id,
                title: saved.title, skills: saved.skills, seniority: saved.seniority,
                location: saved.location, bonus: saved.bonus, description: saved.description, status: saved.status,
                position_description: saved.positionDescription || null,
                evp: saved.evp || null,
                logo_url: saved.logoUrl || null,
                pd_filename: saved.pdFilename || null,
              }).select().single();
              if (newBrief) setBriefs(prev => [...prev, { ...newBrief, matchResults: [] }]);
            }
            setShowBriefModal(false);
            setEditingBrief(null);
          }}
        />
      )}

    </div>
  );
}

// ============================================
// MOBILE HOOK
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ============================================
// PIPELINE LINKS — TA one-click update widget
// ============================================
function PipelineLinks({ referralId, pipelineStatus }) {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/get-pipeline-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralId }),
      });
      const data = await res.json();
      if (data.links) setLinks(data.links);
    } catch (e) { console.error("Pipeline links error:", e); }
    setLoading(false);
  };

  const steps = [
    { status: "shortlisted", label: "Shortlisted", emoji: "📋" },
    { status: "interviewed", label: "Interviewed", emoji: "🤝" },
    { status: "offer_made", label: "Offer Made", emoji: "📄" },
    { status: "hired", label: "Hired", emoji: "🎉" },
  ];

  return (
    <div style={{ background: "#F8F5F2", borderRadius: 10, padding: "13px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, fontFamily: "DM Mono, monospace" }}>Send TA a status update link</div>
      <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10, lineHeight: 1.5 }}>One click for the TA — no login needed. Copy the link and paste it into an email or message.</div>
      {!links ? (
        <button onClick={fetchLinks} disabled={loading}
          style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: loading ? 0.7 : 1 }}>
          {loading ? <><Spin color="#fff" /> Generating links...</> : "🔗 Generate Update Links"}
        </button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {steps.map(({ status, label, emoji }) => {
            const active = pipelineStatus === status;
            const url = links[status];
            return (
              <button key={status} onClick={() => {
                navigator.clipboard?.writeText(url).catch(() => {});
                setCopied(status);
                setTimeout(() => setCopied(null), 2000);
              }}
                style={{ display: "flex", alignItems: "center", gap: 8, background: active ? "rgba(232,137,106,0.12)" : "#FFFFFF", border: active ? "1.5px solid rgba(232,137,106,0.3)" : "1px solid rgba(0,0,0,0.08)", borderRadius: 8, padding: "8px 11px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", color: active ? "#C4623E" : "#374151", textAlign: "left" }}>
                <span>{emoji}</span>
                <span style={{ flex: 1 }}>{label}</span>
                <span style={{ fontSize: 10, fontFamily: "DM Mono, monospace", color: copied === status ? "#059669" : active ? "#E8896A" : "#9CA3AF" }}>
                  {copied === status ? "COPIED ✓" : active ? "CURRENT" : "COPY LINK"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// REFERRER DASHBOARD
// ============================================
// ============================================
// REFERRER INVITE FLOW
function ReferrerInviteFlow({ user }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!email.trim().includes("@")) { alert("Please enter a valid email."); return; }
    setSending(true);
    await fetch("/.netlify/functions/invite-referrers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        taName: user.name,
        taCompany: "",
        taSlug: "",
        emails: [email.trim()],
        personalNote: `${user.name} thinks you'd be a great referrer on PeerRefer. Refer people you know and earn bonuses when they're placed!`,
      }),
    });
    setSending(false);
    setSent(true);
    setEmail("");
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input value={email} onChange={e => setEmail(e.target.value)}
        onKeyDown={e => e.key === "Enter" && send()}
        placeholder="friend@email.com"
        style={{ flex: 1, border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 8, padding: "8px 12px", fontFamily: "inherit", fontSize: 13, outline: "none" }} />
      <button onClick={send} disabled={sending}
        style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
        {sent ? "✓ Sent!" : sending ? "Sending..." : "Send Invite →"}
      </button>
    </div>
  );
}

function ReferrerDashboard({ user, onLogout, onAddToPool, sharedBriefs, taPrefs, preselectBriefId, hasBothPortals }) {
  const isMobile = useIsMobile();
  const [sel, setSel] = useState("jr1");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("referrals");
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [msgCopied, setMsgCopied] = useState(false);
  const [myRefs, setMyRefs] = useState([]);
  const [sharingRole, setSharingRole] = useState(null);
  const [loadedTaPrefs, setLoadedTaPrefs] = useState(null);
  const [localBriefs, setLocalBriefs] = useState([]);
  const [taBonus, setTaBonus] = useState({ enabled: false, amount: 0 });
  const [referrerRefs, setReferrerRefs] = useState([]);
  const [referAgain, setReferAgain] = useState(null); // candidate to re-refer
  const [shareMessage, setShareMessage] = useState(null); // post-referral share nudge

  // Auto-open refer modal if coming from public job page
  useEffect(() => {
    if (preselectBriefId && sharedBriefs?.length > 0) {
      setShowModal(true);
    }
  }, [preselectBriefId, sharedBriefs]); // eslint-disable-line

  // Request push permission on first referrer login
  useEffect(() => {
    const asked = localStorage.getItem("peerrefer_push_asked");
    if (!asked && "Notification" in window && Notification.permission === "default") {
      setTimeout(() => {
        requestPushPermission().then(granted => {
          localStorage.setItem("peerrefer_push_asked", "true");
        });
      }, 3000);
    }
  }, []); // eslint-disable-line

  // Passport state
  const [passport, setPassport] = useState({
    workRights: "", salaryMin: "", salaryMax: "",
    industries: "", preferredLocations: "", careerGoals: "",
    certifications: "", profileSummary: "", introText: "",
  });
  const [passportSaving, setPassportSaving] = useState(false);
  const [passportSaved, setPassportSaved] = useState(false);
  const [passportLoaded, setPassportLoaded] = useState(false);

  useEffect(() => {
    // Load TA profile - bonus + preferences + briefs
    supabase.from("ta_profiles").select("id, name, bonus_enabled, bonus_amount, looking_for, industries, seniority, location, referrer_note").order("created_at", { ascending: false }).limit(1).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setTaBonus({ enabled: data.bonus_enabled, amount: data.bonus_amount });
          setLoadedTaPrefs({
            name: data.name || "Mia Thorne",
            lookingFor: data.looking_for || "Product Managers, Data Analysts, TA professionals",
            industries: data.industries || "SaaS, Tech, Fintech",
            seniority: data.seniority || "Mid to Senior level",
            location: data.location || "Melbourne / Remote",
            referrerNote: data.referrer_note || "I'm always open to strong talent in the Melbourne tech space. Culture fit matters as much as skills.",
          });
          // Load ALL active briefs from all TAs
          supabase.from("briefs").select("*, ta_profiles(name, company, slug)").eq("status", "active").order("created_at", { ascending: false })
            .then(({ data: briefs }) => {
              if (briefs) setLocalBriefs(briefs.map(b => ({ ...b, matchResults: [] })));
            });
        }
      });
    // Load referrer's real referrals from Supabase
    const loadReferrerData = async () => {
      if (!user.email) return;
      try {
        // Find referrer by email
        const { data: referrer } = await supabase
          .from("referrers")
          .select("id, reputation_score, total_referred, total_contacted, total_placed, pending_bonus, paid_bonus")
          .eq("email", user.email)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!referrer) return;

        // Load all referrals by this referrer
        const { data: refs } = await supabase
          .from("referrals")
          .select("*, ta_profiles(name, company, slug)")
          .or(`referrer_id.eq.${referrer.id}`)
          .order("created_at", { ascending: false });

        if (refs && refs.length > 0) {
          const mapped = refs.map(r => {
            const ps = r.pipeline_status || "referred";
            const consentDeclined = r.consented === false;
            const consentGiven = r.consented === true;
            const fmt = d => d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—";
            const pipelineReached = s => {
              const order = ["referred", "email_sent", "consented", "shortlisted", "interviewed", "offer_made", "hired"];
              return order.indexOf(ps) >= order.indexOf(s);
            };
            const timeline = [
              { e: "Referred", d: fmt(r.created_at), done: true },
              { e: "Consent email sent", d: fmt(r.created_at), done: true },
              consentDeclined
                ? { e: "Consent declined", d: fmt(r.consented_at), done: true, declined: true }
                : { e: "Candidate consented", d: consentGiven ? fmt(r.consented_at) : "—", done: consentGiven },
              { e: "Shortlisted", d: pipelineReached("shortlisted") ? fmt(r.pipeline_updated_at) : "—", done: pipelineReached("shortlisted") },
              { e: "Interviewed", d: pipelineReached("interviewed") ? fmt(r.pipeline_updated_at) : "—", done: pipelineReached("interviewed") },
              { e: "Offer Made", d: pipelineReached("offer_made") ? fmt(r.pipeline_updated_at) : "—", done: pipelineReached("offer_made") },
              { e: "Hired 🎉", d: pipelineReached("hired") ? fmt(r.pipeline_updated_at) : "—", done: pipelineReached("hired") },
            ];
            return {
              id: r.id,
              candidateName: r.candidate_name,
              candidateEmail: r.candidate_email,
              initials: inits(r.candidate_name),
              skills: r.skills || [],
              referrerRelationship: r.referrer_relationship || "",
              skillsRating: r.skills_rating || 5,
              workEthicRating: r.work_ethic_rating || 5,
              coachabilityRating: r.coachability_rating || 5,
              brief_id: r.brief_id,
              briefId: r.brief_id,
              referredTo: (r.ta_profiles?.name || "TA") + " · " + (r.ta_profiles?.company || "PeerRefer"),
              submittedAt: new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }),
              status: r.status === "pending_consent" ? "new" : r.status,
              consentStatus: consentGiven ? "consented" : consentDeclined ? "declined" : "pending",
              pipelineStatus: ps,
              bonusOffered: 0,
              bonusPaid: false,
              note: r.note,
              briefTitle: r.brief_title,
              timeline,
            };
          });
          setReferrerRefs(mapped);
        }
      } catch (err) {
        console.error("Load referrer refs error:", err);
      }
    };
    loadReferrerData();

    // Auto-open refer modal if coming from public profile page
    if (localStorage.getItem("peerrefer_open_refer")) {
      localStorage.removeItem("peerrefer_open_refer");
      setTimeout(() => setShowModal(true), 800);
    }

    // Real-time subscription — update when TA moves candidate in pipeline
    const channel = supabase
      .channel("referrer-referrals-live")
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "referrals",
      }, () => {
        loadReferrerData(); // Reload all referrals on any update
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]); // eslint-disable-line

  // Load passport data
  useEffect(() => {
    if (!user?.email || passportLoaded) return;
    const loadPassport = async () => {
      const { data } = await supabase.from("referrers").select("work_rights, salary_min, salary_max, industries, preferred_locations, career_goals, certifications, profile_summary, intro_text").eq("email", user.email).maybeSingle();
      if (data) {
        setPassport({
          workRights: data.work_rights || "",
          salaryMin: data.salary_min || "",
          salaryMax: data.salary_max || "",
          industries: Array.isArray(data.industries) ? data.industries.join(", ") : (data.industries || ""),
          preferredLocations: Array.isArray(data.preferred_locations) ? data.preferred_locations.join(", ") : (data.preferred_locations || ""),
          careerGoals: data.career_goals || "",
          certifications: data.certifications || "",
          profileSummary: data.profile_summary || "",
          introText: data.intro_text || "",
        });
      }
      setPassportLoaded(true);
    };
    loadPassport();
  }, [user, passportLoaded]); // eslint-disable-line

  const savePassport = async () => {
    setPassportSaving(true);
    await supabase.from("referrers").update({
      work_rights: passport.workRights,
      salary_min: passport.salaryMin ? parseInt(passport.salaryMin) : null,
      salary_max: passport.salaryMax ? parseInt(passport.salaryMax) : null,
      industries: passport.industries ? passport.industries.split(",").map(s => s.trim()).filter(Boolean) : [],
      preferred_locations: passport.preferredLocations ? passport.preferredLocations.split(",").map(s => s.trim()).filter(Boolean) : [],
      career_goals: passport.careerGoals,
      certifications: passport.certifications,
      profile_summary: passport.profileSummary,
      intro_text: passport.introText,
      passport_complete: !!(passport.workRights || passport.profileSummary || passport.careerGoals || passport.industries),
    }).eq("email", user.email);
    setPassportSaving(false);
    setPassportSaved(true);
    setTimeout(() => setPassportSaved(false), 3000);
  };
  const selRef = (referrerRefs.length > 0 ? referrerRefs : myRefs).find(r => r.id === sel);
  const displayRefs = referrerRefs;
  const filtered = filter === "all" ? displayRefs : displayRefs.filter(r => r.status === filter);

  const handleSubmit = data => {
    const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    const newRef = {
      id: "jr" + Date.now(),
      candidateName: data.candidateName,
      initials: inits(data.candidateName),
      skills: data.skills,
      referredTo: "Talent Pool · PeerRefer",
      submittedAt: today,
      status: "new",
      consentStatus: "pending",
      bonusOffered: 0,
      bonusPaid: false,
      note: data.note,
      timeline: [
        { e: "Referred", d: today, done: true },
        { e: "Consent email sent", d: today, done: true },
        { e: "Candidate consented", d: "—", done: false },
        { e: "Shortlisted", d: "—", done: false },
        { e: "Interviewed", d: "—", done: false },
        { e: "Offer Made", d: "—", done: false },
        { e: "Hired 🎉", d: "—", done: false },
      ],
    };
    setMyRefs(prev => [newRef, ...prev]);

    // Save to Supabase
    const saveToSupabase = async () => {
      try {
        // Get TA from the selected brief, or fall back to mia-thorne
        let taData = null;
        if (data.briefId) {
          const { data: brief } = await supabase.from("briefs").select("ta_profile_id").eq("id", data.briefId).single();
          if (brief) {
            const { data: ta } = await supabase.from("ta_profiles").select("id, name, company").eq("id", brief.ta_profile_id).single();
            taData = ta;
          }
        }
        if (!taData) {
          // Fall back to the TA whose link was used (from taPrefs slug), or first available TA
          const slug = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("refer") : null;
          if (slug) {
            const { data: ta } = await supabase.from("ta_profiles").select("id, name, company").eq("slug", slug).maybeSingle();
            taData = ta;
          }
          if (!taData) {
            const { data: ta } = await supabase.from("ta_profiles").select("id, name, company").limit(1).maybeSingle();
            taData = ta;
          }
        }
        console.log("taData found:", taData?.id, "briefId:", data.briefId);

        if (!taData) {
          console.error("NO TADATA FOUND - cannot save referral or send consent email");
          return;
        }
        if (true) {
          let referrerId;
          // Always look up existing referrer by email first to avoid duplicates
          if (user.email) {
            const { data: existing } = await supabase
              .from("referrers")
              .select("id, total_referred")
              .eq("email", user.email)
              .maybeSingle();
            if (existing) {
              referrerId = existing.id;
              await supabase.from("referrers")
                .update({ total_referred: (existing.total_referred || 0) + 1 })
                .eq("id", existing.id);
            } else {
              // Create new referrer with email so future lookups work
              const { data: newReferrer } = await supabase
                .from("referrers")
                .insert({ name: user.name, email: user.email, total_referred: 1 })
                .select()
                .single();
              if (newReferrer) referrerId = newReferrer.id;
            }
          } else {
            // No email - create anonymous referrer
            const { data: newReferrer } = await supabase
              .from("referrers")
              .insert({ name: user.name, email: null, total_referred: 1 })
              .select()
              .single();
            if (newReferrer) referrerId = newReferrer.id;
          }
          const { data: savedRef } = await supabase
            .from("referrals")
            .insert({
              ta_profile_id: taData.id,
              referrer_id: referrerId,
              candidate_name: data.candidateName,
              candidate_email: data.candidateEmail || null,
              candidate_linkedin: data.candidateLinkedin || null,
              skills: data.skills,
              note: data.note,
              referrer_relationship: data.referrerRelationship || null,
              referrer_duration: data.referrerDuration || null,
              referrer_standout: data.referrerStandout || null,
              referrer_work_again: data.referrerWorkAgain || null,
              skills_rating: data.skillsRating,
              work_ethic_rating: data.workEthicRating,
              coachability_rating: data.coachabilityRating,
              status: "pending_consent",
              consented: null,
              blurred: true,
              brief_id: data.briefId || null,
              brief_title: data.briefTitle || null,
            })
            .select()
            .single();
          console.log("savedRef:", savedRef, "candidateEmail:", data.candidateEmail);
          if (savedRef && onAddToPool) {
            onAddToPool({
              id: savedRef.id, candidateName: data.candidateName,
              candidateEmail: data.candidateEmail, skills: data.skills,
              note: data.note, referrerName: user.name,
              referrerRelationship: "Referrer",
              referrerStats: { total: 1, placed: 0 },
              skillsRating: data.skillsRating, workEthicRating: data.workEthicRating,
              coachabilityRating: data.coachabilityRating,
              submittedAt: "Just now", status: "new",
              aiAnalysis: null, draftEmail: null, blurred: true,
            });
          }
          console.log("Attempting consent email send:", { savedRef: !!savedRef, email: data.candidateEmail });
          if (savedRef && data.candidateEmail) {
            try {
              const emailRes = await fetch("/.netlify/functions/send-consent", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  candidateName: data.candidateName,
                  candidateEmail: data.candidateEmail,
                  referrerName: user.name,
                  taName: taData?.name || "the recruiter",
                  taCompany: taData?.company || "",
                  referralId: savedRef.id,
                  briefTitle: data.briefTitle || null,
                }),
              });
              const emailResult = await emailRes.json();
              console.log("Consent email result:", emailResult);
            } catch (emailErr) {
              console.error("Consent email error:", emailErr);
            }
          }
        }
      } catch (err) { console.error("Supabase save error:", err); }
    };
    saveToSupabase();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: "Inter, DM Sans, system-ui, sans-serif", color: "#1C1917" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600&family=Inter:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.99); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes slideUpSheet { from { transform:translateY(100%); } to { transform:translateY(0); } }
        .card-enter { animation: fadeUp 0.3s ease forwards; }
        .modal-enter { animation: slideUp 0.3s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        .sheet-enter { animation: slideUpSheet 0.35s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 3px; }
      `}</style>

      <div style={{ background: "linear-gradient(135deg, #4A1828 0%, #6B2D3E 60%, #8B3A50 100%)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "0 14px" : "0 28px", height: 56 }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 30, height: 30, background: "rgba(0,0,0,0.1)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 15, color: "#fff", letterSpacing: "-0.02em" }}>PeerRefer</div>
                {!isMobile && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", fontFamily: "DM Mono, monospace" }}>Referrer Portal · CollabPathways</div>}
              </div>
            </div>
            {!isMobile && <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: "rgba(0,0,0,0.1)", borderRadius: 20, border: "1px solid rgba(0,0,0,0.12)" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#C4623E" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#C4623E", fontFamily: "DM Mono, monospace" }}>YOUR REFERRALS ONLY</span>
            </div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            {!isMobile && (
              <button onClick={() => window.open(`/?referrer=${user.id}`, "_blank")}
                style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(216,90,48,0.1)", border: "1px solid rgba(216,90,48,0.2)", borderRadius: 7, padding: "5px 10px", color: "#E8896A", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                👤 My Profile
              </button>
            )}
            {user.linkedinVerified ? (
              <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(10,102,194,0.15)", border: "1px solid rgba(10,102,194,0.3)", borderRadius: 20, padding: "4px 10px" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#0A66C2", fontFamily: "DM Mono, monospace" }}>VERIFIED</span>
              </div>
            ) : (
              <button onClick={() => window.location.href = `/.netlify/functions/linkedin-auth?referrerId=${user.id || ""}`}
                style={{ display: "flex", alignItems: "center", gap: 5, background: "#0A66C2", color: "#fff", border: "none", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                {isMobile ? "Verify" : "Verify LinkedIn"}
              </button>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowModal(true)}
                style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "7px 14px", fontFamily: "inherit", fontWeight: 700, fontSize: isMobile ? 12 : 13, cursor: "pointer" }}>
                + Refer
              </button>
            </div>
            {!isMobile && <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{user.name}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontFamily: "DM Mono, monospace" }}>Trusted Referrer</div>
            </div>}
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.12)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 13 }}>{user.initials}</div>
            {hasBothPortals && <button onClick={async () => {
              localStorage.setItem("peerrefer_portal", "ta");
              localStorage.setItem("peerrefer_user_email", user?.email || "");
              try { await supabase.auth.updateUser({ data: { preferred_portal: "ta" } }); } catch(e) {}
              window.location.replace("/?portal=ta");
            }} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "rgba(255,255,255,0.8)", marginRight: 6 }}>TA Portal</button>}
            <button onClick={onLogout} style={{ background: "rgba(0,0,0,0.1)", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 7, padding: "5px 10px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", color: "rgba(255,255,255,0.7)" }}>Out</button>
          </div>
        </div>

        <div style={{ padding: "16px 28px 0", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", fontFamily: "DM Mono, monospace", marginBottom: 5 }}>
              <TierBadge totalPlaced={user.totalPlaced} size="sm" />
            </div>
            <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 26, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.1, marginBottom: 5 }}>Your Referral<br />Dashboard</h1>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", maxWidth: 280, lineHeight: 1.5 }}>Refer great people. Track what happens. Earn bonuses when they're placed.</p>
          </div>
          <RepRing score={user.reputationScore} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, background: "rgba(255,255,255,0.07)", marginTop: 20, borderRadius: "10px 10px 0 0", overflow: "hidden" }}>
          {[{ l: "Referred", v: user.totalReferred }, { l: "Consented", v: user.totalConsented }, { l: "Contacted", v: user.totalContacted }, { l: "Placed", v: user.totalPlaced }].map((s, i) => (
            <div key={i} style={{ padding: "12px 14px", background: "rgba(0,0,0,0.06)", textAlign: "center" }}>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 20, color: "#fff", marginBottom: 1 }}>{s.v}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", fontFamily: "DM Mono, monospace" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", background: "#FFFFFF" }}>
        <div style={{ padding: isMobile ? "14px 14px" : "20px 28px", paddingBottom: isMobile ? 80 : 20 }}>
          {/* Tab switcher */}
          <div style={{ display: "flex", gap: 4, background: "rgba(0,0,0,0.04)", borderRadius: 10, padding: 3, marginBottom: 20, width: "fit-content" }}>
            {[["referrals", "📋 Referrals"], ["reputation", "🏆 Reputation"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ background: tab === k ? "#fff" : "transparent", border: "none", borderRadius: 7, padding: isMobile ? "6px 10px" : "6px 14px", fontFamily: "inherit", fontSize: isMobile ? 11 : 12, fontWeight: 700, color: tab === k ? "#1A1612" : "#9CA3AF", cursor: "pointer", boxShadow: tab === k ? "0 1px 3px rgba(26,22,18,0.1)" : "none", whiteSpace: "nowrap" }}>{l}</button>
            ))}
          </div>
          {user.pendingBonus > 0 && (
            <div style={{ background: "rgba(217,119,6,0.08)", borderLeft: "4px solid #B06820", borderRadius: 11, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: "#1C1917" }}>💰 ${user.pendingBonus.toLocaleString()} bonus pending</div>
                <div style={{ fontSize: 11, color: "#6B7280" }}>Confirmed when placement is made via PeerRefer.</div>
              </div>
              {user.paidBonus > 0 && <div style={{ background: "#D97706", color: "#fff", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 700, marginLeft: 12 }}>${user.paidBonus.toLocaleString()} earned</div>}
            </div>
          )}

          {tab === "referrals" && (
            <div>
              <div style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>🔒</span>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>You can only see referrals you submitted. TA inbox, AI analysis, and other referrals are not visible from this portal.</div>
              </div>

              {sharedBriefs && sharedBriefs.filter(b => b.status === "active").length > 0 && (
                <div style={{ background: "rgba(232,137,106,0.08)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, border: "1px solid rgba(232,137,106,0.15)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#C4623E", marginBottom: 8 }}>📤 Share these roles with your network</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {sharedBriefs.filter(b => b.status === "active").sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).map(brief => (
                      <div key={brief.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", borderRadius: 8, padding: "9px 12px" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{brief.title}</div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{brief.location || "Melbourne"}</div>
                        </div>
                        <button onClick={() => setSharingRole(brief)}
                          style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          ↗ Share
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 5, marginBottom: 14 }}>
                {["all", "contacted", "placed", "new"].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{ background: filter === f ? "#1A1612" : "transparent", color: filter === f ? "#FAFAF8" : "#9CA3AF", border: "1.5px solid", borderColor: filter === f ? "#E8896A" : "rgba(0,0,0,0.08)", borderRadius: 7, padding: "4px 11px", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    {f === "all" ? "All" : (STATUS_REF[f]?.label || f)}
                  </button>
                ))}
              </div>

              <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: "1fr 350px", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {filtered.length === 0 && (
                    <div style={{ background: "#FFFFFF", borderRadius: 11, padding: "32px", textAlign: "center", border: "1px solid rgba(0,0,0,0.06)" }}>
                      <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
                      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No referrals yet</div>
                      <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>Click "+ Refer Someone" to add your first referral to the talent pool.</div>
                      <button onClick={() => setShowModal(true)} style={{ background: "#E8896A", color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>+ Refer Someone Now</button>
                    </div>
                  )}
                  {(() => {
                    // Group by candidate — one card per person, role pills inside
                    const groups = [];
                    const seen = new Set();
                    filtered.forEach(r => {
                      const key = r.candidateEmail || r.candidateName;
                      if (!seen.has(key)) {
                        seen.add(key);
                        const allForCandidate = filtered.filter(x => (x.candidateEmail || x.candidateName) === key);
                        groups.push({ key, referrals: allForCandidate });
                      }
                    });
                    return groups.map(group => {
                      const activeRef = group.referrals.find(x => x.id === sel) || group.referrals[0];
                      const r = activeRef;
                      const isSelected = group.referrals.some(x => x.id === sel);
                      return (
                        <div key={group.key}
                          style={{ background: "#FFFFFF", borderRadius: 11, border: "1.5px solid", borderColor: isSelected ? "#E8896A" : "rgba(0,0,0,0.06)", transition: "all 0.15s" }}>

                          {/* Candidate info row */}
                          <div onClick={() => setSel(r.id)} style={{ padding: "13px 16px", cursor: "pointer" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ display: "flex", gap: 10 }}>
                                <div style={{ position: "relative", flexShrink: 0 }}>
                                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 13 }}>{r.initials}</div>
                                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 10, height: 10, borderRadius: "50%", background: STATUS_REF[r.status]?.dot || "#ccc", border: "2px solid #FAF8F4" }} />
                                </div>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{r.candidateName}</div>
                                  <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 5 }}>→ {r.referredTo}</div>
                                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{r.skills.map(s => <Tag key={s}>{s}</Tag>)}</div>
                                </div>
                              </div>
                              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                                <div style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: STATUS_REF[r.status]?.bg, color: STATUS_REF[r.status]?.color, marginBottom: 3, whiteSpace: "nowrap" }}>{STATUS_REF[r.status]?.label}</div>
                                <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{r.submittedAt}</div>
                                {r.bonusOffered > 0 && <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: r.bonusPaid ? "#E8896A" : "#C47D2B" }}>{r.bonusPaid ? "✓ $" + r.bonusOffered.toLocaleString() + " paid" : "$" + r.bonusOffered.toLocaleString() + " if placed"}</div>}
                              </div>
                            </div>

                            {/* Progress bar for active role */}
                            <div style={{ marginTop: 10 }}>
                              <div style={{ display: "flex", gap: 3 }}>{r.timeline.map((s, i) => <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: s.declined ? "#EF4444" : s.done ? "#E8896A" : "rgba(0,0,0,0.07)" }} />)}</div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                                <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>{r.timeline.filter(s => s.done).length}/{r.timeline.length} steps</div>
                                {r.pipelineStatus && r.pipelineStatus !== "referred" && (
                                  <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", color: r.pipelineStatus === "hired" ? "#6B4FBB" : "#E8896A", background: r.pipelineStatus === "hired" ? "#F0EDFB" : "rgba(232,137,106,0.1)", padding: "2px 7px", borderRadius: 20 }}>
                                    {r.pipelineStatus === "shortlisted" ? "Shortlisted" : r.pipelineStatus === "interviewed" ? "Interviewed" : r.pipelineStatus === "offer_made" ? "Offer Made" : r.pipelineStatus === "hired" ? "Hired 🎉" : ""}
                                  </div>
                                )}
                                {r.consentStatus === "declined" && (
                                  <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", color: "#EF4444", background: "rgba(239,68,68,0.08)", padding: "2px 7px", borderRadius: 20 }}>Declined</div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Role pills */}
                          <div style={{ padding: "8px 14px 12px", borderTop: "1px solid rgba(0,0,0,0.05)", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            {group.referrals.map(ref => {
                              const isActive = ref.id === sel || (!sel && ref.id === group.referrals[0].id && isSelected);
                              const dotColor = ref.consentStatus === "consented" ? "#059669" : ref.consentStatus === "declined" ? "#EF4444" : "#F59E0B";
                              return (
                                <button key={ref.id}
                                  onClick={(e) => { e.stopPropagation(); setSel(ref.id); }}
                                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, border: "1.5px solid", borderColor: isActive ? "#D85A30" : "rgba(0,0,0,0.1)", background: isActive ? "#D85A30" : "#F9F7F5", color: isActive ? "#FFFFFF" : "#6B7280", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                                  📋 {ref.briefTitle || "General"}
                                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: isActive ? "rgba(255,255,255,0.7)" : dotColor, flexShrink: 0 }} />
                                </button>
                              );
                            })}
                            <button onClick={(e) => { e.stopPropagation(); setReferAgain(activeRef); }}
                              style={{ fontSize: 10, color: "#5B6EAF", fontWeight: 700, background: "rgba(91,110,175,0.08)", border: "1px solid rgba(91,110,175,0.15)", borderRadius: 20, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>
                              + Another role
                            </button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {selRef && !isMobile && (
                  <div style={{ position: "sticky", top: 20, height: "fit-content" }}>
                    <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", boxShadow: "0 0 0 1px rgba(0,0,0,0.07)" }}>
                      <div style={{ padding: "16px 18px", background: "linear-gradient(135deg, #4A1828, #8B3A50)", color: "#fff" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{selRef.initials}</div>
                          <div>
                            {selRef.briefTitle && <div style={{ fontSize: 10, fontWeight: 700, color: "#E8896A", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "DM Mono, monospace" }}>{selRef.briefTitle}</div>}
                            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15 }}>{selRef.candidateName}</div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{selRef.referredTo}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: 10, display: "flex", gap: 5 }}>
                          <span style={{ padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(0,0,0,0.08)", color: "#fff" }}>{STATUS_REF[selRef.status]?.label}</span>
                          <span style={{ padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(0,0,0,0.08)", color: "#fff" }}>{selRef.consentStatus}</span>
                        </div>
                      </div>
                      <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 11, fontFamily: "DM Mono, monospace" }}>What's happening</div>
                          <Timeline steps={selRef.timeline} />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Your vouch note</div>
                          <div style={{ background: "#FFFFFF", borderRadius: 8, padding: "9px 11px", fontSize: 12, lineHeight: 1.6, color: "#6B7280", borderLeft: "3px solid #D4845A", fontStyle: "italic" }}>"{selRef.note}"</div>
                        </div>

                        {selRef.bonusOffered > 0 && (
                          <div style={{ background: "#F8F5F2", borderRadius: 10, padding: "12px 14px" }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Referral Bonus</div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: "#E8896A" }}>${selRef.bonusOffered.toLocaleString()}</div>
                                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{selRef.bonusPaid ? "✓ Paid" : selRef.status === "placed" ? "Awaiting payment" : "Paid when placed"}</div>
                              </div>
                              {selRef.status === "placed" && !selRef.bonusPaid && (
                                <button onClick={async () => {
                                  const reason = prompt("Briefly describe the dispute (e.g. 'Bonus not paid after 30 days'):");
                                  if (!reason) return;
                                  await supabase.from("referrals").update({ bonus_disputed: true, bonus_dispute_reason: reason }).eq("id", selRef.id);
                                  await fetch("/.netlify/functions/notify-referrer", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ event: "bonus_dispute", referrerEmail: "hello@peerrefer.com", referrerName: "Admin", candidateName: selRef.candidateName, taName: "Admin", briefTitle: selRef.briefTitle, bonusAmount: selRef.bonusOffered }),
                                  });
                                  alert("Dispute submitted. PeerRefer will review and follow up within 2 business days.");
                                }} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "6px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", color: "#EF4444" }}>
                                  Flag Dispute
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {selRef.consentStatus === "declined" && (
                          <div style={{ background: "rgba(107,45,62,0.05)", borderRadius: 8, padding: "9px 11px", fontSize: 11, color: "#9CA3AF", lineHeight: 1.6 }}>🔒 Declined consent — details permanently deleted, never shared with any TA.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Mobile full-screen referrer detail */}
                {selRef && isMobile && (
                  <div className="detail-enter" style={{ position: "fixed", inset: 0, background: "#FAFAF8", zIndex: 200, overflowY: "auto" }}>
                    <div style={{ background: "linear-gradient(135deg, #4A1828, #8B3A50)", padding: "0 16px", position: "sticky", top: 0, zIndex: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 52 }}>
                        <button onClick={() => setSel(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#fff", padding: 0 }}>←</button>
                        <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15, color: "#fff" }}>{selRef.candidateName}</span>
                      </div>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(0,0,0,0.06)", color: "#6B7280" }}>{STATUS_REF[selRef.status]?.label}</span>
                        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "DM Mono, monospace", background: selRef.consentStatus === "consented" ? "rgba(72,187,120,0.1)" : "rgba(0,0,0,0.06)", color: selRef.consentStatus === "consented" ? "#059669" : "#9CA3AF" }}>{selRef.consentStatus}</span>
                      </div>
                      <div style={{ background: "#FFFFFF", borderRadius: 12, padding: "16px", marginBottom: 12, border: "1px solid rgba(0,0,0,0.06)" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 11, fontFamily: "DM Mono, monospace" }}>What's happening</div>
                        <Timeline steps={selRef.timeline} />
                      </div>
                      {selRef.note && (
                        <div style={{ background: "#FFFFFF", borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: "1px solid rgba(0,0,0,0.06)" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7, fontFamily: "DM Mono, monospace" }}>Your vouch note</div>
                          <div style={{ fontSize: 12, lineHeight: 1.6, color: "#6B7280", borderLeft: "3px solid #D4845A", paddingLeft: 10, fontStyle: "italic" }}>"{selRef.note}"</div>
                        </div>
                      )}
                      {selRef.consentStatus === "declined" && (
                        <div style={{ background: "rgba(107,45,62,0.05)", borderRadius: 8, padding: "9px 11px", fontSize: 11, color: "#9CA3AF", lineHeight: 1.6 }}>🔒 Declined consent — details permanently deleted, never shared with any TA.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "reputation" && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", padding: "22px 22px" }}>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Your Tier</div>
                {(() => {
                  const tier = getReferrerTier(user.totalPlaced || 0);
                  const nextPlaced = tier.nextAt ? tier.nextAt - (user.totalPlaced || 0) : 0;
                  return (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <span style={{ fontSize: 48 }}>{tier.emoji}</span>
                        <div>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 28, color: tier.color }}>{tier.name}</div>
                          <div style={{ fontSize: 12, color: "#9CA3AF" }}>{user.totalPlaced || 0} placement{(user.totalPlaced || 0) !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      {tier.next && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <span style={{ fontSize: 12, color: "#6B7280" }}>Progress to {tier.next}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: tier.color }}>{user.totalPlaced || 0}/{tier.nextAt}</span>
                          </div>
                          <div style={{ height: 6, background: "rgba(0,0,0,0.06)", borderRadius: 3 }}>
                            <div style={{ height: "100%", background: tier.color, borderRadius: 3, width: `${Math.min(((user.totalPlaced || 0) / tier.nextAt) * 100, 100)}%`, transition: "width 0.5s" }} />
                          </div>
                          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>{nextPlaced} more placement{nextPlaced !== 1 ? "s" : ""} to reach {tier.next}</div>
                        </div>
                      )}
                      {!tier.next && <div style={{ fontSize: 13, color: tier.color, fontWeight: 600 }}>🏆 You've reached the highest tier!</div>}
                      <div style={{ background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: tier.color, fontFamily: "DM Mono, monospace", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>{tier.name} Benefits</div>
                        {[
                          tier.name === "Bronze" ? "Access to all active job briefs" : null,
                          tier.name === "Silver" ? "Priority visibility to TAs" : null,
                          tier.name === "Silver" ? "Silver badge on your profile" : null,
                          tier.name === "Gold" ? "Gold badge — TAs trust your referrals more" : null,
                          tier.name === "Gold" ? "Featured in top connector rankings" : null,
                          tier.name === "Platinum" ? "Platinum badge — highest trust signal" : null,
                          tier.name === "Platinum" ? "Featured as a top connector to all TAs" : null,
                          tier.name === "Platinum" ? "Early access to new premium roles" : null,
                        ].filter(Boolean).map((b, i) => (
                          <div key={i} style={{ fontSize: 12, color: tier.color, marginBottom: 4 }}>✓ {b}</div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Second column - reputation breakdown + earnings */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", padding: "22px 22px" }}>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Reputation Breakdown</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 18 }}>Built from quality and outcomes of your referrals over time.</div>
                  {[
                    { l: "Referral Quality", v: 92, c: "#E8896A" },
                    { l: "Consent Rate", v: user.totalReferred > 0 ? Math.round((user.totalConsented || 0) / user.totalReferred * 100) : 0, c: "#3A6B8A" },
                    { l: "Contact Rate", v: user.totalReferred > 0 ? Math.round((user.totalContacted || 0) / user.totalReferred * 100) : 0, c: "#C47D2B" },
                    { l: "Placement Rate", v: user.totalReferred > 0 ? Math.round((user.totalPlaced || 0) / user.totalReferred * 100) : 0, c: "#6B4FBB" },
                  ].map(item => (
                    <div key={item.l} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{item.l}</span>
                        <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 16, color: item.c }}>{item.v}%</span>
                      </div>
                      <div style={{ height: 5, background: "rgba(0,0,0,0.04)", borderRadius: 3 }}>
                        <div style={{ height: "100%", width: item.v + "%", background: item.c, borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", padding: "22px 22px", marginTop: 14 }}>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Invite a Referrer</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12, lineHeight: 1.6 }}>Know someone who'd be a great referrer? Invite them — you'll earn +5 reputation points when they make their first referral.</div>
                  <ReferrerInviteFlow user={user} />
                </div>

                <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", padding: "22px 22px", marginTop: 14 }}>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Your Data</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12, lineHeight: 1.6 }}>Under Australian Privacy Act and GDPR, you have the right to request deletion of your data from PeerRefer at any time.</div>
                  <button onClick={async () => {
                    if (!window.confirm("Are you sure you want to request deletion of all your data? This cannot be undone.")) return;
                    await fetch("/.netlify/functions/notify-referrer", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ event: "gdpr_deletion", referrerEmail: "hello@peerrefer.com", referrerName: "Admin", candidateName: user.name, taName: user.email, briefTitle: "Data deletion request", bonusAmount: 0 }),
                    });
                    alert("Your data deletion request has been submitted. We will process it within 30 days and confirm by email.");
                  }} style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 8, padding: "8px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#EF4444" }}>
                    Request Data Deletion
                  </button>
                </div>
                <div style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid rgba(0,0,0,0.06)", padding: "18px 18px" }}>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Earnings</div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                    <span style={{ fontSize: 12, color: "#9CA3AF" }}>Total earned</span>
                    <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 17, color: "#E8896A" }}>${user.paidBonus.toLocaleString()}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 7, borderTop: "1px solid rgba(0,0,0,0.07)" }}>
                    <span style={{ fontSize: 12, color: "#9CA3AF" }}>Pending</span>
                    <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 17, color: "#F59E0B" }}>${user.pendingBonus.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && !referAgain && (
        <SubmitModal
          onClose={() => { setShowModal(false); setReferAgain(null); }}
          onSubmit={handleSubmit}
          isReferrer={true}
          taBonus={taBonus}
          taName={loadedTaPrefs?.name || taPrefs?.name || ""}
          taPrefs={loadedTaPrefs}
          activeBriefs={localBriefs.length > 0 ? localBriefs : (sharedBriefs ? sharedBriefs.filter(b => b.status === "active") : [])}
          taSlug={loadedTaPrefs?.slug || taPrefs?.slug}
          referrerLocation={user?.location || ""}
        />
      )}

      {/* Re-refer modal — simplified 2-step flow for existing candidates */}
      {referAgain && (
        <ReReferModal
          candidate={referAgain}
          activeBriefs={(localBriefs.length > 0 ? localBriefs : (sharedBriefs || [])).filter(b => b.status === "active" && b.id !== (referAgain.briefId || referAgain.brief_id))}
          user={user}
          onClose={() => { setReferAgain(null); setShowModal(false); }}
          onSubmit={async (data) => {
            const allBriefs = localBriefs.length > 0 ? localBriefs : (sharedBriefs || []);
            const brief = allBriefs.find(b => b.id === data.briefId);
            const taProfileId = brief?.ta_profile_id || brief?.taProfileId;
            const insertPayload = {
              candidate_name: data.candidateName,
              candidate_email: data.candidateEmail || null,
              candidate_linkedin: null,
              skills: data.skills || [],
              note: data.note || "",
              brief_id: data.briefId || null,
              brief_title: data.briefTitle || null,
              ta_profile_id: taProfileId || null,
              referrer_id: user?.id || null,
              status: "pending_consent",
              consented: null,
              blurred: true,
              skills_rating: data.s1 || 5,
              work_ethic_rating: data.s2 || 5,
              coachability_rating: data.s3 || 5,
            };
            console.log("Re-refer insert payload:", JSON.stringify(insertPayload));
            const { data: saved, error: saveError } = await supabase.from("referrals").insert(insertPayload).select().single();
            if (saveError) {
              console.error("Re-refer save error:", JSON.stringify(saveError));
              alert(`Save failed: ${saveError.message || saveError.code || JSON.stringify(saveError)}`);
              return;
            }
            console.log("Re-refer: saved=", saved?.id, "brief=", brief?.title, "taData=", brief?.ta_profiles);
            if (saved && data.candidateEmail) {
              const taData = brief?.ta_profiles || {};
              const res = await fetch("/.netlify/functions/send-consent", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  candidateName: data.candidateName,
                  candidateEmail: data.candidateEmail,
                  referrerName: data.refName || user?.name,
                  taName: taData?.name || "the recruiter",
                  taCompany: taData?.company || "",
                  referralId: saved.id,
                  briefTitle: data.briefTitle,
                }),
              });
              const result = await res.json();
              console.log("Consent email result:", result);
              if (!res.ok) {
                alert(`Note: referral saved but consent email failed: ${result.error}`);
              }
            } else {
              console.error("Re-refer failed: saved=", saved, "email=", data.candidateEmail);
              alert("Referral could not be saved. Please try again.");
              return;
            }
            setReferAgain(null);
            alert(`✅ Consent request sent to ${data.candidateName} for ${data.briefTitle}!`);
          }}
        />
      )}

      {sharingRole && (
      <ReferrerShareModal
        brief={sharingRole}
        onClose={() => setSharingRole(null)}
      />
    )}

    {submitted && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#E8896A", color: "#fff", borderRadius: 12, padding: "14px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 2000, display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
          ✓ Referral submitted — now visible in the TA talent pool
        </div>
      )}
    </div>
  );
}



// ============================================
// PUBLIC REFERRAL LINK PAGE
// What anyone sees at peerrefer.co/refer/mia-thorne
// ============================================
function PublicReferralPage({ slug, onRefer }) {
  const [loading, setLoading] = useState(true);
  const [taProfile, setTaProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    supabase.from("ta_profiles").select("*").eq("slug", slug).single()
      .then(({ data }) => { if (data) setTaProfile(data); setLoading(false); });
  }, [slug]); // eslint-disable-line

  const handleSubmit = async data => {
    if (taProfile) {
      let referrerId;
      if (data.referrerEmail) {
        const { data: existing } = await supabase.from("referrers").select("id, total_referred").eq("email", data.referrerEmail).maybeSingle();
        if (existing) {
          referrerId = existing.id;
          await supabase.from("referrers").update({ total_referred: (existing.total_referred || 0) + 1 }).eq("id", existing.id);
        }
      }
      if (!referrerId) {
        const { data: nr } = await supabase.from("referrers").insert({ name: data.referrerName, email: data.referrerEmail || null, total_referred: 1 }).select().single();
        if (nr) referrerId = nr.id;
      }
      await supabase.from("referrals").insert({
        ta_profile_id: taProfile.id, referrer_id: referrerId,
        candidate_name: data.candidateName, candidate_email: data.candidateEmail || null,
        candidate_linkedin: data.candidateLinkedin || null, skills: data.skills, note: data.note,
        skills_rating: data.skillsRating, work_ethic_rating: data.workEthicRating, coachability_rating: data.coachabilityRating,
        status: "pending_consent", consented: null, blurred: true,
        brief_id: data.briefId || null,
        brief_title: data.briefTitle || null,
      });

      // Send consent email to candidate
      if (data.candidateEmail) {
        try {
          await fetch("/.netlify/functions/send-consent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              candidateName: data.candidateName,
              candidateEmail: data.candidateEmail,
              referrerName: data.referrerName || "A referrer",
              taName: taProfile?.name || "the recruiter",
              taCompany: taProfile?.company || "",
              referralId: data.savedReferralId || data.candidateEmail,
              briefTitle: data.briefTitle || null,
            }),
          });
        } catch (emailErr) {
          console.error("Consent email failed:", emailErr);
        }
      }
    }
    setShowForm(false);
    setSubmitted(true);
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#9CA3AF" }}><Spin /> Loading...</div>
    </div>
  );

  if (!taProfile) return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 22, color: "#1C1917", marginBottom: 8 }}>Recruiter not found</div>
        <div style={{ fontSize: 14, color: "#9CA3AF" }}>This referral link may have expired or been removed.</div>
      </div>
    </div>
  );

  if (submitted) {
    const candidateName = form.name || "them";
    const firstName = candidateName.split(" ")[0];
    const shareText = `Hey ${firstName}, I've put your name forward for a role through PeerRefer — a consent-first referral platform. Takes 2 minutes to review and consent. Check your email for details, or here's the link: https://www.peerrefer.com`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    const smsUrl = `sms:?body=${encodeURIComponent(shareText)}`;
    return (
      <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ maxWidth: 480, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
            <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 26, color: "#1C1917", marginBottom: 10, letterSpacing: "-0.02em" }}>Referral submitted!</h1>
            <p style={{ fontSize: 14, color: "#9CA3AF", lineHeight: 1.7, margin: 0 }}>
              A consent email has been sent to <strong style={{ color: "#1C1917" }}>{candidateName}</strong>. You'll be notified when they consent and if they get placed.
            </p>
          </div>

          {/* One-tap message card */}
          <div style={{ background: "#FFFFFF", borderRadius: 16, padding: 24, marginBottom: 16, border: "1.5px solid rgba(0,0,0,0.07)", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontFamily: "DM Mono, monospace" }}>
              💬 Give {firstName} a heads up
            </div>
            <div style={{ background: "#F8F5F2", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#374151", lineHeight: 1.65, marginBottom: 14, fontStyle: "italic" }}>
              "{shareText}"
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 8px", background: "#25D366", borderRadius: 10, textDecoration: "none", transition: "opacity 0.15s" }}>
                <span style={{ fontSize: 20 }}>💬</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF" }}>WhatsApp</span>
              </a>
              <a href={smsUrl}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 8px", background: "#1C1917", borderRadius: 10, textDecoration: "none" }}>
                <span style={{ fontSize: 20 }}>📱</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF" }}>Message</span>
              </a>
              <button onClick={() => { navigator.clipboard?.writeText(shareText); setMsgCopied(true); setTimeout(() => setMsgCopied(false), 2000); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 8px", background: msgCopied ? "#059669" : "#F3F4F6", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                <span style={{ fontSize: 20 }}>{msgCopied ? "✓" : "📋"}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: msgCopied ? "#FFFFFF" : "#6B7280" }}>{msgCopied ? "Copied!" : "Copy"}</span>
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setSubmitted(false)}
              style={{ flex: 1, background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 10, padding: "13px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Refer someone else
            </button>
            <button onClick={onClose}
              style={{ flex: 1, background: "transparent", color: "#6B7280", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 10, padding: "13px", fontFamily: "inherit", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      {/* Nav */}
      <div style={{ padding: "0 28px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(0,0,0,0.08)", background: "rgba(250,250,248,0.97)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, background: "#1C1917", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em" }}>PeerRefer</div>
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>by CollabPathways</div>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "40px 24px" }}>
        {/* TA Profile Card */}
        {taProfile && (
          <div style={{ background: "#FFFFFF", borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 24px rgba(26,22,18,0.1)", marginBottom: 24 }}>
            <div style={{ background: "linear-gradient(135deg, #4A1828, #8B3A50)", padding: "32px 32px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 22, color: "#fff" }}>
                  {taProfile.name.split(" ").map(n => n[0]).join("")}
                </div>
                <div>
                  <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 22, color: "#fff", letterSpacing: "-0.02em" }}>{taProfile.name}</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>Talent Lead · {taProfile.company}</div>
                </div>
              </div>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, marginBottom: 0 }}>
                "I'm always open to strong talent in the Melbourne tech space. Culture fit matters as much as skills — if you know someone great, I'd love to hear about them."
              </p>
            </div>

            <div style={{ padding: "24px 32px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16, fontFamily: "DM Mono, monospace" }}>What I'm Looking For</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { icon: "🎯", label: "Roles", value: taProfile.looking_for || "Various roles" },
                  { icon: "🏢", label: "Industries", value: taProfile.industries || "SaaS, Tech, Fintech" },
                  { icon: "📈", label: "Seniority", value: taProfile.seniority || "Mid to Senior level" },
                  { icon: "📍", label: "Location", value: taProfile.location || "Melbourne / Remote" },
                ].map((item, i) => (
                  <div key={i} style={{ background: "#FAFAF8", borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 3, fontFamily: "DM Mono, monospace" }}>{item.icon} {item.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {taProfile.bonus_enabled && taProfile.bonus_amount > 0 && (
                <div style={{ background: "rgba(245,158,11,0.1)", borderLeft: "4px solid #C47D2B", borderRadius: 10, padding: "14px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 24 }}>💰</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#F59E0B" }}>${taProfile.bonus_amount.toLocaleString()} referral bonus</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>Earn this if your referral leads to a successful placement — paid directly to your bank via PeerRefer.</div>
                  </div>
                </div>
              )}

              <div style={{ background: "rgba(0,0,0,0.07)", borderRadius: 10, padding: "12px 14px", marginBottom: 20, fontSize: 12, color: "#E8896A", lineHeight: 1.6 }}>
                🔒 <strong>Privacy first.</strong> The person you refer will be asked for their explicit consent before any details are shared with {taProfile.name}. You're in control.
              </div>

              <button onClick={() => setShowForm(true)}
                style={{ width: "100%", background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 12, padding: "15px", fontFamily: "inherit", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>
                Refer Someone to {taProfile.name.split(" ")[0]} →
              </button>
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>
          Powered by PeerRefer · by CollabPathways · Privacy-first talent referrals
        </div>
      </div>

      {showForm && (
        <SubmitModal
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
          isReferrer={true}
          taBonus={{ enabled: taProfile?.bonus_enabled, amount: taProfile?.bonus_amount }}
          activeBriefs={[]}
        />
      )}
    </div>
  );
}

// ============================================
// CANDIDATE PROFILE PAGE — post-consent upload
// ============================================
// ============================================
// CANDIDATE PASSPORT DISPLAY — shown in TA detail panel
function CVUpload({ referralId }) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) { setError("Please upload a PDF or Word document."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("File must be under 5MB."); return; }
    setError("");
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `cvs/${referralId}.${ext}`;
    const { error: upErr } = await supabase.storage.from("cvs").upload(path, file, { upsert: true });
    if (upErr) { setError("Upload failed. Please try again."); setUploading(false); return; }
    await supabase.from("referrals").update({ cv_filename: path }).eq("id", referralId);
    setFilename(file.name);
    setUploaded(true);
    setUploading(false);
  };

  if (uploaded) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(5,150,105,0.07)", border: "1px solid rgba(5,150,105,0.2)", borderRadius: 10, padding: "12px 16px" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>CV uploaded ✓</div>
        <div style={{ fontSize: 11, color: "#6B7280" }}>{filename}</div>
      </div>
    </div>
  );

  return (
    <div>
      <label style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        border: "2px dashed rgba(0,0,0,0.12)", borderRadius: 12, padding: "28px 20px",
        cursor: uploading ? "not-allowed" : "pointer", background: "#FAFAF8", textAlign: "center"
      }}>
        <input type="file" accept=".pdf,.doc,.docx" onChange={handleFile} style={{ display: "none" }} disabled={uploading} />
        {uploading ? (
          <div style={{ fontSize: 14, color: "#6B7280" }}>Uploading...</div>
        ) : (
          <>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 10 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Tap to upload your CV</div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>PDF or Word · Max 5MB</div>
          </>
        )}
      </label>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: "#DC2626" }}>{error}</div>}
    </div>
  );
}

function CandidatePassportDisplay({ email }) {
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    supabase.from("candidates").select("*").eq("email", email).maybeSingle()
      .then(({ data }) => { setPassport(data); setLoading(false); });
  }, [email]);

  if (loading || !passport) return null;
  // Show passport if any meaningful data exists
  const hasData = passport.profile_summary || passport.work_rights || passport.career_goals || passport.industries || passport.certifications || passport.preferred_locations;
  if (!hasData) return null;

  const Row = ({ label, value }) => value ? (
    <div style={{ display: "flex", gap: 8, marginBottom: 7 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", minWidth: 90, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div style={{ background: "linear-gradient(135deg, rgba(91,110,175,0.06), rgba(91,110,175,0.02))", borderRadius: 11, padding: "14px 15px", marginBottom: 14, border: "1px solid rgba(91,110,175,0.15)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 11 }}>
        <span style={{ fontSize: 14 }}>🛂</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#5B6EAF", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>Talent Passport</span>
        <span style={{ fontSize: 9, background: passport.passport_complete ? "rgba(5,150,105,0.1)" : "rgba(245,158,11,0.1)", color: passport.passport_complete ? "#059669" : "#D97706", borderRadius: 20, padding: "1px 7px", fontFamily: "DM Mono, monospace", fontWeight: 700 }}>{passport.passport_complete ? "COMPLETE" : "PARTIAL"}</span>
      </div>
      {passport.profile_summary && (
        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, marginBottom: 11, fontStyle: "italic", borderLeft: "3px solid #5B6EAF", paddingLeft: 10 }}>"{passport.profile_summary}"</div>
      )}
      {passport.intro_text && (
        <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6, marginBottom: 11, background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: "8px 10px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#9CA3AF", fontFamily: "DM Mono, monospace", marginBottom: 4 }}>60-SEC INTRO</div>
          {passport.intro_text}
        </div>
      )}
      <Row label="WORK RIGHTS" value={passport.work_rights} />
      <Row label="SALARY" value={passport.salary_min || passport.salary_max ? `$${(passport.salary_min || 0).toLocaleString()} – $${(passport.salary_max || 0).toLocaleString()} AUD` : null} />
      <Row label="INDUSTRIES" value={Array.isArray(passport.industries) ? passport.industries.join(", ") : passport.industries} />
      <Row label="LOCATIONS" value={Array.isArray(passport.preferred_locations) ? passport.preferred_locations.join(", ") : passport.preferred_locations} />
      <Row label="GOALS" value={passport.career_goals} />
      <Row label="CERTS" value={passport.certifications} />
    </div>
  );
}

// ============================================
// TALENT PASSPORT FORM — shown to candidate after consent
function TalentPassportForm({ referralId, candidateEmail, candidateName, taName, referrerName }) {
  const [passport, setPassport] = useState({
    workRights: "", salaryMin: "", salaryMax: "",
    industries: "", preferredLocations: "",
    careerGoals: "", certifications: "",
    profileSummary: "", introText: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Load existing passport if candidate already has one
  useEffect(() => {
    if (!candidateEmail) { setLoading(false); return; }
    supabase.from("candidates").select("*").eq("email", candidateEmail).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setPassport({
            workRights: data.work_rights || "",
            salaryMin: data.salary_min || "",
            salaryMax: data.salary_max || "",
            industries: Array.isArray(data.industries) ? data.industries.join(", ") : (data.industries || ""),
            preferredLocations: Array.isArray(data.preferred_locations) ? data.preferred_locations.join(", ") : (data.preferred_locations || ""),
            careerGoals: data.career_goals || "",
            certifications: data.certifications || "",
            profileSummary: data.profile_summary || "",
            introText: data.intro_text || "",
          });
          if (data.passport_complete) setSaved(true);
        }
        setLoading(false);
      });
  }, [candidateEmail]);

  const save = async () => {
    setSaving(true);
    // Upsert candidate record
    await supabase.from("candidates").upsert({
      email: candidateEmail,
      name: candidateName,
      work_rights: passport.workRights,
      salary_min: passport.salaryMin ? parseInt(passport.salaryMin) : null,
      salary_max: passport.salaryMax ? parseInt(passport.salaryMax) : null,
      industries: passport.industries ? passport.industries.split(",").map(s => s.trim()).filter(Boolean) : [],
      preferred_locations: passport.preferredLocations ? passport.preferredLocations.split(",").map(s => s.trim()).filter(Boolean) : [],
      career_goals: passport.careerGoals,
      certifications: passport.certifications,
      profile_summary: passport.profileSummary,
      intro_text: passport.introText,
      passport_complete: !!(passport.workRights || passport.profileSummary || passport.careerGoals || passport.industries),
      referred_by: referrerName || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "email" });
    setSaving(false);
    setSaved(true);
    setExpanded(false);
  };

  if (loading) return null;

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917" }}>
          🛂 Your Talent Passport
        </div>
        {saved && !expanded && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(5,150,105,0.1)", border: "1px solid rgba(5,150,105,0.2)", borderRadius: 20, padding: "3px 10px" }}>
            <span style={{ color: "#059669", fontSize: 11 }}>✓</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#059669", fontFamily: "DM Mono, monospace" }}>SAVED</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: referrerName ? 12 : 16, lineHeight: 1.6 }}>
        Fill this in once — it's reused every time someone refers you. {taName || "The recruiter"} will see this alongside your referral.
      </div>
      {referrerName && (
        <div style={{ background: "rgba(91,110,175,0.06)", border: "1px solid rgba(91,110,175,0.15)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#5B6EAF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#5B6EAF", fontWeight: 700, fontFamily: "DM Mono, monospace", marginBottom: 1 }}>REFERRED BY</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{referrerName}</div>
          </div>
        </div>
      )}

      {!expanded && saved ? (
        <button onClick={() => setExpanded(true)}
          style={{ background: "rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "9px 16px", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#6B7280" }}>
          Edit Passport →
        </button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Profile Summary */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Profile Summary <span style={{ color: "#E8896A" }}>*</span></label>
            <textarea value={passport.profileSummary} onChange={e => setPassport(p => ({ ...p, profileSummary: e.target.value }))}
              placeholder="2–4 sentences about your experience and what you're looking for next. This is what the recruiter reads first."
              rows={3} style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, lineHeight: 1.7, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          {/* 60-second intro */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>60-Second Intro <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF" }}>— in your own words</span></label>
            <textarea value={passport.introText} onChange={e => setPassport(p => ({ ...p, introText: e.target.value }))}
              placeholder="Describe your background as if speaking to a recruiter. E.g. I'm a senior PM with 8 years in B2B SaaS, most recently at Atlassian. Looking for a Head of Product role at a scale-up..."
              rows={3} style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, lineHeight: 1.7, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          {/* Work rights + Salary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Work rights <span style={{ color: "#E8896A" }}>*</span></label>
              <select value={passport.workRights} onChange={e => setPassport(p => ({ ...p, workRights: e.target.value }))}
                style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", background: "#FAFAF8" }}>
                <option value="">Select...</option>
                {["Australian Citizen", "Australian PR", "NZ Citizen", "Work Visa (Sponsored)", "Student Visa", "Requires Sponsorship"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Salary (AUD/yr)</label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="number" value={passport.salaryMin} onChange={e => setPassport(p => ({ ...p, salaryMin: e.target.value }))}
                  placeholder="Min" style={{ flex: 1, border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 8px", fontFamily: "inherit", fontSize: 12, outline: "none", minWidth: 0 }} />
                <span style={{ color: "#9CA3AF", fontSize: 11, flexShrink: 0 }}>–</span>
                <input type="number" value={passport.salaryMax} onChange={e => setPassport(p => ({ ...p, salaryMax: e.target.value }))}
                  placeholder="Max" style={{ flex: 1, border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 8px", fontFamily: "inherit", fontSize: 12, outline: "none", minWidth: 0 }} />
              </div>
            </div>
          </div>

          {/* Industries + Locations */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Industries (comma separated)</label>
            <input value={passport.industries} onChange={e => setPassport(p => ({ ...p, industries: e.target.value }))}
              placeholder="e.g. SaaS, Fintech, Healthcare" style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Preferred locations (comma separated)</label>
            <input value={passport.preferredLocations} onChange={e => setPassport(p => ({ ...p, preferredLocations: e.target.value }))}
              placeholder="e.g. Melbourne, Sydney, Remote" style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>

          {/* Career Goals */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Career goals</label>
            <textarea value={passport.careerGoals} onChange={e => setPassport(p => ({ ...p, careerGoals: e.target.value }))}
              placeholder="e.g. Move into leadership, transition into climate tech, find a company with strong engineering culture..."
              rows={2} style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, lineHeight: 1.6, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          {/* Certifications */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 5 }}>Certifications & qualifications</label>
            <input value={passport.certifications} onChange={e => setPassport(p => ({ ...p, certifications: e.target.value }))}
              placeholder="e.g. PMP, AWS Solutions Architect, CPA, MBA" style={{ width: "100%", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "9px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>

          <button onClick={save} disabled={saving}
            style={{ background: saved && !saving ? "#059669" : "#1C1917", color: "#fff", border: "none", borderRadius: 10, padding: "13px 20px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving..." : saved ? "✓ Passport saved!" : "Save Talent Passport →"}
          </button>
          <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center" }}>
            🔒 Only shared with TAs when you give explicit consent. Never sold or shared externally.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
function CandidateProfilePage({ onComplete }) {
  const [linkedin, setLinkedin] = useState("");
  const [cvName, setCvName] = useState("");
  const [cvData, setCvData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [cvSummary, setCvSummary] = useState("");

  const handleCV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCvName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setCvData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setAnalysing(true);
    if (cvData) {
      const summary = await callClaude(
        "You are PeerRefer's CV Analyser. Read this CV and return a 2-sentence plain-English summary of the candidate's background, key strengths, and what kind of role they'd suit. Be specific and recruiter-focused.",
        "Please analyse this candidate's CV and provide a brief summary: " + cvName + ". [CV content would be extracted here in production]"
      );
      setCvSummary(summary);
    }
    setAnalysing(false);
    setSubmitted(true);
    setTimeout(() => {
      if (onComplete) onComplete({ linkedin, cvName, cvSummary });
    }, 2000);
  };

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
        <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
          <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 28, color: "#1C1917", marginBottom: 12, letterSpacing: "-0.02em" }}>Profile complete!</h1>
          <p style={{ fontSize: 14, color: "#9CA3AF", lineHeight: 1.7 }}>Your profile is now live in the talent pool. Talent professionals will be in touch if there's a match — on your terms, no spam.</p>
          <div style={{ marginTop: 24, background: "rgba(0,0,0,0.07)", borderRadius: 12, padding: "16px 20px", display: "inline-block" }}>
            <div style={{ fontSize: 13, color: "#E8896A", fontWeight: 600 }}>✓ Profile visible to verified TAs only</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>You can withdraw consent at any time</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32, justifyContent: "center" }}>
          <div style={{ width: 36, height: 36, background: "#1C1917", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 18, color: "#1C1917", letterSpacing: "-0.02em" }}>PeerRefer</div>
        </div>

        <div style={{ background: "#FFFFFF", borderRadius: 20, padding: "36px 32px", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 8px 32px rgba(26,22,18,0.1)" }}>
          <div style={{ fontSize: 40, textAlign: "center", marginBottom: 16 }}>✅</div>
          <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 24, color: "#1C1917", marginBottom: 8, letterSpacing: "-0.02em", textAlign: "center" }}>You're in the pool!</h1>
          <p style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.65, marginBottom: 28, textAlign: "center" }}>
            You've consented to be visible to verified talent professionals. Strengthen your profile by adding your CV and LinkedIn — it significantly increases your chances of being contacted.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>LinkedIn Profile URL <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional but recommended)</span></label>
              <input
                value={linkedin}
                onChange={e => setLinkedin(e.target.value)}
                placeholder="linkedin.com/in/your-profile"
                style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 9, padding: "11px 14px", fontFamily: "inherit", fontSize: 14, color: "#1C1917", outline: "none" }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 6 }}>Upload your CV <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional — PDF or Word)</span></label>
              <label style={{ display: "block", background: "#FAFAF8", border: "2px dashed rgba(26,22,18,0.2)", borderRadius: 10, padding: "20px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}>
                <input type="file" accept=".pdf,.doc,.docx" onChange={handleCV} style={{ display: "none" }} />
                {cvName
                  ? <div style={{ fontSize: 13, color: "#E8896A", fontWeight: 600 }}>✓ {cvName}</div>
                  : <div>
                      <div style={{ fontSize: 24, marginBottom: 6 }}>📄</div>
                      <div style={{ fontSize: 13, color: "#9CA3AF" }}>Click to upload CV</div>
                      <div style={{ fontSize: 11, color: "#C4BAB2", marginTop: 3 }}>PDF, DOC, DOCX up to 5MB</div>
                    </div>
                }
              </label>
              {cvName && (
                <div style={{ marginTop: 8, padding: "10px 12px", background: "rgba(0,0,0,0.07)", borderRadius: 8, fontSize: 12, color: "#E8896A" }}>
                  🤖 AI will analyse your CV and add a summary to your profile — giving TAs richer context about your background.
                </div>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={analysing}
              style={{ background: "#1C1917", color: "#FAFAF8", border: "none", borderRadius: 10, padding: "14px", fontFamily: "inherit", fontWeight: 700, fontSize: 15, cursor: analysing ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: analysing ? 0.7 : 1 }}
            >
              {analysing ? <><Spin color="#FFFFFF" /> AI is analysing your CV...</> : "Complete My Profile →"}
            </button>

            <button onClick={() => onComplete && onComplete({})} style={{ background: "transparent", border: "none", fontFamily: "inherit", fontSize: 12, color: "#9CA3AF", cursor: "pointer", textAlign: "center", padding: "4px" }}>
              Skip for now — go live with basic profile
            </button>
          </div>

          <div style={{ marginTop: 24, padding: "12px 14px", background: "#FAFAF8", borderRadius: 10, fontSize: 12, color: "#9CA3AF", lineHeight: 1.6 }}>
            🔒 Your CV is only visible to verified TA professionals on PeerRefer. It is never shared externally or used to train AI models.
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PORTAL SELECTOR
// ============================================
// ============================================
// RAISE YOUR HAND — candidate self-nominates and gets a shareable refer-me link
function RaiseYourHandPanel({ brief }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [submitted, setSubmitted] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) { alert("Please enter your name and email."); return; }
    await supabase.from("candidates").upsert({
      email: form.email.trim().toLowerCase(),
      name: form.name.trim(),
      profile_summary: form.note.trim() || null,
      raised_hand: true,
      raised_hand_brief_id: brief.id,
      raised_hand_at: new Date().toISOString(),
    }, { onConflict: "email" });
    const url = `${window.location.origin}?briefId=${brief.id}&referMe=${encodeURIComponent(form.email)}&candidateName=${encodeURIComponent(form.name)}`;
    setShareUrl(url);
    setSubmitted(true);
  };

  const copy = () => {
    navigator.clipboard?.writeText(shareUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (submitted) return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px 32px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>✋</div>
      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#1C1917", marginBottom: 8 }}>You're on the list, {form.name.split(" ")[0]}!</div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20, lineHeight: 1.6 }}>Share this link with someone who knows you and can vouch for you. When they refer you, you'll get a consent email.</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input readOnly value={shareUrl} style={{ flex: 1, background: "#F8F5F2", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 8, padding: "9px 12px", fontSize: 12, fontFamily: "DM Mono, monospace", color: "#6B7280", outline: "none" }} />
        <button onClick={copy} style={{ background: "#1C1917", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{copied ? "Copied!" : "Copy"}</button>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(10,102,194,0.08)", border: "1px solid rgba(10,102,194,0.2)", borderRadius: 8, padding: "7px 14px", textDecoration: "none", fontSize: 12, fontWeight: 600, color: "#0A66C2" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          Share on LinkedIn
        </a>
        <a href={`https://wa.me/?text=${encodeURIComponent(`Hey, would you refer me for this role? ${shareUrl}`)}`} target="_blank" rel="noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(37,211,102,0.08)", border: "1px solid rgba(37,211,102,0.2)", borderRadius: 8, padding: "7px 14px", textDecoration: "none", fontSize: 12, fontWeight: 600, color: "#25D366" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
          Send via WhatsApp
        </a>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px 32px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid rgba(91,110,175,0.1)" }}>
      {!open ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 16, color: "#1C1917", marginBottom: 4 }}>✋ Think you'd be a great fit?</div>
            <div style={{ fontSize: 13, color: "#6B7280" }}>Raise your hand — get a link to share with someone who can vouch for you.</div>
          </div>
          <button onClick={() => setOpen(true)}
            style={{ background: "rgba(91,110,175,0.08)", color: "#5B6EAF", border: "1px solid rgba(91,110,175,0.2)", borderRadius: 10, padding: "10px 18px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, marginLeft: 16 }}>
            Raise My Hand →
          </button>
        </div>
      ) : (
        <div>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917", marginBottom: 4 }}>✋ Raise Your Hand</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20, lineHeight: 1.6 }}>Enter your details and we'll generate a link you can share with someone who knows you. They refer you — you consent before anyone sees your profile.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name"
              style={{ border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, outline: "none" }} />
            <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Your email address" type="email"
              style={{ border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, outline: "none" }} />
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Why would you be great for this role? (optional)" rows={3}
              style={{ border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "10px 12px", fontFamily: "inherit", fontSize: 13, outline: "none", resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSubmit} style={{ flex: 1, background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "11px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Generate My Refer-Me Link →</button>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "11px 16px", fontFamily: "inherit", fontSize: 13, cursor: "pointer", color: "#9CA3AF" }}>Cancel</button>
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.6 }}>🔒 Your details are only shared when someone refers you and you give consent. We never share your email without your permission.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// PUBLIC JOB PAGE — shareable role landing page
function PublicJobPage({ briefId, referSlug, onRefer }) {
  const [brief, setBrief] = useState(null);
  const [ta, setTa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("briefs")
        .select("*, ta_profiles(name, company, slug, looking_for, location, referrer_note)")
        .eq("id", briefId)
        .maybeSingle();
      if (data) {
        setBrief(data);
        setTa(data.ta_profiles);
      }
      setLoading(false);
    };
    load();
  }, [briefId]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#F8F5F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin color="#5B6EAF" />
    </div>
  );

  if (!brief) return (
    <div style={{ minHeight: "100vh", background: "#F8F5F2", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 22, color: "#1C1917", marginBottom: 8 }}>Role not found</div>
        <div style={{ fontSize: 14, color: "#9CA3AF" }}>This job link may have expired or been removed.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F8F5F2", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ background: "#FFFFFF", borderBottom: "1px solid rgba(0,0,0,0.06)", padding: "0 24px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, background: "#1C1917", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div>
            <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em" }}>PeerRefer</div>
            <div style={{ fontSize: 9, color: "#9CA3AF", fontFamily: "DM Mono, monospace" }}>Consent-First Referrals</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/?pricing=true" style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, textDecoration: "none" }}>Pricing</a>
          <button onClick={() => onRefer(brief)}
            style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 9, padding: "8px 16px", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            + Refer Someone
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px" }}>
        {/* Company + Role header */}
        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "28px 32px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            {brief.logo_url ? (
              <img src={brief.logo_url} alt={ta?.company} style={{ width: 64, height: 64, borderRadius: 12, objectFit: "contain", border: "1px solid rgba(0,0,0,0.08)", background: "#fff", padding: 4 }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: 12, background: "linear-gradient(135deg, #4A1828, #6B2D3E)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              </div>
            )}
            <div>
              <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, color: "#1C1917", marginBottom: 4 }}>{brief.title}</div>
              <div style={{ fontSize: 13, color: "#6B7280" }}>
                {ta?.company && <span style={{ fontWeight: 600, color: "#374151" }}>{ta.company}</span>}
                {brief.location && <span> · {brief.location}</span>}
                {brief.seniority && <span> · {brief.seniority}</span>}
              </div>
            </div>
          </div>

          {/* Skills */}
          {brief.skills?.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {brief.skills.map(s => (
                <span key={s} style={{ background: "rgba(91,110,175,0.08)", color: "#5B6EAF", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, fontFamily: "DM Mono, monospace" }}>{s}</span>
              ))}
            </div>
          )}

          {/* Bonus */}
          {brief.bonus > 0 && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(232,137,106,0.08)", border: "1px solid rgba(232,137,106,0.2)", borderRadius: 10, padding: "10px 16px", marginBottom: 20 }}>
              <span style={{ fontSize: 20 }}>💰</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#C4623E" }}>${brief.bonus.toLocaleString()} referral bonus</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>Paid to you if your referral is placed</div>
              </div>
            </div>
          )}

          {/* Description */}
          {brief.description && (
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.75, marginBottom: brief.position_description ? 20 : 0 }}>
              {brief.description}
            </div>
          )}
        </div>

        {/* Position Description */}
        {brief.position_description && (
          <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px 32px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontFamily: "DM Mono, monospace" }}>About the role</div>
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{brief.position_description}</div>
          </div>
        )}

        {/* EVP */}
        {brief.evp && (
          <div style={{ background: "linear-gradient(135deg, rgba(91,110,175,0.06), rgba(91,110,175,0.02))", borderRadius: 16, padding: "24px 32px", marginBottom: 16, border: "1px solid rgba(91,110,175,0.12)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5B6EAF", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, fontFamily: "DM Mono, monospace" }}>Why join us</div>
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.75 }}>{brief.evp}</div>
          </div>
        )}

        {/* CTA */}
        <div style={{ background: "linear-gradient(135deg, #1C1917, #3A2A1E)", borderRadius: 16, padding: "28px 32px", marginBottom: 16, textAlign: "center" }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 22, color: "#fff", marginBottom: 8 }}>Know someone perfect for this role?</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 20, lineHeight: 1.6 }}>
            Refer them in 2 minutes. They consent before anyone sees their details.{brief.bonus > 0 ? ` Earn $${brief.bonus.toLocaleString()} if they're placed.` : ""}
          </div>
          <button onClick={() => onRefer(brief)}
            style={{ background: "#5B6EAF", color: "#fff", border: "none", borderRadius: 12, padding: "14px 32px", fontFamily: "inherit", fontWeight: 700, fontSize: 16, cursor: "pointer", marginBottom: 12 }}>
            + Refer Someone for this Role →
          </button>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
            🔒 Consent-first · Candidate privacy protected · No data sold
          </div>
        </div>

        {/* Raise Your Hand */}
        <RaiseYourHandPanel brief={brief} />

        {/* How it works */}
        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px 32px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 16 }}>How PeerRefer works</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              ["1", "You refer someone you know", "Takes 2 minutes — just their name, email and a quick note about them."],
              ["2", "They give consent", "Your referral gets an email asking if they'd like to be considered. Their details stay private until they agree."],
              ["3", "Recruiter reviews their profile", "Only after consent does the recruiter see their details and reach out."],
              [brief.bonus > 0 ? "4" : "4", "You earn a bonus", brief.bonus > 0 ? `If they're placed, you earn $${brief.bonus.toLocaleString()}.` : "Your reputation score grows with every successful referral."],
            ].map(([n, title, desc]) => (
              <div key={n} style={{ display: "flex", gap: 14 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#5B6EAF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{n}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1C1917", marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <PeerReferFooter minimal />
      </div>
    </div>
  );
}

// ============================================
// PRICING PAGE
function PricingPage() {
  const features = [
    "Unlimited job briefs",
    "Full talent pool access — all consented candidates",
    "AI match engine — ranked candidates per role",
    "AI candidate analyser + email drafter",
    "Talent Passport™ — candidate profiles reused across referrals",
    "Consent-first referral flow",
    "Referrer reputation scoring",
    "Pipeline tracking (Shortlisted → Hired)",
    "Warm intro requests with referrers",
    "Share job briefs on LinkedIn, WhatsApp, Email",
    "Mobile optimised — works on any device",
    "Admin dashboard + relationship graph",
    "Email notifications (referrers + TAs)",
    "Stripe-secured billing — cancel anytime",
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#F8F5F2", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: "#FFFFFF", borderBottom: "1px solid rgba(0,0,0,0.06)", padding: "0 32px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, background: "#1C1917", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 16 }}>PeerRefer</div>
        </div>
        <a href="/" style={{ background: "#1C1917", color: "#fff", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>Sign In →</a>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "60px 24px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-block", background: "rgba(91,110,175,0.1)", color: "#5B6EAF", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700, fontFamily: "DM Mono, monospace", marginBottom: 16, letterSpacing: "0.06em" }}>SIMPLE PRICING</div>
          <h1 style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 44, color: "#1C1917", letterSpacing: "-0.02em", marginBottom: 12, lineHeight: 1.1 }}>One price.<br />Full access.</h1>
          <p style={{ fontSize: 16, color: "#6B7280", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
            No tiers. No per-seat fees. No hidden costs. Everything included for $49/month — and it pays for itself the moment you make your first placement.
          </p>
        </div>

        {/* Pricing card */}
        <div style={{ background: "#FFFFFF", borderRadius: 20, border: "1px solid rgba(0,0,0,0.08)", padding: "40px 40px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", textAlign: "center" }}>
          {/* Trial badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(72,187,120,0.1)", border: "1px solid rgba(72,187,120,0.25)", borderRadius: 20, padding: "5px 14px", marginBottom: 24 }}>
            <span style={{ fontSize: 14 }}>🎁</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>30-day free trial — no credit card required to start</span>
          </div>

          {/* Price */}
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 72, color: "#1C1917", letterSpacing: "-0.03em" }}>$49</span>
            <span style={{ fontSize: 18, color: "#9CA3AF", fontWeight: 500 }}>/month</span>
          </div>
          <div style={{ fontSize: 14, color: "#9CA3AF", marginBottom: 32 }}>Billed monthly · Cancel anytime · No lock-in</div>

          {/* Features */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", marginBottom: 36, textAlign: "left" }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#5B6EAF", fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a href="/" style={{ display: "block", background: "#1C1917", color: "#FAFAF8", textDecoration: "none", textAlign: "center", padding: "16px 32px", borderRadius: 12, fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
            Start Free 30-Day Trial →
          </a>
          <div style={{ fontSize: 12, color: "#9CA3AF" }}>No credit card needed for trial · $49/month after · Cancel anytime</div>
        </div>

        {/* Enterprise note */}
        <div style={{ background: "linear-gradient(135deg, #1C1917, #3A2A1E)", borderRadius: 16, padding: "24px 28px", color: "#fff", textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Running an agency or hiring at scale?</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 16, lineHeight: 1.6 }}>We offer custom pricing for recruitment agencies and enterprise teams with multiple talent acquisition professionals.</div>
          <a href="mailto:hello@peerrefer.com" style={{ display: "inline-block", background: "rgba(255,255,255,0.1)", color: "#fff", textDecoration: "none", padding: "10px 24px", borderRadius: 9, fontWeight: 700, fontSize: 13, border: "1px solid rgba(255,255,255,0.15)" }}>Contact us →</a>
        </div>

        {/* FAQ */}
        <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)", padding: "28px 32px", marginBottom: 24 }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, marginBottom: 20 }}>Common questions</div>
          {[
            ["What happens after the 30-day trial?", "Your card is charged $49 automatically. You'll get a reminder email 3 days before the trial ends. Cancel anytime from Settings before then and you won't be charged."],
            ["Can I cancel anytime?", "Yes. Go to Settings → Manage Billing → Cancel subscription. You keep access until the end of your billing period."],
            ["What is a referral bonus?", "You set the bonus amount per role. If a referrer's candidate gets placed, you pay them the bonus directly. PeerRefer doesn't take a cut of bonuses — we only charge the monthly subscription."],
            ["Is candidate data secure?", "Every candidate consents before their details are shared with you. Data is encrypted, stored securely via Supabase, and never sold. Declined referrals are automatically deleted after 90 days."],
            ["Do referrers pay anything?", "No. Referrers use PeerRefer completely free. They earn bonuses when their referrals are placed — you set the bonus amount."],
          ].map(([q, a], i) => (
            <div key={i} style={{ paddingBottom: 16, marginBottom: 16, borderBottom: i < 4 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1C1917", marginBottom: 6 }}>{q}</div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.7 }}>{a}</div>
            </div>
          ))}
        </div>

        <PeerReferFooter minimal />
      </div>
    </div>
  );
}

// ============================================
// ADMIN LOGIN
function AdminLogin({ onAuth, onExit }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  return (
    <div style={{ minHeight: "100vh", background: "#0F0A0D", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      <div style={{ background: "#1A0D12", borderRadius: 16, padding: "40px 36px", width: "100%", maxWidth: 380, border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 22, color: "#E8896A", marginBottom: 4 }}>PeerRefer</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B2D3E", background: "rgba(107,45,62,0.3)", padding: "2px 8px", borderRadius: 4, fontFamily: "DM Mono, monospace", letterSpacing: "0.1em", display: "inline-block", marginBottom: 24 }}>ADMIN ACCESS</div>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { if (password === "PeerRefer2026!") { onAuth(); } else { setError("Incorrect password."); } } }}
          placeholder="Admin password"
          style={{ width: "100%", background: "#0F0A0D", border: "1.5px solid rgba(255,255,255,0.1)", borderRadius: 9, padding: "11px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#FAFAF8", marginBottom: 12, boxSizing: "border-box" }}
          autoFocus
        />
        {error && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 12 }}>{error}</div>}
        <button onClick={() => { if (password === "PeerRefer2026!") { onAuth(); } else { setError("Incorrect password."); } }}
          style={{ width: "100%", background: "#E8896A", color: "#fff", border: "none", borderRadius: 9, padding: "12px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer", marginBottom: 10 }}>
          Enter Admin →
        </button>
        <button onClick={onExit} style={{ width: "100%", background: "none", border: "none", color: "#6B7280", fontFamily: "inherit", fontSize: 12, cursor: "pointer", padding: "6px" }}>← Back to PeerRefer</button>
      </div>
    </div>
  );
}

// ============================================
// ADMIN DASHBOARD
// ============================================
function AdminDashboard({ onExit }) {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({ tas: [], referrers: [], referrals: [], candidates: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [tasRes, referrersRes, referralsRes, candidatesRes] = await Promise.all([
        supabase.from("ta_profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("referrers").select("*").order("reputation_score", { ascending: false }),
        supabase.from("referrals").select("*, referrers(name, email), ta_profiles(name, company)").order("created_at", { ascending: false }),
        supabase.from("candidates").select("*").order("created_at", { ascending: false }),
      ]);
      setData({
        tas: tasRes.data || [],
        referrers: referrersRes.data || [],
        referrals: referralsRes.data || [],
        candidates: candidatesRes.data || [],
      });
      setLoading(false);
    };
    load();
  }, []);

  const stats = {
    totalTAs: data.tas.length,
    proTAs: data.tas.filter(t => t.is_pro).length,
    totalReferrers: data.referrers.length,
    totalReferrals: data.referrals.length,
    consented: data.referrals.filter(r => r.consented).length,
    placed: data.referrals.filter(r => r.status === "placed").length,
    mrr: data.tas.filter(t => t.is_pro).length * 49,
    avgDaysToHire: (() => {
      const withDays = data.referrals.filter(r => r.days_to_hire > 0);
      return withDays.length > 0 ? Math.round(withDays.reduce((a, r) => a + r.days_to_hire, 0) / withDays.length) : null;
    })(),
    disputes: data.referrals.filter(r => r.bonus_disputed).length,
  };

  const suspend = async (table, id, suspended) => {
    await supabase.from(table).update({ suspended: !suspended }).eq("id", id);
    setData(prev => ({
      ...prev,
      [table === "ta_profiles" ? "tas" : "referrers"]: prev[table === "ta_profiles" ? "tas" : "referrers"].map(r => r.id === id ? { ...r, suspended: !suspended } : r),
    }));
  };

  const tabs = [["overview", "📊 Overview"], ["tas", "🏢 TAs"], ["referrers", "🤝 Referrers"], ["referrals", "📋 Referrals"], ["graph", "🕸 Relationship Graph"]];

  return (
    <div style={{ minHeight: "100vh", background: "#0F0A0D", fontFamily: "Inter, DM Sans, system-ui, sans-serif", color: "#FAFAF8" }}>
      {/* Header */}
      <div style={{ background: "#1A0D12", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 28px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 18, color: "#E8896A" }}>PeerRefer</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6B2D3E", background: "rgba(107,45,62,0.3)", padding: "2px 8px", borderRadius: 4, fontFamily: "DM Mono, monospace", letterSpacing: "0.1em" }}>ADMIN</div>
        </div>
        <button onClick={onExit} style={{ background: "rgba(255,255,255,0.06)", border: "none", color: "#9CA3AF", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>← Exit Admin</button>
      </div>

      {/* Tab bar */}
      <div style={{ background: "#1A0D12", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 28px", display: "flex", gap: 2 }}>
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ background: "none", border: "none", borderBottom: tab === k ? "2px solid #E8896A" : "2px solid transparent", padding: "12px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: tab === k ? "#E8896A" : "#6B7280", cursor: "pointer" }}>{l}</button>
        ))}
      </div>

      <div style={{ padding: "28px 28px", maxWidth: 1200, margin: "0 auto" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#6B7280" }}><Spin color="#E8896A" /> Loading...</div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {tab === "overview" && (
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, marginBottom: 20 }}>Platform Overview</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 28 }}>
                  {[
                    { label: "Total TAs", value: stats.totalTAs, sub: `${stats.proTAs} Pro`, color: "#5B6EAF" },
                    { label: "Total Referrers", value: stats.totalReferrers, sub: "Active network", color: "#E8896A" },
                    { label: "Total Referrals", value: stats.totalReferrals, sub: `${stats.consented} consented · ${stats.placed} placed`, color: "#48BB78" },
                    { label: "MRR", value: `$${stats.mrr}`, sub: `${stats.proTAs} paying TAs × $49`, color: "#6B4FBB" },
                    { label: "Avg Days to Hire", value: stats.avgDaysToHire ? `${stats.avgDaysToHire}d` : "—", sub: "Referral to placement", color: "#C47D2B" },
                    { label: "Bonus Disputes", value: stats.disputes, sub: stats.disputes > 0 ? "⚠️ Needs attention" : "All clear", color: stats.disputes > 0 ? "#EF4444" : "#48BB78" },
                  ].map((s, i) => (
                    <div key={i} style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{s.label}</div>
                      <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 800, fontSize: 32, color: s.color, marginBottom: 4 }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Conversion funnel */}
                <div style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)", marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#FAFAF8", marginBottom: 16 }}>Conversion Funnel</div>
                  <div style={{ display: "flex", gap: 0 }}>
                    {[
                      { label: "Referred", value: stats.totalReferrals, color: "#5B6EAF" },
                      { label: "Consented", value: stats.consented, color: "#E8896A" },
                      { label: "Placed", value: stats.placed, color: "#48BB78" },
                    ].map((s, i) => {
                      const pct = stats.totalReferrals > 0 ? Math.round((s.value / stats.totalReferrals) * 100) : 0;
                      return (
                        <div key={i} style={{ flex: 1, padding: "14px 16px", background: `${s.color}15`, borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace", marginBottom: 4 }}>{s.label}</div>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 28, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{pct}% of total</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recent activity */}
                <div style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#FAFAF8", marginBottom: 14 }}>Recent Referrals</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {data.referrals.slice(0, 8).map(r => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#2A1520", color: "#E8896A", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{inits(r.candidate_name)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{r.candidate_name}</div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>via {r.referrers?.name || "Unknown"} → {r.ta_profiles?.company || "Unknown"}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: r.consented ? "rgba(72,187,120,0.15)" : "rgba(255,255,255,0.06)", color: r.consented ? "#48BB78" : "#6B7280" }}>{r.consented ? "Consented" : r.status}</span>
                          <span style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace" }}>{new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TAs TAB */}
            {tab === "tas" && (
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, marginBottom: 20 }}>Talent Acquisition Accounts ({data.tas.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.tas.map(ta => (
                    <div key={ta.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "#1A0D12", borderRadius: 12, border: `1px solid ${ta.suspended ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.06)"}` }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#2A1520", color: "#E8896A", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{inits(ta.name)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{ta.name} <span style={{ fontSize: 11, color: "#6B7280" }}>· {ta.company}</span></div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{ta.email} · slug: {ta.slug} · joined {new Date(ta.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: ta.is_pro ? "rgba(91,110,175,0.2)" : "rgba(255,255,255,0.06)", color: ta.is_pro ? "#8DA4E8" : "#6B7280" }}>{ta.is_pro ? "✓ PRO" : "FREE"}</span>
                        {ta.rating_avg > 0 && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(232,137,106,0.15)", color: "#E8896A" }}>⭐ {Number(ta.rating_avg).toFixed(1)} ({ta.rating_count})</span>}
                        {ta.suspended && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(239,68,68,0.15)", color: "#EF4444" }}>SUSPENDED</span>}
                        <button onClick={() => suspend("ta_profiles", ta.id, ta.suspended)}
                          style={{ background: ta.suspended ? "rgba(72,187,120,0.1)" : "rgba(239,68,68,0.1)", border: "none", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer", color: ta.suspended ? "#48BB78" : "#EF4444" }}>
                          {ta.suspended ? "Reinstate" : "Suspend"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* REFERRERS TAB */}
            {tab === "referrers" && (
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, marginBottom: 20 }}>Referrer Network ({data.referrers.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.referrers.map(r => (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "#1A0D12", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#2A1520", color: "#E8896A", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{inits(r.name)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{r.email} · joined {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</div>
                      </div>
                      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#E8896A" }}>{r.reputation_score || 50}</div>
                          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace" }}>REP</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#FAFAF8" }}>{r.total_referred || 0}</div>
                          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace" }}>REFERRED</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 20, color: "#48BB78" }}>{r.total_placed || 0}</div>
                          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace" }}>PLACED</div>
                        </div>
                        <button onClick={() => suspend("referrers", r.id, r.suspended)}
                          style={{ background: r.suspended ? "rgba(72,187,120,0.1)" : "rgba(239,68,68,0.1)", border: "none", borderRadius: 7, padding: "5px 12px", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer", color: r.suspended ? "#48BB78" : "#EF4444" }}>
                          {r.suspended ? "Reinstate" : "Suspend"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* REFERRALS TAB */}
            {tab === "referrals" && (
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, marginBottom: 20 }}>All Referrals ({data.referrals.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {data.referrals.map(r => (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#1A0D12", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{r.candidate_name}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>via <span style={{ color: "#E8896A" }}>{r.referrers?.name || "Unknown referrer"}</span> → <span style={{ color: "#8DA4E8" }}>{r.ta_profiles?.company || "Unknown TA"}</span>{r.brief_title ? ` · ${r.brief_title}` : ""}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                        <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: r.consented ? "rgba(72,187,120,0.15)" : "rgba(255,255,255,0.06)", color: r.consented ? "#48BB78" : "#6B7280" }}>
                          {r.consented === true ? "✓ Consented" : r.consented === false ? "✗ Declined" : "Pending"}
                        </span>
                        <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: "DM Mono, monospace", background: "rgba(255,255,255,0.06)", color: "#9CA3AF" }}>{r.pipeline_status || r.status}</span>
                        <span style={{ fontSize: 10, color: "#6B7280", fontFamily: "DM Mono, monospace" }}>{new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RELATIONSHIP GRAPH TAB */}
            {tab === "graph" && (
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 24, marginBottom: 8 }}>Relationship Graph</div>
                <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>Who referred who — the trust network behind every placement.</div>

                {/* Top referrers */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  <div style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#E8896A", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Top Connectors</div>
                    {data.referrers.slice(0, 8).map((r, i) => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: i < 3 ? "#E8896A" : "#2A1520", color: i < 3 ? "#fff" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                          <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 4 }}>
                            <div style={{ height: "100%", background: "#E8896A", borderRadius: 2, width: `${Math.min(((r.total_referred || 0) / (data.referrers[0]?.total_referred || 1)) * 100, 100)}%` }} />
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: "#E8896A", fontWeight: 700, fontFamily: "DM Mono, monospace" }}>{r.total_referred || 0}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#48BB78", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Best Placement Rate</div>
                    {data.referrers
                      .filter(r => (r.total_referred || 0) > 0)
                      .sort((a, b) => ((b.total_placed || 0) / (b.total_referred || 1)) - ((a.total_placed || 0) / (a.total_referred || 1)))
                      .slice(0, 8)
                      .map((r, i) => {
                        const rate = Math.round(((r.total_placed || 0) / (r.total_referred || 1)) * 100);
                        return (
                          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <div style={{ width: 24, height: 24, borderRadius: "50%", background: i < 3 ? "#48BB78" : "#2A1520", color: i < 3 ? "#fff" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>{r.total_placed || 0} placed of {r.total_referred || 0} referred</div>
                            </div>
                            <div style={{ fontSize: 13, color: "#48BB78", fontWeight: 700, fontFamily: "DM Mono, monospace" }}>{rate}%</div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Referral connections */}
                <div style={{ background: "#1A0D12", borderRadius: 12, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#8DA4E8", fontFamily: "DM Mono, monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Referral Connections</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {data.referrals.filter(r => r.consented).map(r => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                        <div style={{ fontSize: 12, color: "#E8896A", fontWeight: 600 }}>{r.referrers?.name || "?"}</div>
                        <div style={{ fontSize: 12, color: "#6B7280" }}>→</div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{r.candidate_name}</div>
                        <div style={{ fontSize: 12, color: "#6B7280" }}>→</div>
                        <div style={{ fontSize: 12, color: "#8DA4E8", fontWeight: 600 }}>{r.ta_profiles?.company || "?"}</div>
                        {r.status === "placed" && <span style={{ fontSize: 10, background: "rgba(72,187,120,0.15)", color: "#48BB78", padding: "2px 8px", borderRadius: 20, fontFamily: "DM Mono, monospace", fontWeight: 700 }}>✓ PLACED</span>}
                        {r.brief_title && <span style={{ fontSize: 10, color: "#6B7280", marginLeft: "auto" }}>📋 {r.brief_title}</span>}
                      </div>
                    ))}
                    {data.referrals.filter(r => r.consented).length === 0 && (
                      <div style={{ fontSize: 13, color: "#6B7280", textAlign: "center", padding: "20px 0" }}>No consented referrals yet — connections will appear here.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
function TrustMap({ referral, allReferrals }) {
  // Find all other referrals for the same candidate email
  const sameCandidate = allReferrals.filter(r =>
    r.id !== referral.id &&
    r.candidateEmail &&
    referral.candidateEmail &&
    r.candidateEmail.toLowerCase() === referral.candidateEmail.toLowerCase()
  );

  const totalVouches = sameCandidate.length + 1;
  const allReferrers = [
    { name: referral.referrerName, rel: referral.referrerRelationship, placed: referral.referrerStats?.placed || 0, total: referral.referrerStats?.total || 0, verified: referral.referrerLinkedinVerified, isPrimary: true },
    ...sameCandidate.map(r => ({ name: r.referrerName, rel: r.referrerRelationship, placed: r.referrerStats?.placed || 0, total: r.referrerStats?.total || 0, verified: r.referrerLinkedinVerified, isPrimary: false }))
  ];

  const trustColor = totalVouches >= 3 ? "#059669" : totalVouches === 2 ? "#D97706" : "#6B7280";
  const trustBg = totalVouches >= 3 ? "rgba(5,150,105,0.07)" : totalVouches === 2 ? "rgba(217,119,6,0.07)" : "rgba(107,114,128,0.07)";
  const trustBorder = totalVouches >= 3 ? "rgba(5,150,105,0.2)" : totalVouches === 2 ? "rgba(217,119,6,0.2)" : "rgba(107,114,128,0.15)";
  const trustLabel = totalVouches >= 3 ? "Strong trust signal" : totalVouches === 2 ? "Multiple vouches" : "Single vouch";

  const relIcon = (rel) => {
    if (!rel) return "👤";
    const r = rel.toLowerCase();
    if (r.includes("manager") || r.includes("director") || r.includes("lead")) return "👔";
    if (r.includes("colleague") || r.includes("coworker") || r.includes("team")) return "🤝";
    if (r.includes("university") || r.includes("school") || r.includes("college") || r.includes("study")) return "🎓";
    if (r.includes("client") || r.includes("customer")) return "💼";
    if (r.includes("mentor") || r.includes("coach")) return "⭐";
    if (r.includes("friend")) return "👥";
    return "🔗";
  };

  return (
    <div style={{ background: trustBg, border: `1px solid ${trustBorder}`, borderRadius: 11, padding: "13px 15px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={trustColor} strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/><circle cx="4" cy="14" r="2.5"/><circle cx="20" cy="14" r="2.5"/></svg>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace" }}>Trust Map</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: trustColor }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: trustColor, fontFamily: "DM Mono, monospace" }}>{trustLabel}</span>
        </div>
      </div>

      {/* Candidate node */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1C1917", color: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
          {(referral.candidateName || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2)}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#1C1917" }}>{referral.blurred ? "••••••" : referral.candidateName}</div>
        <div style={{ fontSize: 10, color: "#9CA3AF" }}>candidate</div>
      </div>

      {/* Connection lines + referrer nodes */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {allReferrers.map((ref, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: ref.isPrimary ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)", border: ref.isPrimary ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(0,0,0,0.06)", borderRadius: 9, padding: "9px 11px" }}>
            {/* Line connector */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 2, flexShrink: 0 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: ref.isPrimary ? "#FAECE7" : "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                {relIcon(ref.rel)}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#1C1917" }}>{ref.name}</span>
                {ref.verified && (
                  <span style={{ background: "rgba(10,102,194,0.1)", borderRadius: 8, padding: "1px 5px", fontSize: 9, color: "#0A66C2", fontWeight: 700, fontFamily: "DM Mono, monospace" }}>LI ✓</span>
                )}
                {ref.isPrimary && (
                  <span style={{ background: "#FAECE7", borderRadius: 8, padding: "1px 6px", fontSize: 9, color: "#712B13", fontWeight: 700 }}>this referral</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>{ref.rel || "Professional contact"}</div>
              {ref.placed > 0 && (
                <div style={{ fontSize: 10, color: "#E8896A", fontFamily: "DM Mono, monospace", fontWeight: 600 }}>⭐ {ref.placed}/{ref.total} placed</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalVouches >= 2 && (
        <div style={{ marginTop: 10, padding: "7px 10px", background: "rgba(255,255,255,0.6)", borderRadius: 7, fontSize: 11, color: trustColor, fontWeight: 600, textAlign: "center" }}>
          {totalVouches} people in your network know {referral.blurred ? "this candidate" : referral.candidateName.split(" ")[0]}
        </div>
      )}
    </div>
  );
}


// ============================================
function PublicReferrerPage({ referrerId }) {
  const [referrer, setReferrer] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: ref } = await supabase.from("referrers")
          .select("*").eq("id", referrerId).single();
        if (ref) {
          setReferrer(ref);
          // Load their public referral stats (no personal data)
          const { data: refs } = await supabase.from("referrals")
            .select("id, status, pipeline_status, brief_title, created_at, consented")
            .eq("referrer_id", referrerId)
            .not("consented", "is", null);
          setReferrals(refs || []);
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [referrerId]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0D0608", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>Loading...</div>
    </div>
  );

  if (!referrer) return (
    <div style={{ minHeight: "100vh", background: "#0D0608", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 32 }}>🔍</div>
      <div style={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }}>Profile not found</div>
      <button onClick={() => window.location.href = "/"} style={{ marginTop: 8, background: "#D85A30", border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Back to PeerRefer</button>
    </div>
  );

  const tier = getReferrerTier(referrer.total_placed || 0);
  const score = referrer.reputation_score || 50;
  const totalReferred = referrer.total_referred || 0;
  const totalPlaced = referrer.total_placed || 0;
  const placementRate = totalReferred > 0 ? Math.round((totalPlaced / totalReferred) * 100) : 0;
  const profileUrl = `https://www.peerrefer.com?referrer=${referrerId}`;
  const initials = (referrer.name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2);

  const PR_STYLES = `
    .pr-root { min-height: 100vh; background: #0D0608; font-family: Inter, system-ui, sans-serif; color: #FFFFFF; }
    .pr-nav { display: flex; align-items: center; justify-content: space-between; padding: 18px 48px; border-bottom: 1px solid rgba(255,255,255,0.06); }
    .pr-hero { max-width: 720px; margin: 0 auto; padding: 56px 48px 40px; }
    .pr-avatar { width: 88px; height: 88px; border-radius: 50%; background: linear-gradient(135deg, #5B6EAF, #3A4E8F); display: flex; align-items: center; justify-content: center; font-family: Playfair Display, Georgia, serif; font-weight: 800; font-size: 32px; color: #FFFFFF; margin-bottom: 20px; border: 3px solid rgba(255,255,255,0.1); }
    .pr-name { font-family: Playfair Display, Georgia, serif; font-size: 36px; font-weight: 800; color: #FFFFFF; letter-spacing: -0.03em; margin-bottom: 8px; }
    .pr-tagline { font-size: 15px; color: rgba(255,255,255,0.5); margin-bottom: 24px; line-height: 1.6; }
    .pr-badges { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 32px; }
    .pr-badge { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; }
    .pr-stats { max-width: 720px; margin: 0 auto; padding: 0 48px 48px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
    .pr-stat { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 20px; text-align: center; }
    .pr-stat-n { font-size: 32px; font-weight: 800; color: #FFFFFF; letter-spacing: -0.04em; font-family: Playfair Display, Georgia, serif; }
    .pr-stat-l { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 4px; font-family: DM Mono, monospace; text-transform: uppercase; letter-spacing: 0.06em; }
    .pr-body { max-width: 720px; margin: 0 auto; padding: 0 48px 80px; }
    .pr-score-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 28px; margin-bottom: 20px; }
    .pr-score-title { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px; font-family: DM Mono, monospace; }
    .pr-score-ring { display: flex; align-items: center; gap: 24px; }
    .pr-recent { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 28px; margin-bottom: 20px; }
    .pr-referral-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
    .pr-referral-row:last-child { border-bottom: none; }
    .pr-cta { background: #D85A30; border: none; border-radius: 12px; padding: 16px 28px; font-size: 15px; font-weight: 700; color: #FFFFFF; cursor: pointer; font-family: inherit; transition: all 0.18s; width: 100%; }
    .pr-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(216,90,48,0.4); }
    .pr-share-row { display: flex; gap: 10px; margin-top: 12px; }
    .pr-share-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; padding: 11px; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.7); cursor: pointer; background: transparent; font-family: inherit; transition: all 0.2s; text-decoration: none; }
    .pr-share-btn:hover { background: rgba(255,255,255,0.07); }
    @media (max-width: 600px) {
      .pr-nav { padding: 14px 20px; }
      .pr-hero { padding: 40px 20px 28px; }
      .pr-stats { grid-template-columns: repeat(2, 1fr); padding: 0 20px 32px; }
      .pr-body { padding: 0 20px 60px; }
      .pr-name { font-size: 28px; }
    }
  `;

  // Score ring SVG
  const r = 54, sw = 7, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;

  return (
    <div className="pr-root">
      <style>{PR_STYLES}</style>

      <nav className="pr-nav">
        <div onClick={() => window.location.href = "/"} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="8" fill="#5B6EAF"/><text x="18" y="27" fontFamily="Georgia, serif" fontSize="26" fontWeight="700" fill="#FFFFFF" textAnchor="middle">P</text></svg>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.02em" }}>PeerRefer</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>by CollabPathways</div>
          </div>
        </div>
        <button onClick={() => window.location.href = "/"} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "8px 16px", color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Join PeerRefer
        </button>
      </nav>

      <div className="pr-hero">
        <div className="pr-avatar">{initials}</div>
        <div className="pr-name">{referrer.name}</div>
        <div className="pr-tagline">Trusted talent connector on PeerRefer{referrer.location ? ` · ${referrer.location}` : ""}</div>

        <div className="pr-badges">
          <span className="pr-badge" style={{ background: tier.bg || "rgba(255,255,255,0.08)", color: tier.color || "#FFFFFF" }}>
            {tier.icon} {tier.name} Referrer
          </span>
          {referrer.linkedin_verified && (
            <span className="pr-badge" style={{ background: "rgba(10,102,194,0.15)", color: "#60A5FA" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn Verified
            </span>
          )}
          <span className="pr-badge" style={{ background: "rgba(216,90,48,0.15)", color: "#E8896A" }}>
            🎯 {placementRate}% placement rate
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="pr-stats">
        {[
          { n: totalReferred, l: "Referred" },
          { n: totalPlaced, l: "Placed" },
          { n: `${placementRate}%`, l: "Success rate" },
          { n: score, l: "Connector score" },
        ].map((s, i) => (
          <div key={i} className="pr-stat">
            <div className="pr-stat-n">{s.n}</div>
            <div className="pr-stat-l">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="pr-body">
        {/* Connector score */}
        <div className="pr-score-card">
          <div className="pr-score-title">Connector Score</div>
          <div className="pr-score-ring">
            <svg width="124" height="124" style={{ flexShrink: 0 }}>
              <circle cx="62" cy="62" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={sw}/>
              <circle cx="62" cy="62" r={r} fill="none" stroke="#D85A30" strokeWidth={sw}
                strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                transform="rotate(-90 62 62)" style={{ transition: "stroke-dashoffset 1s ease" }}/>
              <text x="62" y="58" textAnchor="middle" fontSize="28" fontWeight="800" fill="#FFFFFF" fontFamily="Playfair Display, Georgia, serif">{score}</text>
              <text x="62" y="76" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.4)" fontFamily="sans-serif">/100</text>
            </svg>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#FFFFFF", marginBottom: 8 }}>
                {score >= 80 ? "Elite connector" : score >= 60 ? "Active referrer" : score >= 40 ? "Rising connector" : "New referrer"}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
                {score >= 80
                  ? "This person consistently refers high-quality candidates who get placed. Their referrals are worth your attention."
                  : score >= 60
                  ? "An active and trusted referrer with a solid track record on PeerRefer."
                  : score >= 40
                  ? "Building their referral track record. Early signals look positive."
                  : "New to PeerRefer. Every connector starts here."}
              </div>
              {tier.nextAt && (
                <div style={{ marginTop: 12, fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
                  {tier.nextAt - totalPlaced} more placement{tier.nextAt - totalPlaced !== 1 ? "s" : ""} to reach {tier.name === "Bronze" ? "Silver" : tier.name === "Silver" ? "Gold" : "Elite"} tier
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent activity */}
        {referrals.length > 0 && (
          <div className="pr-recent">
            <div className="pr-score-title">Recent referrals</div>
            {referrals.slice(0, 5).map((ref, i) => (
              <div key={i} className="pr-referral-row">
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#FFFFFF" }}>{ref.brief_title || "Open role"}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
                    {new Date(ref.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: ref.pipeline_status === "hired" ? "#059669" : ref.pipeline_status === "shortlisted" ? "#3B82F6" : ref.consented ? "#E8896A" : "#6B7280" }} />
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", fontFamily: "DM Mono, monospace" }}>
                    {ref.pipeline_status === "hired" ? "Placed ✓" : ref.pipeline_status === "shortlisted" ? "Shortlisted" : ref.pipeline_status === "interviewed" ? "Interviewed" : ref.consented ? "In review" : "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: 28 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF", fontFamily: "Playfair Display, Georgia, serif", marginBottom: 8 }}>
            Know someone great?
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", marginBottom: 20, lineHeight: 1.6 }}>
            Refer someone you know to an open role. Candidate consent required before any details are shared.
          </div>
          <button className="pr-cta" onClick={() => {
            // Store intent to open refer modal after login
            localStorage.setItem("peerrefer_open_refer", "true");
            window.location.href = "/";
          }}>
            Refer someone on PeerRefer →
          </button>
          <div className="pr-share-row">
            <button className="pr-share-btn" onClick={() => { navigator.clipboard?.writeText(profileUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              🔗 {copied ? "Copied!" : "Copy profile link"}
            </button>
            <button className="pr-share-btn" onClick={() => {
              const metaUrl = `https://www.peerrefer.com/.netlify/functions/referrer-meta?referrerId=${referrerId}`;
              window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(metaUrl)}`, "_blank");
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              Share on LinkedIn
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "rgba(255,255,255,0.2)" }}>
          Powered by PeerRefer · Consent-first talent referrals · peerrefer.com
        </div>
      </div>
    </div>
  );
}


// ============================================
function PublicBriefPage({ briefId, taSlug }) {
  const [brief, setBrief] = useState(null);
  const [ta, setTA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: briefData } = await supabase.from("briefs").select("*").eq("id", briefId).single();
        const { data: taData } = await supabase.from("ta_profiles").select("name, company, looking_for, location, industries, referrer_note, slug, logo_url").eq("slug", taSlug).maybeSingle();
        setBrief(briefData);
        setTA(taData);

        // Inject dynamic OG meta tags for LinkedIn/social sharing
        if (briefData) {
          const ogImageUrl = `https://www.peerrefer.com/.netlify/functions/og-image?briefId=${briefId}&taSlug=${taSlug}`;
          const ogTitle = `${briefData.title || "Open Role"}${taData?.company ? ` at ${taData.company}` : ""}`;
          const ogDesc = `Refer someone you know for this role. Candidate consent required before any details are shared. ${briefData.bonus ? `$${Number(briefData.bonus).toLocaleString()} referral bonus.` : ""} Powered by PeerRefer.`;
          const ogUrl = `https://www.peerrefer.com?briefId=${briefId}&refer=${taSlug}`;

          // Update or create meta tags
          const setMeta = (prop, val, attr = "property") => {
            let el = document.querySelector(`meta[${attr}="${prop}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
            el.setAttribute("content", val);
          };
          document.title = ogTitle + " · PeerRefer";
          setMeta("og:title", ogTitle);
          setMeta("og:description", ogDesc);
          setMeta("og:image", ogImageUrl);
          setMeta("og:url", ogUrl);
          setMeta("og:type", "website");
          setMeta("twitter:card", "summary_large_image", "name");
          setMeta("twitter:title", ogTitle, "name");
          setMeta("twitter:description", ogDesc, "name");
          setMeta("twitter:image", ogImageUrl, "name");
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [briefId, taSlug]);

  const shareUrl = `https://www.peerrefer.com?briefId=${briefId}&refer=${taSlug}`;
  const linkedInText = encodeURIComponent(`I'm personally looking for a ${brief?.title || "great candidate"} ${brief?.location ? `in ${brief.location}` : ""}.

If you know someone exceptional, refer them through my PeerRefer link. Their details stay private until they give consent.

${shareUrl}`);

  const handleCopy = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const PB_STYLES = `
    .pb-root { min-height: 100vh; background: #0D0608; font-family: Inter, DM Sans, system-ui, sans-serif; color: #FFFFFF; }
    .pb-nav { display: flex; align-items: center; justify-content: space-between; padding: 18px 48px; border-bottom: 1px solid rgba(255,255,255,0.06); }
    .pb-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; cursor: pointer; }
    .pb-wordmark { font-size: 15px; font-weight: 700; color: #FFFFFF; letter-spacing: -0.02em; }
    .pb-parent { font-size: 10px; color: rgba(255,255,255,0.3); }
    .pb-hero { max-width: 800px; margin: 0 auto; padding: 64px 48px 48px; }
    .pb-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(216,90,48,0.15); border: 1px solid rgba(216,90,48,0.3); border-radius: 20px; padding: 5px 14px; font-size: 11px; font-weight: 600; color: #E8896A; margin-bottom: 24px; letter-spacing: 0.02em; }
    .pb-badge-dot { width: 5px; height: 5px; border-radius: 50%; background: #E8896A; animation: pbPulse 2s ease-in-out infinite; }
    @keyframes pbPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(1.4); } }
    .pb-title { font-family: Playfair Display, Georgia, serif; font-size: 48px; font-weight: 800; line-height: 1.1; letter-spacing: -0.03em; color: #FFFFFF; margin-bottom: 16px; }
    .pb-title em { color: #E8896A; font-style: normal; }
    .pb-meta { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-bottom: 40px; }
    .pb-meta-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: rgba(255,255,255,0.5); }
    .pb-meta-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(255,255,255,0.2); }
    .pb-body { max-width: 800px; margin: 0 auto; padding: 0 48px 80px; display: grid; grid-template-columns: 1fr 320px; gap: 32px; align-items: start; }
    .pb-main {}
    .pb-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 28px; margin-bottom: 20px; }
    .pb-card-title { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 16px; }
    .pb-desc { font-size: 15px; color: rgba(255,255,255,0.75); line-height: 1.75; }
    .pb-skills { display: flex; flex-wrap: wrap; gap: 8px; }
    .pb-skill { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 5px 13px; font-size: 12px; color: rgba(255,255,255,0.7); }
    .pb-sidebar {}
    .pb-ta-card { background: #FFFFFF; border-radius: 16px; padding: 24px; margin-bottom: 16px; }
    .pb-ta-avatar { width: 48px; height: 48px; border-radius: 50%; background: #5B6EAF; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700; color: #FFFFFF; margin-bottom: 12px; }
    .pb-ta-name { font-size: 15px; font-weight: 700; color: #1C1917; margin-bottom: 2px; }
    .pb-ta-co { font-size: 12px; color: #6B7280; margin-bottom: 12px; }
    .pb-ta-note { font-size: 12px; color: #6B7280; line-height: 1.6; font-style: italic; border-top: 1px solid rgba(0,0,0,0.07); padding-top: 12px; }
    .pb-consent-badge { display: flex; align-items: center; gap: 8px; background: rgba(5,150,105,0.08); border: 1px solid rgba(5,150,105,0.2); border-radius: 10px; padding: 10px 14px; margin-bottom: 16px; font-size: 12px; color: #059669; font-weight: 600; }
    .pb-cta { background: #D85A30; border: none; border-radius: 12px; padding: 16px; width: 100%; font-size: 14px; font-weight: 700; color: #FFFFFF; cursor: pointer; font-family: inherit; letter-spacing: -0.01em; margin-bottom: 10px; transition: transform 0.18s ease, box-shadow 0.18s ease; }
    .pb-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(216,90,48,0.4); }
    .pb-share-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
    .pb-share-btn { display: flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid rgba(255,255,255,0.15); border-radius: 10px; padding: 10px; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.8); cursor: pointer; background: transparent; font-family: inherit; transition: all 0.2s ease; }
    .pb-share-btn:hover { background: rgba(255,255,255,0.07); border-color: rgba(255,255,255,0.25); }
    .pb-bonus { background: rgba(216,90,48,0.1); border: 1px solid rgba(216,90,48,0.2); border-radius: 12px; padding: 16px; text-align: center; }
    .pb-bonus-label { font-size: 10px; font-weight: 700; color: #E8896A; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 4px; }
    .pb-bonus-amount { font-size: 28px; font-weight: 800; color: #FFFFFF; letter-spacing: -0.03em; }
    .pb-bonus-sub { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 2px; }
    .pb-footer-bar { border-top: 1px solid rgba(255,255,255,0.06); padding: 24px 48px; display: flex; align-items: center; justify-content: space-between; }
    .pb-footer-text { font-size: 11px; color: rgba(255,255,255,0.25); }
    @media (max-width: 768px) {
      .pb-nav { padding: 14px 20px; }
      .pb-hero { padding: 40px 20px 32px; }
      .pb-title { font-size: 32px; }
      .pb-body { grid-template-columns: 1fr; padding: 0 20px 60px; }
      .pb-footer-bar { padding: 20px; flex-direction: column; gap: 8px; text-align: center; }
    }
  `;

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0D0608", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>Loading...</div>
    </div>
  );

  if (!brief) return (
    <div style={{ minHeight: "100vh", background: "#0D0608", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 32 }}>🔍</div>
      <div style={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }}>Role not found or no longer active</div>
      <button onClick={() => window.location.href = "/"} style={{ marginTop: 8, background: "#D85A30", border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Back to PeerRefer</button>
    </div>
  );

  const taInitials = (ta?.name || "TA").split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2);

  return (
    <div className="pb-root">
      <style>{PB_STYLES}</style>

      <nav className="pb-nav">
        <div className="pb-brand" onClick={() => window.location.href = "/"}>
          <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="8" fill="#5B6EAF"/><text x="18" y="27" fontFamily="Georgia, serif" fontSize="26" fontWeight="700" fill="#FFFFFF" textAnchor="middle">P</text></svg>
          <div>
            <div className="pb-wordmark">PeerRefer</div>
            <div className="pb-parent">by CollabPathways</div>
          </div>
        </div>
        <button onClick={() => window.location.href = "/?refer=" + taSlug} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "8px 16px", color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Sign in
        </button>
      </nav>

      <div className="pb-hero">
        <div className="pb-badge"><div className="pb-badge-dot"></div>Active opportunity · {ta?.company || "PeerRefer Network"}</div>
        {ta?.logo_url && <img src={ta.logo_url} alt={ta.company} style={{ height: 40, objectFit: "contain", marginBottom: 20, borderRadius: 6 }} />}
        <h1 className="pb-title">{brief.title || "Untitled Role"}<br/>{brief.location && <em>in {brief.location}</em>}</h1>
        <div className="pb-meta">
          {brief.seniority && <span className="pb-meta-item">{brief.seniority}</span>}
          {brief.seniority && brief.location && <div className="pb-meta-dot"></div>}
          {brief.location && <span className="pb-meta-item">📍 {brief.location}</span>}
          {brief.location && brief.skills?.length > 0 && <div className="pb-meta-dot"></div>}
          {brief.skills?.length > 0 && <span className="pb-meta-item">🎯 {brief.skills.slice(0,3).join(", ")}</span>}
        </div>
      </div>

      <div className="pb-body">
        <div className="pb-main">
          {brief.description && (
            <div className="pb-card">
              <div className="pb-card-title">About this role</div>
              <div className="pb-desc">{brief.description}</div>
            </div>
          )}
          {brief.evp && (
            <div className="pb-card">
              <div className="pb-card-title">Why join</div>
              <div className="pb-desc">{brief.evp}</div>
            </div>
          )}
          {brief.skills?.length > 0 && (
            <div className="pb-card">
              <div className="pb-card-title">Skills & experience</div>
              <div className="pb-skills">
                {brief.skills.map((s, i) => <span className="pb-skill" key={i}>{s}</span>)}
              </div>
            </div>
          )}
          <div className="pb-card" style={{ background: "rgba(5,150,105,0.06)", borderColor: "rgba(5,150,105,0.15)" }}>
            <div className="pb-card-title" style={{ color: "#059669" }}>How referrals work</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { n: "1", t: "You refer someone", b: "Tell us who you know and your relationship to them." },
                { n: "2", t: "Candidate consents first", b: "They receive an email and agree before any details are shared." },
                { n: "3", t: "TA reviews the referral", b: "Only then does the recruiter see the candidate's full profile." },
                { n: "4", t: "You earn a bonus if placed", b: brief.bonus ? `$${brief.bonus.toLocaleString()} if they're hired.` : "Bonus paid on successful placement." },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(5,150,105,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#059669", flexShrink: 0 }}>{s.n}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#FFFFFF", marginBottom: 2 }}>{s.t}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>{s.b}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pb-sidebar">
          {/* TA card */}
          <div className="pb-ta-card">
            <div className="pb-ta-avatar">{taInitials}</div>
            <div className="pb-ta-name">{ta?.name || "Talent Acquisition"}</div>
            <div className="pb-ta-co">{ta?.company || "PeerRefer Network"}</div>
            {ta?.referrer_note && <div className="pb-ta-note">"{ta.referrer_note}"</div>}
          </div>

          {/* Consent badge */}
          <div className="pb-consent-badge">
            🛡 Candidate consent required before CV is shared
          </div>

          {/* Bonus */}
          {brief.bonus > 0 && (
            <div className="pb-bonus" style={{ marginBottom: 16 }}>
              <div className="pb-bonus-label">Referral bonus</div>
              <div className="pb-bonus-amount">A${brief.bonus.toLocaleString()}</div>
              <div className="pb-bonus-sub">Paid on successful placement</div>
            </div>
          )}

          {/* CTA */}
          <button className="pb-cta" onClick={() => window.location.href = `/?briefId=${briefId}&refer=${taSlug}`}>
            Refer someone for this role →
          </button>

          {/* Share buttons */}
          <div className="pb-share-row">
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="pb-share-btn" style={{ textDecoration: "none" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            <button className="pb-share-btn" onClick={handleCopy}>
              {copied ? "✓ Copied!" : "🔗 Copy link"}
            </button>
          </div>

          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", textAlign: "center", lineHeight: 1.5 }}>
            Powered by PeerRefer · Consent-first referrals
          </div>
        </div>
      </div>

      <div className="pb-footer-bar">
        <div className="pb-footer-text">© CollabPathways 2026 · PeerRefer</div>
        <div style={{ display: "flex", gap: 16 }}>
          <a href="/?privacy=true" style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", textDecoration: "none" }}>Privacy</a>
          <a href="/?terms=true" style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", textDecoration: "none" }}>Terms</a>
        </div>
      </div>
    </div>
  );
}


// ============================================
function PortalSelector({ onSelect, preselectBriefId }) {
  useEffect(() => {
    if (preselectBriefId) onSelect("referrer");
  }, []); // eslint-disable-line

  const LP_STYLES = `
    .lp-root { font-family: Inter, DM Sans, system-ui, sans-serif; color: #1C1917; background: #FAFAF8; }
    .lp-nav { display: flex; align-items: center; justify-content: space-between; padding: 18px 48px; border-bottom: 1px solid rgba(0,0,0,0.07); background: #FAFAF8; position: sticky; top: 0; z-index: 100; }
    .lp-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
    .lp-wordmark { font-weight: 700; font-size: 17px; color: #1C1917; letter-spacing: -0.02em; }
    .lp-parent { font-size: 10px; color: #9CA3AF; margin-top: 1px; }
    .lp-nav-links { display: flex; align-items: center; gap: 20px; }
    .lp-nav-link { font-size: 13px; color: #6B7280; cursor: pointer; text-decoration: none; }
    .lp-btn-ghost { background: none; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 7px 16px; font-size: 12px; font-weight: 600; color: #374151; cursor: pointer; font-family: inherit; }
    .lp-btn-coral { background: #D85A30; border: none; border-radius: 8px; padding: 8px 18px; font-size: 12px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; }
    .lp-hero { padding: 72px 48px 64px; display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; max-width: 1100px; margin: 0 auto; }
    .lp-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; border-radius: 20px; background: rgba(216,90,48,0.15); border: 1px solid rgba(216,90,48,0.3); font-size: 11px; font-weight: 600; color: #E8896A; margin-bottom: 22px; letter-spacing: 0.02em; }
    .lp-pill-dot { width: 5px; height: 5px; border-radius: 50%; background: #E8896A; flex-shrink: 0; animation: lpPulse 2s ease-in-out infinite; }
    @keyframes lpPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.6; transform:scale(1.3); } }
    .lp-h1 { font-size: 40px; font-weight: 800; line-height: 1.15; letter-spacing: -0.03em; color: #FFFFFF; margin-bottom: 18px; font-family: Playfair Display, Georgia, serif; }
    .lp-h1 em { font-style: normal; color: #E8896A; }
    .lp-hero-body { font-size: 15px; color: rgba(255,255,255,0.6); line-height: 1.7; margin-bottom: 28px; max-width: 420px; }
    .lp-cta-row { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
    .lp-btn-hero { background: #D85A30; border: none; border-radius: 9px; padding: 13px 26px; font-size: 14px; font-weight: 700; color: #fff; cursor: pointer; font-family: inherit; letter-spacing: -0.01em; }
    .lp-btn-ghost2 { background: none; border: none; font-size: 13px; color: #9CA3AF; cursor: pointer; font-family: inherit; text-decoration: underline; }
    .lp-hero-note { font-size: 11px; color: rgba(255,255,255,0.35); display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .lp-note-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(255,255,255,0.2); }
    .lp-inbox { background: #fff; border: 1px solid rgba(255,255,255,0.1); border-radius: 14px; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05); }
    .lp-inbox-bar { padding: 11px 14px; border-bottom: 1px solid rgba(0,0,0,0.07); display: flex; align-items: center; justify-content: space-between; background: #FAFAF8; }
    .lp-inbox-title { font-size: 11px; font-weight: 600; color: #6B7280; }
    .lp-inbox-badge { background: #FAECE7; color: #712B13; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
    .lp-ref-item { padding: 12px 14px; border-bottom: 1px solid rgba(0,0,0,0.06); display: flex; align-items: flex-start; gap: 10px; }
    .lp-ref-item:last-child { border-bottom: none; }
    .lp-avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
    .lp-av1 { background: #FAECE7; color: #712B13; }
    .lp-av2 { background: #E1F5EE; color: #085041; }
    .lp-av3 { background: #EEEDFE; color: #3C3489; }
    .lp-ref-body { flex: 1; min-width: 0; }
    .lp-ref-name { font-size: 12px; font-weight: 600; color: #1C1917; margin-bottom: 2px; }
    .lp-ref-meta { font-size: 10px; color: #9CA3AF; margin-bottom: 6px; }
    .lp-tags { display: flex; gap: 4px; flex-wrap: wrap; }
    .lp-tag { font-size: 9px; padding: 2px 7px; border-radius: 10px; border: 1px solid rgba(0,0,0,0.08); color: #6B7280; }
    .lp-status { font-size: 9px; font-weight: 700; padding: 3px 8px; border-radius: 10px; white-space: nowrap; flex-shrink: 0; margin-top: 2px; }
    .lp-s1 { background: #FAECE7; color: #712B13; }
    .lp-s2 { background: #E1F5EE; color: #085041; }
    .lp-s3 { background: #EEEDFE; color: #3C3489; }
    .lp-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 0; }
    .lp-logos { padding: 24px 48px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .lp-logos-label { font-size: 11px; color: #9CA3AF; margin-right: 4px; }
    .lp-logo-pill { font-size: 11px; color: #6B7280; padding: 4px 12px; border: 1px solid rgba(0,0,0,0.09); border-radius: 20px; background: #fff; }
    .lp-section { padding: 64px 48px; max-width: 1100px; margin: 0 auto; }
    .lp-section-label { font-size: 10px; font-weight: 700; color: #993C1D; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
    .lp-section-h { font-size: 28px; font-weight: 800; letter-spacing: -0.03em; color: #1C1917; margin-bottom: 36px; font-family: Playfair Display, Georgia, serif; line-height: 1.2; }
    .lp-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(0,0,0,0.07); border: 1px solid rgba(0,0,0,0.07); border-radius: 12px; overflow: hidden; }
    .lp-step { padding: 22px; background: #fff; }
    .lp-step-n { font-size: 10px; font-weight: 700; color: #D85A30; margin-bottom: 10px; letter-spacing: 0.04em; }
    .lp-step-t { font-size: 13px; font-weight: 700; color: #1C1917; margin-bottom: 6px; line-height: 1.3; }
    .lp-step-b { font-size: 11px; color: #6B7280; line-height: 1.65; }
    .lp-why-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
    .lp-pillars { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .lp-pillar { padding: 22px; border: none; border-radius: 14px; background: #FFFFFF; }
    .lp-pillar-icon { font-size: 20px; margin-bottom: 12px; }
    .lp-pillar-t { font-size: 13px; font-weight: 700; color: #1C1917; margin-bottom: 6px; }
    .lp-pillar-b { font-size: 12px; color: #6B7280; line-height: 1.6; }
    .lp-why-desc { font-size: 14px; color: #6B7280; line-height: 1.75; margin-bottom: 24px; }
    .lp-quote { border-left: 3px solid #D85A30; padding-left: 16px; margin-bottom: 28px; }
    .lp-quote p { font-size: 13px; color: #6B7280; line-height: 1.65; font-style: italic; margin-bottom: 6px; }
    .lp-quote cite { font-size: 11px; color: #9CA3AF; font-style: normal; }
    .lp-why-ctas { display: flex; gap: 10px; flex-wrap: wrap; }
    .lp-btn-ta { background: #5B6EAF; border: none; border-radius: 8px; padding: 10px 18px; font-size: 12px; font-weight: 700; color: #fff; cursor: pointer; font-family: inherit; }
    .lp-btn-ref { background: none; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 10px 18px; font-size: 12px; font-weight: 600; color: #374151; cursor: pointer; font-family: inherit; }
    .lp-price-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: start; }
    .lp-price-card { border: 1px solid rgba(0,0,0,0.09); border-radius: 14px; padding: 28px; background: #fff; }
    .lp-price-tag { font-size: 10px; font-weight: 700; color: #712B13; background: #FAECE7; padding: 3px 10px; border-radius: 20px; display: inline-block; margin-bottom: 16px; }
    .lp-price-amt { font-size: 36px; font-weight: 800; letter-spacing: -0.04em; color: #1C1917; }
    .lp-price-period { font-size: 14px; color: #9CA3AF; }
    .lp-price-trial { font-size: 11px; color: #D85A30; font-weight: 700; margin: 6px 0 16px; }
    .lp-feat-list { list-style: none; margin: 16px 0 24px; }
    .lp-feat-list li { font-size: 12px; color: #374151; padding: 7px 0; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid rgba(0,0,0,0.06); }
    .lp-feat-list li:last-child { border-bottom: none; }
    .lp-check { color: #D85A30; font-weight: 700; font-size: 13px; }
    .lp-price-cta { width: 100%; padding: 12px; font-size: 13px; font-weight: 700; background: #D85A30; border: none; border-radius: 9px; color: #fff; cursor: pointer; font-family: inherit; }
    .lp-price-note { font-size: 10px; color: #9CA3AF; text-align: center; margin-top: 8px; }
    .lp-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
    .lp-stat { padding: 14px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; }
    .lp-stat-n { font-size: 24px; font-weight: 800; color: #FFFFFF; letter-spacing: -0.03em; }
    .lp-stat-l { font-size: 10px; color: rgba(255,255,255,0.4); margin-top: 2px; }
    .lp-faq-item { border-bottom: 1px solid rgba(255,255,255,0.07); padding: 12px 0; }
    .lp-faq-item:last-child { border-bottom: none; }
    .lp-faq-q { font-size: 12px; font-weight: 700; color: #FFFFFF; margin-bottom: 4px; }
    .lp-faq-a { font-size: 11px; color: rgba(255,255,255,0.45); line-height: 1.6; }
    .lp-footer { padding: 28px 48px; border-top: 1px solid rgba(0,0,0,0.07); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; background: #FAFAF8; }
    .lp-footer-l { font-size: 11px; color: #9CA3AF; }
    .lp-footer-links { display: flex; gap: 16px; }
    .lp-footer-link { font-size: 11px; color: #9CA3AF; text-decoration: none; cursor: pointer; }

    @keyframes lpFadeUp { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
    @keyframes lpFadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes lpScaleIn { from { opacity:0; transform:scale(0.93); } to { opacity:1; transform:scale(1); } }
    @keyframes lpPillPop { from { opacity:0; transform:translateY(-8px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }

    .lp-anim { animation: lpFadeUp 0.7s cubic-bezier(0.16,1,0.3,1) both; }
    .lp-anim-pill { animation: lpPillPop 0.5s cubic-bezier(0.16,1,0.3,1) 0.1s both; }
    .lp-anim-h1 { animation: lpFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s both; }
    .lp-anim-sub { animation: lpFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.25s both; }
    .lp-anim-cta { animation: lpFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.35s both; }
    .lp-anim-note { animation: lpFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
    .lp-anim-inbox { animation: lpScaleIn 0.9s cubic-bezier(0.16,1,0.3,1) 0.3s both; }
    .lp-anim-logos { animation: lpFadeIn 0.8s ease 0.5s both; }

    .lp-step { transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s ease; }
    .lp-step:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
    .lp-pillar { transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s ease; }
    .lp-pillar:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(0,0,0,0.25); }
    .lp-btn-hero { transition: transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.18s ease, background 0.15s ease; box-shadow: 0 0 0 0 rgba(216,90,48,0); }
    .lp-btn-hero:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(216,90,48,0.5); background: #C4522B; }
    .lp-btn-hero:active { transform: scale(0.97); }
    .lp-btn-coral { transition: transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.18s ease; }
    .lp-btn-coral:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(216,90,48,0.3); }
    .lp-btn-coral:active { transform: scale(0.97); }
    .lp-price-cta { transition: transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.18s ease, background 0.15s ease; }
    .lp-price-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(216,90,48,0.35); background: #C4522B; }
    .lp-price-cta:active { transform: scale(0.97); }
    .lp-ref-item { transition: background 0.15s ease; }
    .lp-ref-item:hover { background: rgba(0,0,0,0.02); }
    .lp-logo-pill { transition: border-color 0.2s ease, color 0.2s ease; }
    .lp-logo-pill:hover { border-color: rgba(216,90,48,0.3); color: #D85A30; }
    .lp-nav-link { transition: color 0.15s ease; }
    .lp-nav-link:hover { color: #1C1917 !important; }
    .lp-stat { transition: transform 0.2s cubic-bezier(0.16,1,0.3,1); }
    .lp-stat:hover { transform: scale(1.03); }

    @media (max-width: 768px) {
      .lp-nav { padding: 14px 20px; }
      .lp-nav-links .lp-nav-link { display: none; }
      .lp-btn-ghost { display: none; }
      .lp-btn-coral { font-size: 11px; padding: 7px 14px; }
      .lp-hero { grid-template-columns: 1fr; gap: 32px; padding: 40px 20px 36px; }
      .lp-h1 { font-size: 28px; }
      .lp-hero-body { font-size: 14px; }
      .lp-btn-hero { width: 100%; text-align: center; padding: 14px; }
      .lp-logos { padding: 20px; }
      .lp-section { padding: 48px 20px; }
      .lp-section-h { font-size: 22px; margin-bottom: 24px; }
      .lp-steps { grid-template-columns: 1fr 1fr; }
      .lp-why-grid { grid-template-columns: 1fr; gap: 32px; }
      .lp-price-wrap { grid-template-columns: 1fr; gap: 24px; }
      .lp-footer { padding: 20px; flex-direction: column; align-items: flex-start; gap: 12px; }
      .lp-why-ctas { flex-direction: column; }
      .lp-btn-ta, .lp-btn-ref { width: 100%; text-align: center; }
      .lp-price-cta { font-size: 14px; padding: 14px; }
    }
    @media (max-width: 400px) {
      .lp-steps { grid-template-columns: 1fr; }
      .lp-pillars { grid-template-columns: 1fr; }
      .lp-stats { grid-template-columns: 1fr; }
      .lp-h1 { font-size: 26px; }
    }
  `;

  return (
    <div className="lp-root">
      <style>{LP_STYLES}</style>

      <nav className="lp-nav">
        <div className="lp-logo">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#5B6EAF"/><text x="18" y="27" font-family="Georgia, serif" font-size="26" font-weight="700" fill="#FFFFFF" text-anchor="middle">P</text></svg>
          <div>
            <div className="lp-wordmark">PeerRefer</div>
            <div className="lp-parent">by CollabPathways</div>
          </div>
        </div>
        <div className="lp-nav-links">
          <span className="lp-nav-link" onClick={() => document.getElementById('lp-how').scrollIntoView({behavior:'smooth'})}>How it works</span>
          <span className="lp-nav-link" onClick={() => document.getElementById('lp-pricing').scrollIntoView({behavior:'smooth'})}>Pricing</span>
          <span className="lp-nav-link" onClick={() => onSelect("ta")}>Sign in</span>
          <button className="lp-btn-coral" onClick={() => onSelect("ta")}>Start free trial</button>
        </div>
      </nav>

      <section style={{background:"#1A0D12", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,right:0,width:"50%",height:"100%",background:"radial-gradient(ellipse at 80% 50%, rgba(216,90,48,0.08) 0%, transparent 70%)",pointerEvents:"none"}}></div>
        <div style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",backgroundImage:"radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",backgroundSize:"32px 32px",pointerEvents:"none"}}></div>
        <div className="lp-hero lp-anim">
          <div>
            <div className="lp-pill lp-anim-pill"><div className="lp-pill-dot"></div>Warm referrals · Faster shortlists · No cold applications</div>
            <h1 className="lp-h1 lp-anim-h1">Find trusted talent<br/>through people<br/><em>who know them.</em></h1>
            <p className="lp-hero-body lp-anim-sub">Access referrals with candidate consent, relationship context, and warm introductions — helping your team fill roles faster without relying on cold applications.</p>
            <div className="lp-cta-row lp-anim-cta">
              <button className="lp-btn-hero" onClick={() => onSelect("ta")}>Start free — no card needed</button>
              <button className="lp-btn-ghost2" onClick={() => document.getElementById('lp-how').scrollIntoView({behavior:'smooth'})}>See how it works</button>
            </div>
            <div className="lp-hero-note lp-anim-note">
              <span>30-day free trial</span>
              <div className="lp-note-dot"></div>
              <span>A$49/month after</span>
              <div className="lp-note-dot"></div>
              <span>No card needed to start</span>
            </div>
          </div>
          <div>
            <div className="lp-inbox lp-anim-inbox">
              <div className="lp-inbox-bar">
                <span className="lp-inbox-title">Referral inbox</span>
                <span className="lp-inbox-badge">3 new</span>
              </div>
              {[
                {av:"SL",cls:"lp-av1",name:"Sarah Lim",meta:"Referred by Marcus Webb · Product Manager, 6 yrs",tags:["SaaS","B2B","Agile"],status:"Awaiting consent",scls:"lp-s1"},
                {av:"JK",cls:"lp-av2",name:"James Kowalski",meta:"Referred by Priya Nair · Data Analyst, 4 yrs",tags:["SQL","Python","Fintech"],status:"CV received",scls:"lp-s2"},
                {av:"AT",cls:"lp-av3",name:"Anika Tran",meta:"Referred by Luca Bianchi · TA Lead, 8 yrs",tags:["Talent","HR Tech"],status:"Shortlisted",scls:"lp-s3"},
              ].map((r,i) => (
                <div className="lp-ref-item" key={i}>
                  <div className={`lp-avatar ${r.cls}`}>{r.av}</div>
                  <div className="lp-ref-body">
                    <div className="lp-ref-name">{r.name}</div>
                    <div className="lp-ref-meta">{r.meta}</div>
                    <div className="lp-tags">{r.tags.map((t,j)=><span className="lp-tag" key={j}>{t}</span>)}</div>
                  </div>
                  <div className={`lp-status ${r.scls}`}>{r.status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="lp-divider"></div>
      <div className="lp-logos lp-anim-logos" style={{background:"#1A0D12", borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <span style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginRight:4}}>Built for</span>
        {["In-house TA teams","Agency recruiters","Independent recruiters","HR consultants"].map((l,i)=><span key={i} style={{fontSize:11,color:"rgba(255,255,255,0.5)",padding:"4px 12px",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,background:"rgba(255,255,255,0.04)",transition:"all 0.2s"}}>{l}</span>)}
      </div>
      <div className="lp-divider"></div>

      <section id="lp-how" className="lp-section" style={{background:"#FAFAF8"}}>
        <div className="lp-section-label">How it works</div>
        <div className="lp-section-h" style={{color:"#1C1917"}}>From referral to consent in four steps</div>
        <div className="lp-steps">
          {[
            {n:"01",t:"Share your link",b:"Your network sees exactly what talent you need — roles, industries, seniority. No open role required."},
            {n:"02",t:"Referral lands in your inbox",b:"Someone vouches for a candidate. You see their name, skills, and who referred them — before any CV is shared."},
            {n:"03",t:"Candidate consents first",b:"PeerRefer emails the candidate. Only after they agree does their CV and Talent Passport reach you."},
            {n:"04",t:"Move them through pipeline",b:"Shortlist, interview, place. Referrers stay in the loop automatically. Track everything in one place."},
          ].map((s,i)=>(
            <div className="lp-step" key={i}>
              <div className="lp-step-n">{s.n}</div>
              <div className="lp-step-t">{s.t}</div>
              <div className="lp-step-b">{s.b}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="lp-divider"></div>

      <section className="lp-section" style={{background:"#1C1117", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",backgroundImage:"radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)",backgroundSize:"28px 28px",pointerEvents:"none"}}></div>
        <div style={{position:"absolute",bottom:0,right:0,width:"400px",height:"400px",background:"radial-gradient(ellipse at 100% 100%, rgba(91,110,175,0.12) 0%, transparent 70%)",pointerEvents:"none"}}></div>
        <div className="lp-why-grid" style={{position:"relative"}}>
          <div>
            <div className="lp-section-label" style={{color:"#E8896A"}}>Why PeerRefer</div>
            <div className="lp-section-h" style={{color:"#FFFFFF"}}>You already know who<br/>refers great people.<br/><em style={{color:"#E8896A",fontStyle:"normal"}}>Now it's productised.</em></div>
            <p className="lp-why-desc" style={{color:"rgba(255,255,255,0.55)"}}>Every recruiter already mentally scores their network — "Sarah always refers great engineers", "Mark knows everything in fintech." PeerRefer turns that instinct into structured data. Better quality, faster shortlists, lower agency spend.</p>
            <div className="lp-quote" style={{borderLeftColor:"#E8896A"}}>
              <p style={{color:"rgba(255,255,255,0.6)"}}>"Recruiters wake up thinking about hard-to-fill roles, pipeline quality and agency costs. PeerRefer solves all three — through the network you already have."</p>
              <cite style={{color:"rgba(255,255,255,0.35)"}}>Dij Thulasi, Founder · 12+ years in TA</cite>
            </div>
            <div className="lp-why-ctas">
              <button className="lp-btn-ta" onClick={() => onSelect("ta")}>Sign in as Talent Acquisition →</button>
            </div>
            <div style={{marginTop:12}}>
              <span style={{fontSize:11,color:"#9CA3AF"}}>Referring someone? </span>
              <span style={{fontSize:11,color:"#6B7280",textDecoration:"underline",cursor:"pointer",fontWeight:600}} onClick={() => onSelect("referrer")}>Sign in as Referrer →</span>
            </div>
          </div>
          <div className="lp-pillars">
            {[
              {icon:"⚡",t:"Better candidate quality",b:"Referred candidates are pre-vouched by someone you trust. Higher signal, lower noise than any job board."},
              {icon:"🎯",t:"Faster shortlists",b:"Relationship context and AI matching surface the right people first. Spend less time reviewing, more time hiring."},
              {icon:"💰",t:"Lower agency spend",b:"Build a warm pipeline through your network. Less reliance on agencies for hard-to-fill roles."},
              {icon:"🤝",t:"Warmer candidates",b:"Candidates who consent are already engaged. They know who referred them and why. The conversation starts warm."},
            ].map((p,i)=>(
              <div className="lp-pillar" key={i}>
                <div className="lp-pillar-icon">{p.icon}</div>
                <div className="lp-pillar-t">{p.t}</div>
                <div className="lp-pillar-b">{p.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="lp-divider"></div>

      <section id="lp-pricing" className="lp-section" style={{background:"#120A0D", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:"600px",height:"300px",background:"radial-gradient(ellipse at 50% 0%, rgba(216,90,48,0.1) 0%, transparent 70%)",pointerEvents:"none"}}></div>
        <div className="lp-section-label">Pricing</div>
        <div className="lp-section-h" style={{color:"#FFFFFF"}}>Less than one agency CV.<br/>Unlimited trusted referrals.</div>
        <div className="lp-price-wrap">
          <div className="lp-price-card" style={{background:"#FFFFFF",boxShadow:"0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)",position:"relative"}}>
            <div className="lp-price-tag">Pro plan</div>
            <div style={{marginBottom:4}}>
              <span className="lp-price-amt">A$49</span><span className="lp-price-period"> /month</span>
            </div>
            <div className="lp-price-trial">30-day free trial · No card required to start</div>
            <ul className="lp-feat-list">
              {["Unlimited referrals & briefs","Consent email flow","Talent Passport for candidates","AI match to brief (top 3 candidates)","Referrer leaderboard & tier system","Analytics dashboard & CSV export","Candidate feedback & TA ratings","Stripe-secured billing — cancel anytime"].map((f,i)=>(
                <li key={i}><span className="lp-check">✓</span>{f}</li>
              ))}
            </ul>
            <button className="lp-price-cta" onClick={() => onSelect("ta")}>Start free trial — no card needed</button>
            <div className="lp-price-note">Cancel anytime · No lock-in · Billed monthly after trial</div>
          </div>
          <div>
            <div className="lp-stats">
              {[{n:"0",l:"Agency placement fees"},{n:"A$49",l:"Per month — less than one CV"},{n:"30",l:"Day free trial"},{n:"100%",l:"Candidate consent built in"}].map((s,i)=>(
                <div className="lp-stat" key={i}><div className="lp-stat-n">{s.n}</div><div className="lp-stat-l">{s.l}</div></div>
              ))}
            </div>
            <div style={{marginTop:8}}>
              <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Common questions</div>
              {[
                {q:"What happens after my trial ends?",a:"Your card is charged A$49 automatically. We'll remind you 3 days before. Cancel anytime from Settings — no charge."},
                {q:"Do I need a credit card to start?",a:"No. Start your 30-day trial with just your email. Add a card only if you decide to continue."},
                {q:"Is there an enterprise plan?",a:"Not yet — but if you're an agency with multiple TAs, get in touch and we'll work something out."},
              ].map((item,i)=>(
                <div className="lp-faq-item" key={i}>
                  <div className="lp-faq-q">{item.q}</div>
                  <div className="lp-faq-a">{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Referrer box */}
      <div className="lp-divider"></div>
      <div style={{ padding: "48px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20, background: "#D85A30", position: "relative", overflow: "hidden" }}>
        <div style={{position:"absolute",top:0,right:0,width:"300px",height:"100%",background:"radial-gradient(ellipse at 100% 50%, rgba(255,255,255,0.08) 0%, transparent 70%)",pointerEvents:"none"}}></div>
        <div style={{position:"relative"}}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.03em", marginBottom: 6, fontFamily: "Playfair Display, Georgia, serif" }}>Know someone great?</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }}>Refer them to a recruiter in your network — takes 2 minutes.</div>
        </div>
        <button style={{ background: "#FFFFFF", color: "#D85A30", border: "none", borderRadius: 9, padding: "13px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "transform 0.18s ease, box-shadow 0.18s ease", position: "relative" }}
          onMouseEnter={e => { e.target.style.transform="translateY(-1px)"; e.target.style.boxShadow="0 6px 20px rgba(0,0,0,0.2)"; }}
          onMouseLeave={e => { e.target.style.transform=""; e.target.style.boxShadow=""; }}
          onClick={() => onSelect("referrer")}>
          Refer someone →
        </button>
      </div>
      <div className="lp-divider"></div>

      <footer className="lp-footer" style={{background:"#0D0608", borderTop:"1px solid rgba(255,255,255,0.06)"}}>
        <div>
          <div className="lp-footer-l" style={{color:"rgba(255,255,255,0.35)"}}>© CollabPathways 2026 · All rights reserved</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",marginTop:2}}>PeerRefer is a CollabPathways product</div>
        </div>
        <div className="lp-footer-links">
          <span className="lp-footer-link" style={{color:"rgba(255,255,255,0.3)"}} onClick={() => onSelect("referrer")}>Referrer sign in</span>
          <a href="/?privacy=true" className="lp-footer-link" style={{color:"rgba(255,255,255,0.3)"}}>Privacy policy</a>
          <a href="/?terms=true" className="lp-footer-link" style={{color:"rgba(255,255,255,0.3)"}}>Terms & conditions</a>
          <a href="mailto:hello@peerrefer.com" className="lp-footer-link" style={{color:"rgba(255,255,255,0.3)"}}>Contact</a>
        </div>
      </footer>
    </div>
  );
}
// ============================================
// ROOT APP
// ============================================
// Module-level milestone tracking — persists for the session
const _shownMilestones = new Set();

export default function PeerRefer() {
  const [portal, setPortal] = useState(null);
  const [user, setUser] = useState(null);
  const [portalRestored, setPortalRestored] = useState(false);
  const [hasBothPortals, setHasBothPortals] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [pool, setPool] = useState([]);
  const [poolLoading, setPoolLoading] = useState(true);
  const [taProfile, setTaProfile] = useState(null);
  const [sharedBriefs, setSharedBriefs] = useState([]);
  const [taPrefs, setTaPrefs] = useState(null);
  const [isProTA, setIsProTA] = useState(false);

  const logout = async () => { await supabase.auth.signOut(); localStorage.removeItem("peerrefer_portal"); localStorage.removeItem("peerrefer_user_email"); setUser(null); setPortal(null); setPool([]); setTaProfile(null); setSharedBriefs([]); setIsProTA(false); };

  const [adminAuthed, setAdminAuthed] = useState(false);
  const [consentSuccess, setConsentSuccess] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetMode, setResetMode] = useState(false);

  // Check for password reset redirect — handles token_hash, ?code= (PKCE), and legacy #type=recovery
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const code = params.get("code");
    const tokenHash = params.get("token_hash");
    const type = params.get("type");
    const isReset = params.get("reset") === "true";
    const isLegacyRecovery = hash && hash.includes("type=recovery");

    const isRecovery = (tokenHash && type === "recovery") || (isReset && code) || isLegacyRecovery;

    if (isRecovery) {
      setResetMode(true);
      if (tokenHash && type === "recovery") {
        // New token_hash flow — verify OTP then show reset form
        supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" }).then(({ error }) => {
          if (error) {
            console.error("Token verify error:", error.message);
            setResetMode(false);
            alert("Reset link has expired or already been used. Please request a new one.");
          }
        });
      } else if (code) {
        // PKCE code flow
        supabase.auth.exchangeCodeForSession(window.location.href).then(({ error }) => {
          if (error) console.error("Code exchange error:", error.message);
        });
      }
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  // Handle post-Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Handle email verification redirect
    const isVerified = params.get("verified") === "true";
    const code = params.get("code");
    const tokenHash = params.get("token_hash");
    const typeParam = params.get("type");

    if (isVerified || (tokenHash && typeParam === "email") || code) {
      window.history.replaceState({}, "", window.location.pathname);

      const handleVerification = async () => {
        try {
          // Exchange code or token for session
          if (tokenHash && typeParam === "email") {
            await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "email" });
          } else if (code) {
            await supabase.auth.exchangeCodeForSession(window.location.href);
          }

          // Get session after exchange
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;

          const userEmail = session.user?.email;
          const userId2 = session.user?.id;

          // Check for pending TA profile
          const pendingTA = localStorage.getItem("pendingTA");
          if (pendingTA) {
            try {
              const { name: tName, email: tEmail, company: tCompany, slug: tSlug } = JSON.parse(pendingTA);
              if (tEmail === userEmail) {
                localStorage.removeItem("pendingTA");
                const { data: newTA } = await supabase.from("ta_profiles").insert({
                  auth_user_id: userId2, name: tName, email: tEmail, company: tCompany,
                  slug: tSlug, bonus_enabled: false, bonus_amount: 2000, bonus_trigger: "placement", is_pro: false,
                }).select().single();
                if (newTA) {
                  setPortal("ta");
                  setUser({ role: "ta", name: newTA.name, company: newTA.company, email: newTA.email, initials: (newTA.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), slug: newTA.slug, id: newTA.id, is_pro: false });
                  fetch("/.netlify/functions/ta-onboarding", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ taName: tName, taEmail: tEmail, taSlug: tSlug, taCompany: tCompany }),
                  }).catch(() => {});
                  return;
                }
              }
            } catch(e) { console.error("Pending TA error:", e); }
          }

          // Check for pending referrer profile
          const pendingRef = localStorage.getItem("pendingReferrer");
          if (pendingRef) {
            try {
              const { name: rName, email: rEmail } = JSON.parse(pendingRef);
              if (rEmail === userEmail) {
                localStorage.removeItem("pendingReferrer");
                const { data: newRef } = await supabase.from("referrers").insert({
                  auth_user_id: userId2, name: rName, email: rEmail, reputation_score: 50, total_referred: 0, total_placed: 0,
                }).select().single();
                if (newRef) {
                  setPortal("referrer");
                  setUser({ role: "referrer", name: newRef.name, email: newRef.email, initials: (newRef.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), id: newRef.id });
                  return;
                }
              }
            } catch(e) { console.error("Pending referrer error:", e); }
          }

          // No pending profile — check existing profiles
          const { data: ta } = await supabase.from("ta_profiles").select("*").eq("email", userEmail).maybeSingle();
          if (ta) {
            if (ta.is_pro) setIsProTA(true);
            setPortal("ta");
            setUser({ role: "ta", name: ta.name, company: ta.company, email: ta.email, initials: (ta.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), slug: ta.slug, id: ta.id, is_pro: ta.is_pro });
            return;
          }
          const { data: ref } = await supabase.from("referrers").select("*").eq("email", userEmail).maybeSingle();
          if (ref) {
            setPortal("referrer");
            setUser({ role: "referrer", name: ref.name, email: ref.email, initials: (ref.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), id: ref.id });
          }
        } catch(e) { console.error("Verification error:", e); }
      };

      handleVerification();
    }

    if (params.get("upgraded") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      // Refresh user profile to pick up is_pro
      if (user?.email) {
        supabase.from("ta_profiles").select("is_pro").eq("email", user.email).maybeSingle()
          .then(({ data }) => {
            if (data?.is_pro) setIsProTA(true);
          });
      }
      alert("Payment successful! Your Pro plan is now active. Welcome to PeerRefer Pro 🎉");
    }
    if (params.get("upgrade_cancelled") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  // Handle consent/decline URL - must be before any conditional returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const consentToken = params.get("consent");
    const refId = params.get("ref");
    const declineToken = params.get("decline");
    if ((consentToken || declineToken) && refId) {
      const consented = !!consentToken;
      const handleConsent = async () => {
        const { error } = await supabase.from("referrals").update({
          consented,
          blurred: !consented,
          status: consented ? "new" : "declined",
          consented_at: consented ? new Date().toISOString() : null,
        }).eq("id", refId);
        if (!error) {
          if (consented) {
            const { data: refData } = await supabase
              .from("referrals")
              .select("*, referrer_id, briefs(title, description, position_description, evp, logo_url, seniority, location, bonus, ta_profiles(name, company))")
              .eq("id", refId)
              .maybeSingle();
            window.history.replaceState({}, "", window.location.pathname);
            // Get TA name from briefs join or fallback lookup
            let taName = refData?.briefs?.ta_profiles?.name || "";
            let taCompany = refData?.briefs?.ta_profiles?.company || "";
            if (!taName && refData?.ta_profile_id) {
              const { data: taP } = await supabase.from("ta_profiles").select("name, company").eq("id", refData.ta_profile_id).maybeSingle();
              taName = taP?.name || "";
              taCompany = taP?.company || "";
            }
            if (!taName && refData?.ta_slug) {
              const { data: taP } = await supabase.from("ta_profiles").select("name, company").eq("slug", refData.ta_slug).maybeSingle();
              taName = taP?.name || "";
              taCompany = taP?.company || "";
            }
            setConsentSuccess({
              referralId: refId,
              candidateName: refData?.candidate_name || "",
              candidateEmail: refData?.candidate_email || "",
              referrerName: refData?.referrer_name || "",
              brief: refData?.briefs || null,
              taName: taName,
              taCompany: taCompany,
            });

            // Notify referrer by email
            if (consented && refData) {
              const { data: referrer } = await supabase
                .from("referrers")
                .select("email, name")
                .eq("id", refData.referrer_id)
                .maybeSingle();
              if (referrer?.email) {
                notifyReferrer({
                  event: "consented",
                  referrerEmail: referrer.email,
                  referrerName: referrer.name,
                  candidateName: refData.candidate_name,
                  taName: refData.briefs?.ta_profiles?.name || "",
                  taCompany: refData.briefs?.ta_profiles?.company || "",
                  briefTitle: refData.brief_title || refData.briefs?.title || "",
                  bonusAmount: refData.briefs?.bonus || 0,
                });
              }
            }
          } else {
            window.history.replaceState({}, "", window.location.pathname);
            alert("You have declined the referral. Your details will not be shared.");
          }
        }
      };
      handleConsent();
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const userEmail = session.user.email;
        const userId = session.user.id;

        // URL param takes highest priority (set by switcher buttons)
        const urlParams = new URLSearchParams(window.location.search);
        const urlPortal = urlParams.get("portal");
        if (urlPortal === "referrer" || urlPortal === "ta") {
          localStorage.setItem("peerrefer_portal", urlPortal);
          // Clean URL without reloading
          window.history.replaceState({}, "", "/");
        }

        // Use server-side function with service key — bypasses RLS completely
        let taProfile = null, referrer = null;
        try {
          const profileRes = await fetch("/.netlify/functions/get-profile", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, email: userEmail }),
          });
          const profileData = await profileRes.json();
          taProfile = profileData.taProfile || null;
          referrer = profileData.referrer || null;
          console.log("Profile lookup:", userEmail, "TA:", taProfile?.id, "Ref:", referrer?.id);
        } catch(e) {
          console.error("Profile lookup failed:", e);
        }

        const hasBoth = taProfile && referrer;
        if (hasBoth) setHasBothPortals(true);

        // Priority order for portal selection:
        // 1. URL param (set by switcher - most immediate)
        // 2. localStorage (set on login - most recent)  
        // 3. Supabase metadata (set async - may be stale)
        // 4. Single profile type (no choice needed)
        // 5. Default to TA
        const urlParams2 = new URLSearchParams(window.location.search);
        const urlPortal2 = urlParams2.get("portal");
        const localPortal = localStorage.getItem("peerrefer_portal");
        const localEmail = localStorage.getItem("peerrefer_user_email");
        const localPortalValid = localPortal && localEmail === userEmail;
        const metaPortal = session.user?.user_metadata?.preferred_portal;

        console.log("Portal resolution: url=", urlPortal2, "local=", localPortal, "localValid=", localPortalValid, "meta=", metaPortal, "hasBoth=", hasBoth);

        let targetPortal = null;
        if (!hasBoth) {
          // Only one profile type — no choice needed
          targetPortal = taProfile ? "ta" : referrer ? "referrer" : null;
        } else if (urlPortal2 === "referrer" || urlPortal2 === "ta") {
          targetPortal = urlPortal2;
        } else if (localPortalValid) {
          targetPortal = localPortal;
        } else if (metaPortal === "referrer" || metaPortal === "ta") {
          targetPortal = metaPortal;
        } else {
          targetPortal = "ta"; // default for dual accounts
        }

        console.log("Target portal:", targetPortal);

        if (targetPortal === "ta" && taProfile) {
          if (taProfile.is_pro) setIsProTA(true);
          setUser({ role: "ta", name: taProfile.name, email: userEmail, slug: taProfile.slug, company: taProfile.company, initials: inits(taProfile.name), is_pro: taProfile.is_pro });
          setPortal("ta");
          localStorage.setItem("peerrefer_portal", "ta");
          localStorage.setItem("peerrefer_user_email", userEmail);
          supabase.auth.updateUser({ data: { preferred_portal: "ta" } }).catch(() => {});
          setAuthLoading(false);
          return;
        }

        if (targetPortal === "referrer" && referrer) {
          setUser({ role: "referrer", name: referrer.name, email: userEmail, initials: inits(referrer.name), reputationScore: referrer.reputation_score || 50, totalReferred: referrer.total_referred || 0, totalConsented: referrer.total_consented || 0, totalContacted: referrer.total_contacted || 0, totalPlaced: referrer.total_placed || 0, pendingBonus: referrer.pending_bonus || 0, paidBonus: referrer.paid_bonus || 0, linkedinVerified: referrer.linkedin_verified || false, linkedinName: referrer.linkedin_name || "", id: referrer.id, location: referrer.location || "" });
          setPortal("referrer");
          localStorage.setItem("peerrefer_portal", "referrer");
          localStorage.setItem("peerrefer_user_email", userEmail);
          supabase.auth.updateUser({ data: { preferred_portal: "referrer" } }).catch(() => {});
          setAuthLoading(false);
          return;
        }
      }
      setAuthLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") { setUser(null); setPortal(null); setAuthLoading(false); }
      if (event === "PASSWORD_RECOVERY") { setResetMode(true); }
      if (event === "SIGNED_IN" && session) {
        // ONLY handle email verification flows - never override a normal login
        const hasPendingProfile = localStorage.getItem("pendingTA") || localStorage.getItem("pendingReferrer");
        const isVerificationUrl = window.location.search.includes("verified=true") || window.location.search.includes("token_hash") || window.location.search.includes("code=");
        if (!hasPendingProfile && !isVerificationUrl) return; // Normal login - do nothing here
        const userEmail = session.user?.email;
        const userId2 = session.user?.id;
        if (userEmail) {
          supabase.from("ta_profiles").select("*").eq("email", userEmail).maybeSingle().then(async ({ data: ta }) => {
            if (ta) {
              if (ta.is_pro) setIsProTA(true);
              localStorage.setItem("peerrefer_portal", "ta");
              localStorage.setItem("peerrefer_user_email", ta.email);
              setPortal("ta");
              setUser({ role: "ta", name: ta.name, company: ta.company, email: ta.email, initials: (ta.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), slug: ta.slug, id: ta.id, is_pro: ta.is_pro });
            } else {
              // Check for pending TA signup
              const pendingTA = localStorage.getItem("pendingTA");
              if (pendingTA) {
                try {
                  const { name: tName, email: tEmail, company: tCompany, slug: tSlug } = JSON.parse(pendingTA);
                  if (tEmail === userEmail) {
                    localStorage.removeItem("pendingTA");
                    const { data: newTA } = await supabase.from("ta_profiles").insert({
                      auth_user_id: userId2, name: tName, email: tEmail, company: tCompany,
                      slug: tSlug, bonus_enabled: false, bonus_amount: 2000, bonus_trigger: "placement", is_pro: false,
                    }).select().single();
                    if (newTA) {
                      setPortal("ta");
                      setUser({ role: "ta", name: newTA.name, company: newTA.company, email: newTA.email, initials: (newTA.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), slug: newTA.slug, id: newTA.id, is_pro: false });
                      // Send onboarding email
                      fetch("/.netlify/functions/ta-onboarding", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ taName: tName, taEmail: tEmail, taSlug: tSlug, taCompany: tCompany }),
                      }).catch(() => {});
                      return;
                    }
                  }
                } catch(e) { console.error("Pending TA error:", e); }
              }
              // Check for pending referrer signup (deferred profile creation)
              const pending = localStorage.getItem("pendingReferrer");
              if (pending) {
                try {
                  const parsedRef = JSON.parse(pending);
                  const { name: rName, email: rEmail, location: rLocation } = parsedRef;
                  if (rEmail === userEmail) {
                    localStorage.removeItem("pendingReferrer");
                    const { data: newRef } = await supabase.from("referrers").insert({
                      auth_user_id: userId2, name: rName, email: rEmail, location: rLocation || "", reputation_score: 50, total_referred: 0, total_placed: 0,
                    }).select().single();
                    if (newRef) {
                      setPortal("referrer");
                      setUser({ role: "referrer", name: newRef.name, email: newRef.email, initials: (newRef.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), id: newRef.id });
                      return;
                    }
                  }
                } catch(e) { console.error("Pending referrer error:", e); }
              }
              // Check for existing referrer profile
              supabase.from("referrers").select("*").eq("email", userEmail).maybeSingle().then(({ data: ref }) => {
                if (ref) {
                  setPortal("referrer");
                  setUser({ role: "referrer", name: ref.name, email: ref.email, initials: (ref.name||"").split(" ").map(w=>w[0]).join("").toUpperCase(), id: ref.id });
                }
              });
            }
          });
        }
      }
    });
    return () => listener?.subscription?.unsubscribe();
  }, []); // eslint-disable-line

  // Load TA profile and referrals from Supabase when TA logs in
  useEffect(() => {
    if (user && user.role === "ta") {
      setPoolLoading(true);
      const loadTA = async () => {
        try {
          // Step 1: get TA profile
          const { data: profile, error: profileError } = await supabase
            .from("ta_profiles")
            .select("*")
            .eq("slug", user.slug)
            .single();
          if (profileError) { console.error("Profile error:", profileError); setPoolLoading(false); return; }
          if (profile) {
            setTaProfile(profile);
            if (profile.is_pro) setIsProTA(true);
            // Load preferences into taPrefs
            setTaPrefs({
              name: profile.name || "Mia Thorne",
              lookingFor: profile.looking_for || "Product Managers, Data Analysts, TA professionals",
              industries: profile.industries || "SaaS, Tech, Fintech",
              seniority: profile.seniority || "Mid to Senior level",
              location: profile.location || "Melbourne / Remote",
              referrerNote: profile.referrer_note || "I'm always open to strong talent in the Melbourne tech space.",
            });
          }
          // Step 2: get briefs
          const { data: briefsData } = await supabase
            .from("briefs")
            .select("*")
            .eq("ta_profile_id", profile.id)
            .order("created_at", { ascending: false });
          if (briefsData) {
            setSharedBriefs(briefsData.map(b => ({ ...b, matchResults: [] })));
          }

          // Step 3: get referrals using profile ID
          const { data: refs, error: refsError } = await supabase
            .from("referrals")
            .select("*, referrers(*)")
            .eq("ta_profile_id", profile.id)
            .eq("consented", true)
            .order("created_at", { ascending: false });
          if (refsError) { console.error("Refs error:", refsError); setPoolLoading(false); return; }
          if (refs) {
            const prevCount = pool.length;
            const mapped = refs.map(r => ({
              id: r.id,
              candidateName: r.candidate_name,
              candidateEmail: r.candidate_email || "",
              candidateLinkedin: r.candidate_linkedin || "",
              skills: r.skills || [],
              note: r.note,
              referrerName: r.referrers?.name || "Unknown",
              referrerEmail: r.referrers?.email || "",
              referrerLinkedinVerified: r.referrers?.linkedin_verified || false,
              referrerRelationship: r.referrer_relationship || (r.referrers ? "Referrer" : "Unknown"),
              referrerDuration: r.referrer_duration || "",
              referrerStandout: r.referrer_standout || "",
              referrerWorkAgain: r.referrer_work_again || "",
              introRequested: r.intro_requested || false,
              introAccepted: r.intro_accepted ?? null,
              taNotes: r.ta_notes || "",
              referrerStats: {
                total: r.referrers?.total_referred || 1,
                placed: r.referrers?.total_placed || 0,
              },
              skillsRating: r.skills_rating || 4,
              workEthicRating: r.work_ethic_rating || 4,
              coachabilityRating: r.coachability_rating || 4,
              submittedAt: new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" }),
              status: r.status,
              blurred: !profile?.is_pro,
              cvFilename: r.cv_filename || null,
              aiAnalysis: r.ai_analysis || null,
              draftEmail: null,
              briefId: r.brief_id || null,
              briefTitle: r.brief_title || null,
              pipelineStatus: r.pipeline_status || "referred",
            }));
            setPool(mapped);
            // Notify TA if new referrals came in since last load
            if (mapped.length > prevCount && prevCount > 0) {
              const newCount = mapped.length - prevCount;
              showLocalNotification(
                "New referral" + (newCount > 1 ? "s" : "") + " in your inbox",
                `${newCount} new candidate${newCount > 1 ? "s" : ""} added to your talent pool`,
                "/"
              );
            }
          }
        } catch (err) {
          console.error("Load error:", err);
        }
        setPoolLoading(false);
      };
      loadTA();
    }
  }, [user]); // eslint-disable-line

  // Handle LinkedIn OAuth callback — must be before any conditional returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liVerified = params.get("linkedin_verified");
    const liError = params.get("linkedin_error");
    if (liVerified === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      setUser(prev => prev ? { ...prev, linkedinVerified: true } : prev);
    }
    if (liError) {
      window.history.replaceState({}, "", window.location.pathname);
      alert("LinkedIn verification failed. Please try again.");
    }
  }, []); // eslint-disable-line

  // When pro status changes, unblur all candidates
  useEffect(() => {
    if (isProTA) {
      setPool(prev => prev.map(r => ({ ...r, blurred: false })));
    }
  }, [isProTA]); // eslint-disable-line

  // Milestone detection is inside TADashboard component

  const isCandidate = typeof window !== "undefined" && window.location.search.includes("candidate=true");
  if (isCandidate) return <CandidateProfilePage onComplete={() => window.location.href = "/"} />;

  // Public referrer profile — ?referrer=ID
  const routeParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const referrerPageId = routeParams.get("referrer");
  if (referrerPageId && !routeParams.get("briefId")) return <PublicReferrerPage referrerId={referrerPageId} />;

  // Public brief page — ?briefId=X&refer=slug
  const briefParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const briefPageId = briefParams.get("briefId");
  const briefPageSlug = briefParams.get("refer");
  if (briefPageId && briefPageSlug && !user) return <PublicBriefPage briefId={briefPageId} taSlug={briefPageSlug} />;

  // Pricing page — ?pricing=true
  const isPricingUrl = typeof window !== "undefined" && window.location.search.includes("pricing=true");
  if (isPricingUrl) return <PricingPage />;

  // Admin dashboard — ?admin=true
  const isAdminUrl = typeof window !== "undefined" && window.location.search.includes("admin=true");
  if (isAdminUrl) {
    if (!adminAuthed) return <AdminLogin onAuth={() => setAdminAuthed(true)} onExit={() => window.location.href = "/"} />;
    return <AdminDashboard onExit={() => { setAdminAuthed(false); window.location.href = "/"; }} />;
  }

  // Public referral link — ?refer=mia-thorne
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const referSlug = urlParams ? urlParams.get("refer") : null;
  const publicBriefId = urlParams ? urlParams.get("briefId") : null;
  const preselectBriefId = urlParams ? urlParams.get("preselect") : null;

  if (referSlug && !publicBriefId && !preselectBriefId) return <PublicReferralPage slug={referSlug} />;
  if (authLoading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAFAF8", fontFamily: "Inter, sans-serif", color: "#9CA3AF", fontSize: 14 }}><Spin /> Loading...</div>;

  // Public job page — always show if briefId in URL, even if logged in
  if (publicBriefId) return (
    <PublicJobPage
      briefId={publicBriefId}
      referSlug={referSlug}
      onRefer={(brief) => {
        // Clear the briefId from URL first so we don't loop back
        const newUrl = referSlug
          ? `${window.location.origin}?refer=${referSlug}&preselect=${brief.id}`
          : `${window.location.origin}?preselect=${brief.id}`;
        window.location.href = newUrl;
      }}
    />
  );

  if (consentSuccess) return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", fontFamily: "Inter, DM Sans, system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 10, background: "#FFFFFF" }}>
        <div style={{ width: 32, height: 32, background: "#5B6EAF", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <span style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917" }}>PeerRefer</span>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "40px 24px" }}>

        {/* Success header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, background: "rgba(5,150,105,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 28, color: "#1C1917", marginBottom: 8 }}>
            You're in the pool{consentSuccess.candidateName ? `, ${consentSuccess.candidateName.split(" ")[0]}` : ""}
          </div>
          <div style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.6 }}>
            Your consent has been recorded. {consentSuccess.taName || "The recruiter"} can now view your profile.
          </div>
        </div>

        {/* Brief / Role info */}
        {consentSuccess.brief && (
          <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16 }}>
            {/* Company header */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {consentSuccess.brief.logo_url ? (
                <img src={consentSuccess.brief.logo_url} alt="logo"
                  style={{ width: 52, height: 52, borderRadius: 10, objectFit: "contain", border: "1px solid rgba(0,0,0,0.08)", background: "#fff", padding: 4 }} />
              ) : (
                <div style={{ width: 52, height: 52, borderRadius: 10, background: "#F0F2FA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B6EAF" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                </div>
              )}
              <div>
                <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917" }}>{consentSuccess.brief.title}</div>
                <div style={{ fontSize: 13, color: "#6B7280" }}>{consentSuccess.taCompany}{consentSuccess.brief.location ? ` · ${consentSuccess.brief.location}` : ""}{consentSuccess.brief.seniority ? ` · ${consentSuccess.brief.seniority}` : ""}</div>
              </div>
            </div>

            {/* EVP */}
            {consentSuccess.brief.evp && (
              <div style={{ background: "rgba(91,110,175,0.06)", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid rgba(91,110,175,0.12)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#5B6EAF", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace", marginBottom: 6 }}>WHY JOIN US</div>
                <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>{consentSuccess.brief.evp}</div>
              </div>
            )}

            {/* Position Description */}
            {consentSuccess.brief.position_description && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em", fontFamily: "DM Mono, monospace", marginBottom: 8 }}>ABOUT THE ROLE</div>
                <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.75, whiteSpace: "pre-wrap", maxHeight: 200, overflow: "auto" }}>{consentSuccess.brief.position_description}</div>
              </div>
            )}

            {/* Brief description fallback */}
            {!consentSuccess.brief.position_description && consentSuccess.brief.description && (
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, marginBottom: 16 }}>{consentSuccess.brief.description}</div>
            )}
          </div>
        )}

        {/* CV Upload */}
        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16 }}>
          <div style={{ fontFamily: "Playfair Display, Georgia, serif", fontWeight: 700, fontSize: 18, color: "#1C1917", marginBottom: 6 }}>Attach your CV</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20, lineHeight: 1.6 }}>Give {consentSuccess.taName || "the recruiter"} a complete picture. Candidates who attach a CV are significantly more likely to be contacted.</div>
          <CVUpload referralId={consentSuccess.referralId} />
        </div>

        {/* Talent Passport */}
        <TalentPassportForm referralId={consentSuccess.referralId} candidateEmail={consentSuccess.candidateEmail} candidateName={consentSuccess.candidateName} taName={consentSuccess.taName} referrerName={consentSuccess.referrerName} />

        {/* What happens next */}
        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "20px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.75 }}>
            <strong style={{ color: "#1C1917" }}>What happens next?</strong><br/>
            {consentSuccess.taName || "The recruiter"} will review your profile. If there is a match, they will reach out directly via email. You can withdraw consent at any time by replying to the original consent email.
          </div>
        </div>
        <PeerReferFooter />
      </div>
    </div>
  );

  if (resetMode) return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, DM Sans, system-ui, sans-serif", padding: "20px" }}>
      <div style={{ background: "#FFFFFF", borderRadius: 20, padding: "40px 36px", width: "100%", maxWidth: 420, border: "1px solid rgba(0,0,0,0.07)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
  <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#5B6EAF"/><text x="18" y="27" font-family="Georgia, serif" font-size="26" font-weight="700" fill="#FFFFFF" text-anchor="middle">P</text></svg>
          <span style={{ fontWeight: 700, fontSize: 16, color: "#1C1917", letterSpacing: "-0.02em" }}>PeerRefer</span>
        </div>
        <div style={{ fontWeight: 700, fontSize: 22, color: "#1C1917", marginBottom: 6, letterSpacing: "-0.02em" }}>Set new password</div>
        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 24, lineHeight: 1.5 }}>Choose a strong password — at least 6 characters.</div>
        <div style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#92400E" }}>
          ⚡ Enter your new password quickly — reset links expire after a few minutes.
        </div>
        <input type="password" autoFocus value={resetPassword} onChange={e => { setResetPassword(e.target.value); setResetError(""); }}
          placeholder="New password"
          style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "12px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#1C1917", marginBottom: 10, boxSizing: "border-box" }} />
        <input type="password" id="reset-confirm" placeholder="Confirm new password"
          style={{ width: "100%", background: "#FAFAF8", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 9, padding: "12px 13px", fontFamily: "inherit", fontSize: 14, outline: "none", color: "#1C1917", marginBottom: 14, boxSizing: "border-box" }} />
        {resetError && <div style={{ fontSize: 13, color: "#DC2626", marginBottom: 12, padding: "8px 12px", background: "rgba(220,38,38,0.06)", borderRadius: 7 }}>{resetError}</div>}
        <button onClick={async () => {
          const confirm = document.getElementById("reset-confirm")?.value || "";
          if (resetPassword.length < 6) { setResetError("Password must be at least 6 characters."); return; }
          if (resetPassword !== confirm) { setResetError("Passwords don't match."); return; }
          setResetLoading(true);
          setResetError("");
          const { error } = await supabase.auth.updateUser({ password: resetPassword });
          setResetLoading(false);
          if (error) {
            if (error.message.includes("session")) {
              setResetError("Reset link has expired. Please request a new one from the sign in page.");
            } else {
              setResetError(error.message);
            }
          } else {
            await supabase.auth.signOut();
            setResetMode(false);
            setResetPassword("");
            setPortal("ta");
            alert("Password updated! Please sign in with your new password.");
          }
        }} disabled={resetLoading} style={{ width: "100%", background: resetLoading ? "#9CA3AF" : "#D85A30", color: "#fff", border: "none", borderRadius: 9, padding: "13px", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: resetLoading ? "not-allowed" : "pointer", transition: "background 0.2s" }}>
          {resetLoading ? "Updating..." : "Set new password →"}
        </button>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <span style={{ fontSize: 12, color: "#9CA3AF", cursor: "pointer", textDecoration: "underline" }} onClick={() => { setResetMode(false); setPortal(null); }}>
            Back to home
          </span>
        </div>
      </div>
    </div>
  );
  // Wait for session restore before showing anything
  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: "#0D0608", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="12" fill="#5B6EAF"/><text x="24" y="36" fontFamily="Georgia,serif" fontSize="34" fontWeight="700" fill="#FFFFFF" textAnchor="middle">P</text></svg>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", fontFamily: "sans-serif" }}>Loading...</div>
    </div>
  );
  if (!portal) return <PortalSelector onSelect={(p) => { setPortal(p); }} preselectBriefId={preselectBriefId} />;
  if (!user) return <LoginScreen portalType={portal} onLogin={(u) => {
    setUser(u);
    if (u.is_pro) setIsProTA(true);
    if (u.hasBoth) setHasBothPortals(true);
    const p = u.role === "ta" ? "ta" : "referrer";
    setPortal(p);
    localStorage.setItem("peerrefer_portal", p);
    localStorage.setItem("peerrefer_user_email", u.email || "");
    supabase.auth.updateUser({ data: { preferred_portal: p } }).catch(() => {});
  }} onBack={() => { setPortal(null); window.history.replaceState({}, "", window.location.pathname); }} preselectBriefId={preselectBriefId} />;
  if (user.role === "ta") return <TADashboard user={user} referrals={pool} setReferrals={setPool} poolLoading={poolLoading} taProfile={taProfile} onLogout={logout} sharedBriefs={sharedBriefs} setSharedBriefs={setSharedBriefs} isProTA={isProTA} setIsProTA={setIsProTA} setTaPrefs={setTaPrefs} hasBothPortals={hasBothPortals} />;
  if (user.role === "referrer") return <ReferrerDashboard user={user} onLogout={logout} onAddToPool={r => setPool(prev => [r, ...prev])} sharedBriefs={sharedBriefs} taPrefs={taPrefs} preselectBriefId={preselectBriefId} hasBothPortals={hasBothPortals} />;
  return null;
}
